import { Injectable, Injector, OnDestroy } from '@angular/core';

import {
    BehaviorSubject,
    catchError,
    combineLatest,
    EMPTY,
    forkJoin,
    from,
    lastValueFrom,
    Observable,
    Observer,
    of,
    Subject,
    throwError
} from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { uniq, cloneDeep, isEmpty } from 'lodash';
import * as uuid from 'uuid';
import { base64ToFile } from '@Core/utils';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { isEqual, union } from 'underscore';
import { difference } from 'underscore';
import moment from 'moment-timezone';

import { AbstractPageBuilderService } from '../../../Common/PageBuilder/services/abstract-page-builder.service';
import { AIApi } from '../../../api/ai/AI-api';
import { BlockModel, LayoutBlockTypesEnum } from '../../../Common/PageBuilder/models/block.model';
import { BlockTypeModel } from '../../../Common/PageBuilder/models/block-type.model';
import { BriefingApiModel } from '../../../api/briefings/model/briefing-api.model';
import {
    briefingBlockToGeneralStyle,
    briefingBlockTypeToGeneralStylesPropertyMap,
    generalStyleToBriefingBlockMap
} from '../models/briefing-global-styles-property.const';
import {
    BriefingBlockTypeEnum,
    briefingBlockTypeEnumToStyleTypeEnum,
    briefingBlockTypeToGeneralStylesKey,
    isMentionBlockTypes,
    styleTypeApiEnumToBriefingBlockTypeEnum
} from '../enums/briefing-block-type.enum';
import { BriefingHistoryModel } from '../models/briefing-history.model';
import {
    briefingAnalysisBlockType,
    briefingLayoutBlockTypes,
    briefingsBlockTypes,
    briefingsDefaultBlockTypes,
    briefingsSubBlockTypes,
    briefingsMentionListBlockTypes,
    curatedMentionListBlockType,
    dynamicMentionListBlockType,
    briefingOneColumnLayoutBlockType,
    titleBlockType,
    briefingBlockTypeEnumToBlockTypeMap
} from '../models/briefings-block-types.model';
import { BriefingListItemModel } from '../../../Common/MonitorShared/model/briefing-list-item.model';
import { BriefingModel } from '../models/briefing.model';
import { BriefingOrganizeByEnum } from '../enums/briefing-organize-by.enum';
import { BriefingQuickModel } from '../models/briefing-quick.model';
import { BriefingsApi } from '../../../api/briefings/briefings.api';
import { BriefingsApiErrorEnum } from '../../../api/briefings/enum/briefings-api-error.enum';
import { getBriefingsBlockTypeGroups } from '../models/briefings-block-type-groups.model';
import { BriefingSortByEnum } from '../enums/briefing-sort-by.enum';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CuratedMentionListDisplayElementModel } from '../models/curated-mention-list-display-element.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DateDisplayElementModel } from '../../../Common/PageBuilder/models/date-display-element.model';
import { DisplayElementWithSearchCriteriaModel } from '../models/display-element-with-search-criteria.model';
import { DynamicMentionListDisplayElementModel } from '../models/dynamic-mention-list-display-element.model';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MediaTypeModel } from '../../Shared/models/media-type.model';
import { Mention } from '../../../Common/Mentions/models/mention';
import { MentionCustomizationModel } from '../../Shared/models/mention-customization.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { MonitoringBackEndDataSourceModel } from '../../Shared/models/monitoring-back-end-data-source.model';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { MonitoringRoutingService } from '../../Core/services/monitoring-routing.service';
import { MonitoringSeriesTypeEnum } from '../../Shared/enums/monitoring-series-type.enum';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { SentimentModel } from '../../Shared/models/sentiment.model';
import { StylesService } from '../../../Common/Styles/services/styles.service';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TwigRendererService } from '../../../Common/PageBuilder/services/twig-renderer.service';
import { UserModel } from '../../../Core/Users/models/user.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import {
    FilterListProviderServiceInterface
} from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { BriefingStatusApiEnum } from '../../../api/briefings/enum/briefing-status-api.enum';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { BriefingSendHistoryApiModel } from '../../../api/briefings/model/briefing-send-history-api.model';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';
import { MailingListsService } from '../../../Common/MailingLists/services/mailing-lists.service';
import { MailingListModel } from '../../../Common/MailingLists/models/mailingList.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { MentionContentCustomizationEnum } from '../../../Common/Mentions/enums/mention-content-customization.enum';
import { SnippetAreaEnum } from '../../../api/mentions/enum/snippet-area.enum';
import { BriefingTextDisplayElementModel } from '../models/briefing-text-display-element.model';
import { CurrentResourceInterface } from '../../../Common/Basic/models/current-resource-abstract.model';
import { AttributeModel } from '../../../Common/Misc/models/attribute.model';
import { BriefingDetailsEnum } from '../enums/briefing-details.enum';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { RSSFeedService } from '../../Shared/services/rss-feed.service';

import { TimeZoneFormatEnum } from '../../../Common/Misc/enums/time-zone-format.enum';
import { SentBriefingEnum } from '../enums/sent-briefing-attributes.enum';
import { BriefingHistorySentModel } from '../../../api/briefings/model/briefing-history-sent.model copy';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { BriefingTypeEnum } from '@Monitor/Briefings/enums/briefing-type.enum';
import { BriefingBlockTypeApiEnum } from '@api/briefings/enum/briefing-block-type-api.enum';
import {
    BriefingCuratedMentionListDisplayElementApiModel
} from '@api/briefings/model/briefing-curated-mention-list-display-element-api.model';
import {
    BriefingAutomaticMentionListDisplayElementApiModel
} from '@api/briefings/model/briefing-automatic-mention-list-display-element-api.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { MentionSearchListItemModel } from '../../../Monitor/Shared/models/mention-search-list-item.model';
import { TopicsService } from '../../../Monitor/Core/services/topics.service';
import { LayoutDisplayElement } from '../../../Common/PageBuilder/models/layout-display-element.model';
import { LayoutBlock } from '../../../Common/PageBuilder/models/layout-block.model';
import { Template } from '../../../Common/Styles/models/template.model';
import { BlockMoveDirectionEnum } from '../../../Common/PageBuilder/enums/direction.enum';
import { FocusedMoveButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { LayoutBlockType } from '../../../Common/PageBuilder/models/layout-block-type.model';
import { AnalysisDisplayElementModel } from '../models/briefing-analysis-display-element.model';
import { CoverageInsights } from '../../Shared/models/coverage-insights.model';
import { BriefingAnalysisDisplayElementApiModel } from '../../../api/briefings/model/briefing-analysis-display-element-api.model';
import { MentionSearchService } from '../../../Monitor/Core/services/mention-search.service';
import { AnalysisUpsellPopupComponent } from '../components/AnalysisUpsellPopup/analysis-upsell-popup.component';
import { AutoSavingConfigModel } from '@Monitor/Shared/models/auto-saving-config.model';
import { MonitoringDateStyleModel } from '../../../Monitor/Shared/models/monitoring-date-style-model';
import { dateFormats } from '../../../Core/Users/data/date-formats';
import { timeFormats } from '../../../Core/Users/data/time-formats';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { ChartSetupDialogComponent } from '../../../Common/Reports/components/ChartSetupDialog/chart-setup-dialog.component';
import { RecentColorsService } from '../../../Common/Forms/components/AdvancedColorPickerPopover/service/recent-colors.service';
import { BriefingChartDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-chart-display-element.model';
import { ChartDisplayElementModel } from '../../../Common/Reports/models/chart-display-element.model';
import {
    ReportBlockTypeEnum,
    reportBlockTypesToNewAccentColorMapKeysMap,
    reportBlockTypesToSetupDialogAccentColorMapKeysMap,
    reportChartInfoMap
} from '../../../Common/Reports/enums/report-block-type.enum';
import { OnlineBriefingTemplateConfigModel } from '../models/online-briefing-option.model';
import { deliverySchedulingTypesEnum } from '../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { BriefingDeliveryMethodEnum } from '../enums/briefing-delivery-method.enum';
import { BlockDisplayTypeEnum } from '../../../Common/PageBuilder/enums/block-display-type.enum';
import { MediaServiceApi } from '../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../api/mediaService/enums/media-service-access-type.enum';
import { MediaResultModel, MediaServiceErrorCodesEnum } from '../../../api/mediaService/mediaResults.api.model';
import { TableOfContentDisplayElementModel } from '../models/table-of-content-display-element.model';
import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { BriefingTitleDisplayElementModel } from '../models/briefing-title-display-element.model';
import { BriefingSearchApi } from '@api/briefings/model/briefing-search-api';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { SavedMentionSearchApiModel } from '@api/mentions/model/saved-mention-search.api.model';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';
import { MapChartColorSchemeEnum } from '../../../Common/Charts/models/map-chart.model';
import { BriefingChartDisplayElementApiModel } from '@api/briefings/model/briefing-chart-display-element-api.model';
import { BriefingImageDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingImageDisplayElementApiModel } from '@api/briefings/model/briefing-image-display-element-api.model';
import { CanvasAgentResponseApiModel } from '@api/ai/model/canvas-agent-response-api.model';
import { CanvasV2AcceptedModel, CanvasV2ProgressEvent } from '@api/ai/model/new-canvas-agent-response-api.model';
import { CuratorDisplayElement } from '../../../Common/PageBuilder/models/curator-display-element.model';
import { CuratorBlock } from '../../../Common/PageBuilder/models/curator-block.model';
import { CuratorBlockType } from '../../../Common/PageBuilder/models/curator-block-type.model';
import { promptLengthOptions } from '../../../Common/Forms/data/prompt-length-options';
import { CompletionLengthEnum } from '../../../api/ai/enums/completion-length.enum';
import { BriefingsHelper } from '../helpers/briefings.helper';
import { BriefingCoverageCuratorDisplayElementApiModel } from '../../../api/briefings/model/briefing-coverage-curator-display-element-api.model';
import { DisplayElementModel } from '../../../Common/PageBuilder/models/display-element.model';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { promptLanguageOptions } from '../../../Common/Forms/data/prompt-language-options';
import { ImageSizeEnum } from '../../../Monitor/Shared/enums/image-size.enum';
import { BriefingCuratorBlockGenerationOptionInterface } from '../models/briefing-curator-block-generations.interface';
import { PaddingModel } from '../../../Common/PageBuilder/models/padding.model';
import { BriefingHeadingLevelEnum } from '../enums/briefing-heading-level.enum';
import { AiAnalysisTypes } from '@Common/Mentions/enums/ai-analysis-types.enum';
import { BriefingBlockCount } from '../models/briefing-block-count.model';
import { ImageHelper } from '@Common/PageBuilder/helpers/image.helper';
import { ImageSourceEnum } from '@Common/PageBuilder/enums/image-source.enum';
import { BriefingImageFrameEnum } from '@Monitor/Shared/enums/briefing-image-frame.enum';
import { BriefingImagePropertiesModel } from '@Common/PageBuilder/models/briefing-image-properties.model';
import { BriefingCuratedMentionDetailsApi } from '@api/briefings/model/briefing-curated-mention-Details-api';
import { CachedDateService } from '@Common/MonitorShared/services/cached-date.service';
import { DigestService } from '@Monitor/Shared/services/digest.service';
import { DigestPatchApi } from '@api/briefings/model/digest-patch-api';


@Injectable()
export class BriefingsService extends AbstractPageBuilderService implements OnDestroy, FilterListProviderServiceInterface, CurrentResourceInterface {
    readonly MERCURY_ID_PATTERN = /^[A-Za-z]+\|\d+\|[-\w]+/;

    private currentBriefing: BriefingModel;
    private cachedCurrentBriefing: BriefingModel;
    private currentBriefingProvider: BehaviorSubject<BriefingModel>;
    private includeEmptyMentionSectionsProvider: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    protected loadingSubject = new BehaviorSubject<boolean>(false);
    loading$: Observable<boolean> = this.loadingSubject.asObservable();

    protected dirtyCheckSubject = new BehaviorSubject<boolean>(null);
    dirtyCheck$: Observable<boolean> = this.dirtyCheckSubject.asObservable();
    protected refreshChartBlockData$: BehaviorSubject<BlockModel[]> = new BehaviorSubject([]);

    saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private briefingHistory: BriefingHistoryModel;
    private briefingHistoryProvider: BehaviorSubject<BriefingHistoryModel>;

    private currentBriefingMentions: Mention[] = [];
    private currentBriefingMentionsProvider: BehaviorSubject<Mention[]>;

    private currentAutoDataSourceProvider: BehaviorSubject<MonitoringBackEndDataSourceModel>;

    private currentBriefingAllocatedMentionsCount: number = 0;
    private currentBriefingAllocatedMentionsCountProvider: BehaviorSubject<number>;

    private briefingListItems: BriefingListItemModel[];
    private briefingListItemsProvider: BehaviorSubject<BriefingListItemModel[]>;

    private preferences: PreferencesModel;
    private topics: MentionSearchListItemModel[];
    private currentBriefingDeliveryMethod: BriefingDeliveryMethodEnum;

    private currentRoute: MonitoringRouteModel;

    public readonly maxNumberOfMentionsInBriefing = 1000;

    private availableMediaTypes: MediaTypeModel[];
    private availableSentiments: SentimentModel[] = MentionSearchCriteriaModel.allSentiments.slice();

    private user: UserModel;

    private mailingLists: MailingListModel[] = [];

    private readonly briefingTotalBlockLimit: number = 100;
    private readonly briefingAnalysisBlockLimit: number = 20;

    totalBlockCount = 0;

    get isBlockLimitExceeded(): boolean {
        return this.totalBlockCount > this.briefingTotalBlockLimit;
    }

    get currentBriefingId(): number {
        return this.currentBriefing?.id;
    }

    private briefingBlocks: BlockTypeModel[] = [
        ...briefingsDefaultBlockTypes,
        ...briefingsSubBlockTypes,
        ...briefingsMentionListBlockTypes,
        ...briefingLayoutBlockTypes
    ];

    private chartBlockNoData$: BehaviorSubject<ReportSeriesModel[]> = new BehaviorSubject(null);
    private canLeaveBypass: boolean = false;


    briefingInitialCreate: boolean = false;

    /** Import dialog is handled this way to avoid a circular dependency */
    private importDialogProvider: Subject<BlockModel> = new Subject<BlockModel>();

    readonly globalProperty?: string = 'briefing';
    showSendConfirmationOnLoad$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    hasDynamicElements$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    loadingCuratedMentions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private forceReportRefresh$ = new BehaviorSubject<boolean>(false);

    languageTag: LanguageTagEnum = LanguageTagEnum.en_US;
    protected userTimezonePreference: string;

    private updatedAutomaticBlock$: BehaviorSubject<BlockModel> = new BehaviorSubject<BlockModel>(null);

    hasCoverageInsightsEnabled: boolean = false;

    /// TODO: Investigate whether the briefings and alerts services can both extend a base model instead of duplicating method

    readonly MAX_CANVAS_BLOCK_MENTIONS: number = 100;
    readonly CURATOR_BLOCK_MENTIONS: number = 20;
    readonly aiPopupWidth: number = 485;
    readonly aiPopupHeight: number = 372;
    readonly chartRowHeight: number = 125;
    // Default chart height for Coverage By Region Chart with "Map" type and "Region Colors" color scheme
    readonly mapChartWithRegionColorsChartHeight: number = 550;
    overlayContainer: Element;
    isRefMediaAccount: boolean = false;
    briefingComponentInjector: Injector;
    private lastDigestPatchKey: string = null;

    constructor(
        private briefingApi: BriefingsApi,
        private mentionsApi: MentionsApi,
        private savedSearchesApi: SavedMentionSearchesApi,
        private routingService: MonitoringRoutingService,
        public localizationService: LocalizationService,
        private messageService: MessageService,
        private currentUserStateService: CurrentUserStateService,
        private tagsService: TagsService,
        private coreDialogService: CoreDialogService,
        private journodexRegionsService: JournodexRegionsService,
        public stylesService: StylesService,
        public twigRenderService: TwigRendererService,
        public websocketEvents: WebsocketAwareNeptuneService,
        private monitorConfigService: MonitorConfigService,
        private currencyConversionService: CurrencyConversionService,
        private configService: ConfigService,
        private mailingListsService: MailingListsService,
        private rssFeedService: RSSFeedService,
        private topicsService: TopicsService,
        public mentionSearchService: MentionSearchService,
        public injector: Injector,
        private displayGroupsService: DisplayGroupsService,
        private journodexApiService: JournodexApiService,
        public mediaServiceApi: MediaServiceApi,
        private aIApi: AIApi,
        private cachedDateService: CachedDateService,
        private digestService: DigestService,
        private recentColorsService: RecentColorsService
    ) {
        super(stylesService, twigRenderService, localizationService);

        this.currentBriefingProvider = new BehaviorSubject<BriefingModel>(null);

        this.currentBriefingMentionsProvider = new BehaviorSubject<Mention[]>(this.currentBriefingMentions);
        this.currentAutoDataSourceProvider = new BehaviorSubject<MonitoringBackEndDataSourceModel>(null);

        this.briefingHistoryProvider = new BehaviorSubject<BriefingHistoryModel>(null);

        this.briefingListItemsProvider = new BehaviorSubject<BriefingListItemModel[]>(null);

        this.currentBriefingAllocatedMentionsCountProvider = new BehaviorSubject<number>(null);

        this.setBlockTypeEnumFromStyleTypeEnumMap(styleTypeApiEnumToBriefingBlockTypeEnum);
        this.setGeneralStylesToBlocksMap(generalStyleToBriefingBlockMap);
        this.setBlockToGeneralStylePropertiesMap(briefingBlockToGeneralStyle);

        this.defaultStyles = BriefingModel.defaultBriefingStyles;
        this.blockMaxWidth$.next(640);

        this.setSubscriptions();
        this.overlayContainer = document.getElementById('main-content');

        this.isRefMediaAccount = this.configService.brandGUIDIsRefMedia();
        this.setInlineAdderHeight(389);
    }

    /**
     * Sets subscriptions, pushing to array for easy cleanup later
     */
    private setSubscriptions(): void {
        this.subscriptions.push(
            this.routingService.getCurrentRoute().subscribe(
                routeModel => {
                    this.currentRoute = routeModel;

                    this.updateBriefingFromRoute(routeModel);
                }
            ),
            this.currentUserStateService
                .getUser()
                .subscribe(
                    user => {
                        this.user = user;
                        this.setDefaultReplyToAndFromName();
                    }
                ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    const hasPreferences: boolean = preferences instanceof PreferencesModel;
                    const hasCurrentBriefing: boolean = this.currentBriefing instanceof BriefingModel;
                    const isCurrentBriefingSaved: boolean = hasCurrentBriefing && this.currentBriefing.isSaved;
                    const hasValidTimeZone: boolean = hasCurrentBriefing && this.currentBriefing.hasValidSendAtTimezone;

                    this.preferences = preferences;
                    this.userTimezonePreference = preferences.timeZone;

                    if (hasPreferences) {
                        // If the currentBriefing is already loaded, and is a new (id is null) and doesn't have a timezone, default it
                        if (hasCurrentBriefing && !isCurrentBriefingSaved && !hasValidTimeZone) {
                            this.currentBriefing.sendAt.timeZone = this.preferences.timeZone;
                            this.setCurrentBriefing(this.currentBriefing);

                            // Don't set the current briefing as cached, create a new default briefing (which will now have the timezone set)
                            // and make that the cached briefing, that way if the user has already made changes other than the timezone, they
                            // won't get included in the cached copy
                            this.cachedCurrentBriefing = cloneDeep(this.getBlankBriefing());
                        }

                        // If there is a current briefing, and a new mention block has been added, but it hasn't been saved yet, update the timezone
                        if (hasCurrentBriefing) {
                            for (const block of this.currentBriefing.blocks) {
                                if (
                                    (
                                        block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
                                        block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST
                                    ) && !block.id && block.displayElement.styles
                                ) {
                                    block.displayElement.styles.timezone = this.preferences.timeZone;
                                }
                            }
                            this.setDefaultLanguage();
                        }
                    }
                }
            ),
            this.currentUserStateService.getAccount().pipe(filter(x => !!x))
                .subscribe(
                    account => {
                        this.setBlockTypes(this.setDefaultBriefingTemplatesOnBlockTypes(briefingsBlockTypes));
                        this.checkAnalysisBlockBeforeSettingBlockTypeGroups();
                        this.setLayoutBlockTypes(briefingLayoutBlockTypes);
                    }
                ),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        // Not ideal, a change in briefings refreshes both alerts and briefings
                        if (message.resourceType === ResourceType.briefings) {
                            if (
                                message.slug && (
                                    (message.slug as string).includes('draft') ||
                                    (message.slug as string).includes('preview') ||
                                    (message.slug as string).includes('testSend')
                                )
                            ) {
                                // Do nothing, these actions don't result in new briefings/alerts
                            } else {
                                this.updateBriefingListItems();
                            }
                        }
                    }
                ),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(availableMediaTypes => {
                this.availableMediaTypes = availableMediaTypes.map(type => new MediaTypeModel(type));
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(languageTag => this.languageTag = languageTag as LanguageTagEnum),
            this.mailingListsService.getMailingLists().subscribe(mailingLists => {
                this.mailingLists = mailingLists;
            }),
            this.topicsService.getTopics().subscribe(topics => this.topics = topics),
            this.monitorConfigService.getHasCoverageInsightsEnabled().subscribe(x => {
                this.hasCoverageInsightsEnabled = x;
                this.checkAnalysisBlockBeforeSettingBlockTypeGroups();
            }),
            this.hasDefaultTemplates$.subscribe(
                hasTemplates => {
                    if (hasTemplates) {
                        this.setBlockTypes(this.setDefaultTemplatesOnBlockTypes(briefingsDefaultBlockTypes));
                        this.setDefaultTemplatesOnBlockTypes(briefingLayoutBlockTypes);
                        this.setTemplatesOnBlocks(this.blocks);
                    }
                }
            ),
            this.cachedDateService.getCachedSelectedDate().subscribe(
                cachedDate => {
                    this.cachedSelectedDate = cachedDate;
                }
            ),
        );
    }

    /**
     * Gets the provider for the current briefing
     */
    public getFocusedResource(): Observable<BriefingModel> {
        return this.currentBriefingProvider;
    }

    /**
     * The cached current briefing
     */
    public getCachedCurrentBriefing(): BriefingModel {
        return this.cachedCurrentBriefing;
    }

    /**
     * Updates the currentBriefing
     */
    public setCurrentBriefing(briefing: BriefingModel): void {
        this.currentBriefing = briefing;
        this.setTemplatesOnBlocks(this.currentBriefing.blocks);

        if ((this.selectedBlock) && (
            (this.selectedBlock.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) ||
            (this.selectedBlock.type.key === BriefingBlockTypeEnum.ANALYSIS))
        ) {
            if ((this.selectedBlock.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource) {
                this.setCurrentAutoDataSource((this.selectedBlock.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource);
            }
        }
        this.currentBriefingDeliveryMethod = this.currentBriefing.deliveryMethod;
        this.includeEmptyMentionSectionsProvider.next(this.currentBriefing.includeEmptyMentionSections);
        this.currentBriefingProvider.next(this.currentBriefing);

        this._cachedActionableState = null;
        this.updateBlocks();
        this.checkIfBriefingHasDynamicElements();
    }

    /**
     * Shortcut to just update the blocks of the current briefing
     */
    public setCurrentBriefingBlocks(blocks: BlockModel[]): void {
        this.currentBriefing.blocks = blocks;
        this.setCurrentBriefing(this.currentBriefing);

        this.updateCurrentBriefingMentions();
    }

    getCurrentBriefingDeliveryMethod(): BriefingDeliveryMethodEnum {
        return this.currentBriefingDeliveryMethod;
    }

    setIncludeEmptyMentionSections(value: boolean): void {
        this.includeEmptyMentionSectionsProvider.next(value);
    }

    shouldShowLayoutBlockEmptySectionWarning$(): Observable<boolean> {
        return this.includeEmptyMentionSectionsProvider.pipe(map(value => !value));
    }

    /**
     * Observable of the all the searchMentions found in the curated sections
     */
    public getCurrentBriefingMentions(): Observable<Mention[]> {
        return this.currentBriefingMentionsProvider;
    }

    /**
     * Observable of the all the searchMentions found in the curated sections
     */
    public setCurrentAutoDataSource(dataSource: MonitoringBackEndDataSourceModel) {
        this.currentAutoDataSourceProvider.next(dataSource);
    }

    /**
     * Observable of the all the searchMentions found in the curated sections
     */
    public getCurrentAutoDataSource(): Observable<MonitoringBackEndDataSourceModel> {
        return this.currentAutoDataSourceProvider;
    }

    /**
     * Observable for DisplayMentionLimitReachedDialog
     */
    public getCurrentBriefingAllocatedMentionsCountProvider(): Observable<number> {
        return this.currentBriefingAllocatedMentionsCountProvider;
    }

    public updateBriefingVisibility(
        id: number,
        action: ActionsEnum,
        button: LeftSidebarListItemButton
    ): void {
        const hidden = action === ActionsEnum.hide;
        button.loading = true;

        this.briefingApi.getById(id).subscribe(
            briefing => {
                // Clean image properties, links shouldn't have IDs when updating
                if (briefing.blocks) {
                    briefing.blocks.forEach((block: any) => {

                        if (block.imageProperties?.link?.id) {
                            delete block.imageProperties.link.id;
                        }

                        if (Array.isArray(block.images)) {
                            block.images.forEach((image: any) => {
                                if (image.link?.id) {
                                    delete image.link.id;
                                }
                            });
                        }
                    });
                }
                briefing.hidden = hidden;
                this.briefingApi.updateFull(briefing).subscribe({
                    next: () => this.updateBriefingListItems(button),
                    error: () => button.loading = false
                });
            }
        );
    }


    public bulkUpdateVisibility(ids: number[], action: ActionsEnum): void {
        const hidden = action === ActionsEnum.hide;
        const observables = ids.map(id => {
            return this.briefingApi.getById(id).pipe(
                switchMap(briefing => {
                    briefing.hidden = hidden;
                    return this.briefingApi.updateFull(briefing);
                })
            );
        });
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Briefing.bulkUpdateBriefingSuccessHeading',
                    detail: this.localizationService.transform('Briefing.bulkUpdateBriefingSuccessMessage', {
                        number: result.length
                    })
                });
                this.updateBriefingListItems();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Briefing.bulkUpdateBriefingErrorHeading',
                    detail: 'Briefing.bulkUpdateBriefingErrorMessage'
                });
                this.updateBriefingListItems();
            }
        });
    }

    public openRenameBriefingDialog(briefing: BriefingListItemModel) {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'renameBriefing',
                heading: 'Briefing.renameBriefingDialog.heading',
                okLabel: 'Lhp.actions.rename',
                placeholder: 'Briefing.renameBriefingDialog.placeholder',
                description: 'Briefing.renameBriefingDialog.description',
                initialValue: briefing.name,
                formValidator: (name: string) => {
                    if (this.isBriefingNameDuplicate(name, briefing.id)) {
                        return [
                            'Briefing.copyBriefingDuplicateNameErrorMessage'
                        ];
                    }

                    return [];
                },
                submit: (name: string) => {
                    if (name !== briefing.name) {
                        this.updateBriefingName(briefing.id, name);
                    }
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    public updateBriefingName(id: number, name: string) {
        let oldName;
        this.briefingApi.getById(id).pipe(
            mergeMap((briefing) => {
                oldName = briefing.briefingName;
                briefing.briefingName = name;
                return this.briefingApi.updateFull(briefing);
            })
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Briefing.renameBriefingSuccessHeading'),
                    detail: this.localizationService.transform('Briefing.renameBriefingSuccessMessage', {
                        name: name
                    })
                });
                this.updateBriefingListItems();
                if (this.currentBriefing?.id === id) {
                    this.currentBriefing.name = name;
                    this.setCurrentBriefing(this.currentBriefing);
                }
            },
            error: (err) => {
                if (err.error.details?.[0].code === BriefingsApiErrorEnum.nameDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Briefing.renameBriefingErrorHeading'),
                        detail: this.localizationService.transform('Briefing.saveBriefingDuplicateNameErrorMessage', {
                            name: name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Briefing.renameBriefingErrorHeading'),
                        detail: this.localizationService.transform('Briefing.renameBriefingErrorMessage', {
                            oldName: oldName,
                            name: name
                        })
                    });
                }
                this.updateBriefingListItems();
            }
        });

    }

    /**
     * Adds selected briefings to a group (or removes group ID if Uncategorized is selected)
     * @param briefingIds
     * @param groupId group ID or undefined if Uncategorized is selected
     */
    public updateGroup(briefingIds: number[], group: DisplayGroupModel) {
        let request;
        if (group.id) {
            request = this.briefingApi.updateBriefingDisplayGroup(briefingIds, [], group.id, BriefingTypeEnum.BRIEFING);
        } else {
            request = this.briefingApi.removeBriefingsFromDisplayGroup(briefingIds, BriefingTypeEnum.BRIEFING);
        }
        request.subscribe({
            next: () => {
                this.updateBriefingListItems();
                if (briefingIds.length === 1) {
                    const itemName = this.briefingListItems.find(briefing => briefing.id === briefingIds[0]).name;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            { itemName: itemName, groupName: group.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            { number: briefingIds.length, name: group.name }
                        )
                    });
                }
            },
            error: (err) => {
                this.updateBriefingListItems();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes group ID from all provided briefings
     * @param ids briefing ids
     */
    public removeAllBriefingsFromGroup(ids: number[]) {
        this.briefingApi.removeBriefingsFromDisplayGroup(ids, BriefingTypeEnum.BRIEFING).subscribe({
            next: () => {
                this.updateBriefingListItems();
            }
        });
    }

    public updateFavorites(addIds: number[], removeIds: number[]) {
        this.briefingApi.updateBriefingFavorites(addIds, removeIds, BriefingTypeEnum.BRIEFING).subscribe({
            next: () => {
                this.updateBriefingListItems();
            },
            error: (err) => {
                this.updateBriefingListItems();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }


    public updateBlocks(): void {
        if (!this.currentBriefing) {
            // TODO: loading here
            return;
        }

        // if the selected block no longer exists, then deselect it
        if (!this.blockExists()) {
            // we don't need to cancel changes because it's not going to get saved anyway
            this.deselectBlock(true);
        }

        // Don't call this.setBlocks because it calls setCurrentBriefingBlocks and results in a infinite loop
        super.setBlocks(this.currentBriefing.blocks);
        this.setBriefingBlocksActionable(this.currentBriefing);
        this.setGeneralStyles(this.currentBriefing.generalStyles);
        this.checkIfBriefingHasDynamicElements();
        this.recentColorsService.setContext(this.currentBriefing.blocks, this.currentBriefing.generalStyles);
    }

    /**
     * Adds a block
     */
    public addBlock(block: BlockModel): void {
        if (!block.type) {
            throw new Error('Blocks must have a type');
        }
        this.applyGeneralStylesToBlock(block);

        if (
            (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST ||
                block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST
            ) && this.preferences
            && block.displayElement.styles
        ) {
            block.displayElement.styles.timezone = this.preferences.timeZone;
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            }
            (block.displayElement as CuratedMentionListDisplayElementModel).blockReferenceId = uuid.v4();
        }
        if (
            block.type.key === BriefingBlockTypeEnum.DATE &&
            block.displayElement.styles &&
            this.preferences
        ) {
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            }
        }

        if (block.type.key === BriefingBlockTypeEnum.TEXT) {
            (block.displayElement as BriefingTextDisplayElementModel).enableAIRefinement = true;
        }
        if (block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            (block.displayElement as AnalysisDisplayElementModel).enableAIRefinement = true;
            (block.displayElement as AnalysisDisplayElementModel).analysisField = new CoverageInsights();
        }

        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        } else {
            block.displayElement.displayType = BlockDisplayTypeEnum.TWIG;
        }

        this.blocks.push(block);
        this.setBlocks(this.blocks);
        this.validateBlockCount();
    }

    protected checkAnalysisBlockBeforeSettingBlockTypeGroups(): void {
        if (!this.hasCoverageInsightsEnabled) {
            const blockTypeGroups = cloneDeep(getBriefingsBlockTypeGroups());
            let blockTypes = blockTypeGroups[0].blockTypes;
            if (this.isRefMediaAccount) {
                blockTypes = blockTypes.filter(block => block.key !== BriefingBlockTypeEnum.ANALYSIS && block.key !== BriefingBlockTypeEnum.COVERAGE_CURATOR);
            }
            blockTypes.map(type => type.isLocked = type.key === BriefingBlockTypeEnum.ANALYSIS || type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR);
            blockTypeGroups[0].blockTypes = blockTypes;
            this.setBlockTypeGroups(blockTypeGroups);
        } else {
            this.setBlockTypeGroups(getBriefingsBlockTypeGroups());
        }
    }

    addBlockOfTypeWithEvent(type: BlockTypeModel, position: string, event: Event): void {
        if ((type.key === BriefingBlockTypeEnum.ANALYSIS || type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) && !this.hasCoverageInsightsEnabled) {
            const mousePositionX = (event as PointerEvent)?.clientX || 0;
            const mousePositionY = (event as PointerEvent)?.clientY || 0;

            const ioModel = this.coreDialogService.getIoConfigBasedOnMousePosition(mousePositionX, mousePositionY, this.aiPopupWidth, this.aiPopupHeight);
            ioModel.inputs.overlayContainer = this.overlayContainer;
            this.coreDialogService.open(AnalysisUpsellPopupComponent, ioModel, this.injector);
        } else if (type.key === BriefingBlockTypeEnum.CHART) {

            const block = new BlockModel(null, type, new type.displayElementType());

            block.canAddChart = true;
            block.canEditChart = false;

            const generalStylesField = briefingBlockTypeToGeneralStylesKey.get(type.key as BriefingBlockTypeEnum);
            let defaultReference: string = this.generalStyles[generalStylesField].getDefaultReference();
            if (defaultReference === BriefingStyleStateEnum.CANVAS_1) {
                defaultReference = BriefingStyleStateEnum.BRIEFING_1;
            }
            block.type.twig = this.getDefaultTemplateByBlockType(type.key, defaultReference);
            this.applyGeneralStylesToBlock(block);

            if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
            } else {
                block.displayElement.displayType = BlockDisplayTypeEnum.TWIG;
            }

            if (!isNaN(+position) || !position) {
                const positionToAdd = !position ? null : +position;
                if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                    this.blocks.splice(positionToAdd, 0, block);
                } else {
                    this.blocks.push(block);
                }
            } else {
                if (position) {
                    const [blockPosition, columnIndex, positionInColumn] = position.split('.');
                    const layoutBlock = this.blocks[+blockPosition] as LayoutBlock;
                    const column = layoutBlock.blocks[+columnIndex];
                    const positionToAdd = +positionInColumn;
                    if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < column.length) {
                        column.splice(positionToAdd, 0, block);
                    } else {
                        column.push(block);
                    }
                }
            }

            this.selectBlock(block);
            this.setBlocks(this.blocks);

            this.setFocusedBlockInfo({
                blockPosition: position,
                focusedBlockButtonType: null
            });

            this.dirtyCheckSubject.next(true);
        } else {
            this.addBlockOfType(type, position);
        }
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockOfType(type: BlockTypeModel, position?: string): void {
        let block;

        if (type.key === BriefingBlockTypeEnum.ONE_COLUMN_LAYOUT ||
            type.key === BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT ||
            type.key === BriefingBlockTypeEnum.THREE_COLUMN_LAYOUT) {
            block = new LayoutBlock(null, type as LayoutBlockType, new type.displayElementType() as LayoutDisplayElement);
        } else if(type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
            block = this.createCuratorBlock(type as CuratorBlockType, {});
        } else {
            block = new BlockModel(null, type, new type.displayElementType());
        }

        if ((type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST ||
            type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST)
        ) {
            if (this.preferences && block.displayElement.styles) {
                block.displayElement.styles.timezone = this.preferences.timeZone;
                if (!block.displayElement.styles.locale) {
                    block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
                }
                (block.displayElement as CuratedMentionListDisplayElementModel).blockReferenceId = uuid.v4();
            }
        }
        if (type.key === BriefingBlockTypeEnum.TEXT) {
            (block.displayElement as BriefingTextDisplayElementModel).enableAIRefinement = true;
        }
        if (type.key === BriefingBlockTypeEnum.ANALYSIS) {
            const analysisDisplayElement = (block.displayElement as AnalysisDisplayElementModel);
            analysisDisplayElement.enableAIRefinement = true;
            analysisDisplayElement.analysisField = new CoverageInsights();

            if (this.cachedSelectedDate) {
                if (analysisDisplayElement?.backendDataSource?.combinedSearchCriteria?.query) {
                    analysisDisplayElement.backendDataSource.combinedSearchCriteria.query.dateQuery = this.cachedSelectedDate;
                }
                if (analysisDisplayElement?.backendDataSource?.combinedSearchCriteria?.filterQuery) {
                    analysisDisplayElement.backendDataSource.combinedSearchCriteria.filterQuery.dateQuery = this.cachedSelectedDate;
                }
                if (analysisDisplayElement?.backendDataSource?.filterSearchCriteria?.query) {
                    analysisDisplayElement.backendDataSource.filterSearchCriteria.query.dateQuery = this.cachedSelectedDate;
                }
            }
        }
        if (type.key === BriefingBlockTypeEnum.TABLE_OF_CONTENT) {
            const mentionBlocks = this.getAllMentionBlocks();
            // if at least one mention block has showSectionHeading false.. newly added TOC includeTitle will be set to true
            if (mentionBlocks?.length) {
                mentionBlocks?.forEach(mentionBlock => {
                    if ((mentionBlock.displayElement.styles as MentionCustomizationModel).showSectionHeading === false) {

                        // Might change (we may pass mention styles in the TOC template)
                        (block.displayElement as TableOfContentDisplayElementModel).settings.includeTitle = true;
                        (block.displayElement as TableOfContentDisplayElementModel).settings.includeMentionCount = false;
                        (block.displayElement as TableOfContentDisplayElementModel).settings._includeMentionSections = false;
                        (block.displayElement as TableOfContentDisplayElementModel).settings.includeHeadlines = false;
                        return;
                    }
                });
            } else {
                (block.displayElement as TableOfContentDisplayElementModel).settings.includeTitle = true;

                // Might change (we may pass mention styles in the TOC template)
                (block.displayElement as TableOfContentDisplayElementModel).settings.includeMentionCount = false;
                (block.displayElement as TableOfContentDisplayElementModel).settings._includeMentionSections = false;
                (block.displayElement as TableOfContentDisplayElementModel).settings.includeHeadlines = false;
            }
        }
        const generalStylesField = briefingBlockTypeToGeneralStylesKey.get(type.key as BriefingBlockTypeEnum);
        let defaultReference: string = this.generalStyles[generalStylesField].getDefaultReference();
        if (defaultReference === BriefingStyleStateEnum.CANVAS_1) {
            defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        }
        block.type.twig = this.getDefaultTemplateByBlockType(type.key, defaultReference);
        this.applyGeneralStylesToBlock(block);

        if (type.key === BriefingBlockTypeEnum.DATE) {
            this.localizeDateBlock(block);
            if (this.preferences && block.displayElement.styles && !block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            }
        }

        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            block.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        } else {
            block.displayElement.displayType = BlockDisplayTypeEnum.TWIG;
        }

        if (!isNaN(+position) || !position) {
            const positionToAdd = !position ? null : +position;
            if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                this.blocks.splice(positionToAdd, 0, block);
            } else {
                this.blocks.push(block);
            }
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const parentBlock = this.blocks[+blockPosition];

            let column: BlockModel[];
            if (parentBlock instanceof CuratorBlock) {
                // Curator blocks hold a flat list of blocks rather than columns
                column = parentBlock.blocks;
                this.moveInCuratorBlock(block, parentBlock);
            } else {
                column = (parentBlock as LayoutBlock).blocks[+columnIndex];
            }

            const positionToAdd = +positionInColumn;

            if (block.type.key === BriefingBlockTypeEnum.IMAGE) {
                const layoutDisplayElement = parentBlock.displayElement as LayoutDisplayElement;
                let paddingWidth = layoutDisplayElement.styles.columnSpacing || 0;
                if (layoutDisplayElement.styles.padding?.paddingOption) {
                    paddingWidth = paddingWidth + (layoutDisplayElement.styles.padding.leftPadding || 0)
                        + (layoutDisplayElement.styles.padding.rightPadding || 0);
                } else if (layoutDisplayElement.styles.padding?.paddingOption === false) {
                    paddingWidth += (layoutDisplayElement.styles.padding.paddingAll * 2 || 0);
                }

                if ((block.displayElement as BriefingImageDisplayElementModel)?.images?.length) {
                    (block.displayElement as BriefingImageDisplayElementModel)[0].width
                        = (640 - paddingWidth) * parseFloat(layoutDisplayElement.styles.width?.[columnIndex]) / 100;
                }
            }

            if ((!!positionToAdd || positionToAdd === 0) && positionToAdd >= 0 && positionToAdd < column.length) {
                column.splice(positionToAdd, 0, block);
            } else {
                column.push(block);
            }
        }
        this.setBlocks(this.blocks);

        this.setFocusedBlockInfo({
            blockPosition: position,
            focusedBlockButtonType: null
        });
        this.validateBlockCount();
    }

    moveBlock(block: BlockModel, direction: BlockMoveDirectionEnum, position: string, isToLayoutBlock: boolean = false) {
        const blocks = this.blocks;
        let focusedBlockPosition, focusedMoveButtonType;
        if (!isNaN(+position)) {
            const index = blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }

            if (direction === BlockMoveDirectionEnum.up) {
                if (index === 0) {
                    // already at the top, can't move up more
                    return;
                }
                blocks.splice(index, 1);
                // focusedBlockPosition = index - 1;
                if (isToLayoutBlock) {
                    if (blocks[index - 1] instanceof CuratorBlock) {
                        (blocks[index - 1] as CuratorBlock).blocks.push(block);
                        focusedBlockPosition = (index - 1) + '.0.' + ((blocks[index - 1] as CuratorBlock).blocks.length - 1);
                        this.moveInCuratorBlock(block, blocks[index - 1] as CuratorBlock);
                    } else {
                        (blocks[index - 1] as LayoutBlock).blocks[0].push(block);
                        focusedBlockPosition = (index - 1) + '.0.' + ((blocks[index - 1] as LayoutBlock).blocks[0].length - 1);
                    }
                } else {
                    blocks.splice(index - 1, 0, block);
                    focusedBlockPosition = index - 1 + '';
                    focusedMoveButtonType = FocusedMoveButtonTypesEnum.up;
                }
            } else if (direction === BlockMoveDirectionEnum.down) {
                if (index === blocks.length - 1) {
                    // already at the bottom, can't move down more
                    return;
                }

                blocks.splice(index, 1);
                if (isToLayoutBlock) {
                    if (blocks[index] instanceof CuratorBlock) {
                        (blocks[index] as CuratorBlock).blocks.push(block);
                        focusedBlockPosition = index + '.0.' + ((blocks[index] as CuratorBlock).blocks.length - 1);
                        this.moveInCuratorBlock(block, blocks[index] as CuratorBlock);
                    } else {
                        (blocks[index] as LayoutBlock).blocks[0].push(block);
                        focusedBlockPosition = index + '.0.' + ((blocks[index] as LayoutBlock).blocks[0].length - 1);
                    }
                } else {
                    blocks.splice(index + 1, 0, block);
                    focusedBlockPosition = index + 1 + '';
                    focusedMoveButtonType = FocusedMoveButtonTypesEnum.down;
                }
            }
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition];
            let column: BlockModel[];

            if (layoutBlock instanceof CuratorBlock) {
                // Curator blocks hold a flat list of blocks rather than columns
                column = layoutBlock.blocks;
            } else {
                column = (layoutBlock as LayoutBlock).blocks[+columnIndex];
            }
            let newColumnIndex;
            const index = +positionInColumn;
            column.splice(index, 1);
            switch (direction) {
                case BlockMoveDirectionEnum.up:
                    if (isToLayoutBlock) {
                        column.splice(index - 1, 0, block);
                        focusedBlockPosition = blockPosition + '.' + columnIndex + '.' + (index - 1);
                    } else {
                        blocks.splice(+blockPosition, 0, block);
                        focusedBlockPosition = blockPosition;
                        focusedMoveButtonType = FocusedMoveButtonTypesEnum.up;

                        if (layoutBlock instanceof CuratorBlock) {
                            this.moveOutCuratorBlock(block);
                        }
                    }
                    break;
                case BlockMoveDirectionEnum.down:
                    if (isToLayoutBlock) {
                        column.splice(index + 1, 0, block);
                        focusedBlockPosition = blockPosition + '.' + columnIndex + '.' + (index + 1);
                    } else {
                        blocks.splice(+blockPosition + 1, 0, block);
                        focusedBlockPosition = (+blockPosition + 1) + '';
                        focusedMoveButtonType = FocusedMoveButtonTypesEnum.down;

                        if (layoutBlock instanceof CuratorBlock) {
                            this.moveOutCuratorBlock(block);
                        }
                    }
                    break;
                case BlockMoveDirectionEnum.left:
                    newColumnIndex = +columnIndex - 1;
                    column = (layoutBlock as LayoutBlock).blocks[newColumnIndex];
                    column.push(block);
                    focusedBlockPosition = blockPosition + '.' + newColumnIndex + '.' + (column.length - 1);
                    break;
                case BlockMoveDirectionEnum.right:
                    newColumnIndex = +columnIndex + 1;
                    column = (layoutBlock as LayoutBlock).blocks[newColumnIndex];
                    column.push(block);
                    focusedBlockPosition = blockPosition + '.' + newColumnIndex + '.' + (column.length - 1);
                    break;
            }
        }

        this.setBlocks(blocks);

        this.setFocusedBlockInfo({
            blockPosition: focusedBlockPosition,
            focusedBlockButtonType: focusedMoveButtonType
        });
    }

    /**
     * While moveBlock is used for the move buttons, which limits to moving one block distance a time,
     * This method is used in drag and drop to move a block to an arbitrary position
     */
    moveBlockToPosition(block: BlockModel, position: string): void {
        let insideCuratorMove = false;
        if (this.draggingBlockPosition && isNaN(+position)) {
            const blockPosition1 = this.draggingBlockPosition.split('.')[0];
            const blockPosition2 = position.split('.')[0];
            if (blockPosition1 === blockPosition2) {
                insideCuratorMove = true;
            }
        }

        const indexOfBlock = this.blocks.indexOf(block);
        if (indexOfBlock > -1) {
            this.blocks.splice(indexOfBlock, 1);
        } else {
            if (this.draggingBlockPosition) {
                const indexParts = this.draggingBlockPosition.split('.');
                const blockPosition = +indexParts[0];
                const columnIndex = +indexParts[1];
                const positionInColumn = +indexParts[2];
                let column;
                if (this.blocks[blockPosition] instanceof LayoutBlock) {
                    column = (this.blocks[blockPosition] as LayoutBlock).blocks[columnIndex];
                } else {
                    // Curator block
                    column = (this.blocks[blockPosition] as CuratorBlock).blocks;
                    if (!insideCuratorMove) {
                        this.moveOutCuratorBlock(block);
                    }
                }
                column.splice(positionInColumn, 1);
            } else {
                this.blocks.forEach((b, i) => {
                    if (!(b as any).blocks) {
                        return;
                    }
                    const layoutBlock = b as LayoutBlock;
                    layoutBlock.blocks.forEach(column => {
                        const index = column.indexOf(block);
                        if (index > -1) {
                            column.splice(index, 1);
                        }
                    });
                });
            }
        }

        if (isNaN(+position)) {
            const indexParts = position.split('.');
            let blockPosition = +indexParts[0];
            const columnIndex = +indexParts[1];
            const positionInColumn = +indexParts[2];
            if (indexOfBlock > -1 && indexOfBlock < blockPosition) {
                blockPosition--;
            }

            const layoutBlock = this.blocks[blockPosition];
            let column;

            if (layoutBlock instanceof CuratorBlock) {
                column = layoutBlock.blocks;
                if (!insideCuratorMove) {
                    this.moveInCuratorBlock(block, layoutBlock);
                }
            } else {
                column = (layoutBlock as LayoutBlock).blocks[columnIndex];
            }
            const index = positionInColumn;
            column.splice(index, 0, block);
        }
        this.setBlocks(this.blocks);
    }

    removeBlock(block: BlockModel, position?: string): void {
        if (!isNaN(+position) || !position) {
            const index = this.blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }

            // If the deleted block is the selected block, unselect it
            if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(block.displayElement)) {
                this.deselectBlock(false);
            }
            this.blocks.splice(index, 1);
            this.setBlocks(this.blocks);
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition];

            let column: BlockModel[];
            if (layoutBlock instanceof CuratorBlock) {
                // Curator blocks hold a flat list of blocks rather than columns
                column = layoutBlock.blocks;
            } else {
                column = (layoutBlock as LayoutBlock).blocks[+columnIndex];
            }
            const positionToRemove = +positionInColumn;
            if (this.selectedBlock && this.selectedBlock.displayElement?.isEqual(layoutBlock.displayElement)) {
                this.deselectBlock(false);
            }
            column.splice(positionToRemove, 1);
            this.setBlocks(this.blocks);
            this.setFocusedBlockInfo({
                blockPosition: blockPosition + '.' + columnIndex + '.' + Math.max(0, positionToRemove - 1),
                focusedBlockButtonType: null
            });
        }
    }

    // Cache the last actionable state to avoid iterating all blocks if unchanged
    private _cachedActionableState: boolean | null = null;

    /**
     * Sets briefing blocks actionable based on status
     *
     * @param currentBriefing - the current briefing
     */
    setBriefingBlocksActionable(currentBriefing: BriefingModel) {
        const shouldBeActionable = currentBriefing.status !== BriefingStatusEnum.SENT
            && currentBriefing.status !== BriefingStatusEnum.SCHEDULED
            && !currentBriefing.isPreviewMode;

        // Skip if actionable state hasn't changed
        if (this._cachedActionableState === shouldBeActionable) {
            return;
        }

        this._cachedActionableState = shouldBeActionable;
        this.setBlocksActionable(shouldBeActionable);
    }

    /**
     * Updates the list of blocks
     * Note: This method avoids calling setCurrentBriefing() to prevent double-triggering
     * of blocksProvider.next() which would cause all block components to react twice.
     */
    setBlocks(blocks: BlockModel[]): void {
        this.currentBriefing.blocks = blocks;
        super.setBlocks(blocks);
        this.setBriefingBlocksActionable(this.currentBriefing);
        this.setGeneralStyles(this.currentBriefing.generalStyles);
        this.recentColorsService.setContext(this.currentBriefing.blocks, this.currentBriefing.generalStyles);
        this.updateCurrentBriefingMentions();
        this.checkIfBriefingHasDynamicElements();
    }

    /**
     * Duplicate a block
     *
     * @param block The block to duplicate
     */
    duplicateBlock(block: BlockModel, position?: string): void {
        const clonedBlock = block.clone();

        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            clonedBlock.displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        } else {
            clonedBlock.displayElement.displayType = BlockDisplayTypeEnum.TWIG;
        }

        if (clonedBlock.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || clonedBlock.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
            this.anonymizeMentionBlock(clonedBlock);
        }

        if (clonedBlock.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
            (clonedBlock as CuratorBlock).displayElement.searchCriteriaId = null;
            (clonedBlock as CuratorBlock).blocks.forEach(b => {
                if (b.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                    this.clearCloneAnalysisBlock(b);
                }
                if (b.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || b.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                    this.anonymizeMentionBlock(b);
                }
            });
        }

        if (clonedBlock.type.key === BriefingBlockTypeEnum.TITLE) {
            const titleDisplayElement = block.displayElement as BriefingTitleDisplayElementModel;
            const clonedTitleDisplayElement = clonedBlock.displayElement as BriefingTitleDisplayElementModel;
            clonedTitleDisplayElement.headingLevel = titleDisplayElement.headingLevel;
            clonedTitleDisplayElement.canAutomaticallyGenerated = titleDisplayElement.canAutomaticallyGenerated;
            clonedTitleDisplayElement.automaticallyGenerated = titleDisplayElement.automaticallyGenerated;
            clonedTitleDisplayElement.styles = titleDisplayElement.styles.clone ?
                titleDisplayElement.styles.clone() : JSON.parse(JSON.stringify(titleDisplayElement.styles));
            clonedTitleDisplayElement.displayType = titleDisplayElement.displayType;
        }

        // when duplicating a mention block, we have to anonymize the clone so each mention block has it's own saved search
        if (clonedBlock.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            this.clearCloneAnalysisBlock(clonedBlock);
        }
        if (block.type.key === BriefingBlockTypeEnum.CHART) {
            this.clearCloneChartBlock(clonedBlock);
            if (block.displayElement.styles['accentColor']) {
                clonedBlock.displayElement.styles['accentColor'] = block.displayElement.styles['accentColor'];
            }
        }

        if (clonedBlock.isLayoutBlockType()) {
            for (const b of (clonedBlock as LayoutBlock).blocks) {
                for (const column of b) {
                    if (column.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                        this.clearCloneAnalysisBlock(column);
                    }
                    if (column.type.key === BriefingBlockTypeEnum.CHART) {
                        this.clearCloneChartBlock(column);
                    }

                    if (column.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || column.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                        this.anonymizeMentionBlock(column);
                    }
                }
            }
        }

        if (!isNaN(+position)) {
            const index = this.blocks.indexOf(block);
            if (index < 0) {
                // shouldn't ever happen
                throw new Error('Item not in list');
            }
            this.blocks.splice(index, 0, clonedBlock);
        } else {
            const [blockPosition, columnIndex, positionInColumn] = position.split('.');
            const layoutBlock = this.blocks[+blockPosition];

            let column: BlockModel[];
            if (layoutBlock instanceof CuratorBlock) {
                // Curator blocks hold a flat list of blocks rather than columns
                column = layoutBlock.blocks;
            } else {
                column = (layoutBlock as LayoutBlock).blocks[+columnIndex];
            }

            const positionToAdd = +positionInColumn;
            column.splice(positionToAdd, 0, clonedBlock);
        }

        this.setBlocks(this.blocks);
        this.validateBlockCount();
    }

    anonymizeMentionBlock(block: BlockModel) {
        // when duplicating a mention block, we have to anonymize the clone so each mention block has it's own saved search
        if (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
            const displayElement = (block.displayElement as CuratedMentionListDisplayElementModel);
            displayElement.searchCriteria = displayElement?.getSearchCriteria()?.clone(true);
            displayElement.searchCriteriaId = null;
            displayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;
            displayElement.blockReferenceId = uuid.v4();

            // if enable duplicate mentions is unchecked, we clear the clone block
            if (!this.currentBriefing.enableDuplicateMentions) {
                displayElement.mentionIds = [];
                displayElement.mentions = [];
            }

            if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
            } else {
                displayElement.displayType = BlockDisplayTypeEnum.TWIG;
            }
        }

        // when duplicating a mention block, we have to anonymize the clone so each mention block has it's own saved search
        if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) {
            const displayElement = (block.displayElement as DynamicMentionListDisplayElementModel);
            displayElement.filterMentionSearchModel = displayElement?.getSearchCriteria()?.clone(true);
            displayElement.searchCriteriaId = null;
            displayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;
            displayElement.blockReferenceId = uuid.v4();
            if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
            } else {
                displayElement.displayType = BlockDisplayTypeEnum.TWIG;
            }
        }


    }

    clearCloneAnalysisBlock(clonedBlock: BlockModel) {
        const clonedDisplayElement = (clonedBlock.displayElement as AnalysisDisplayElementModel);
        clonedDisplayElement.filterMentionSearchModel = clonedDisplayElement?.getSearchCriteria()?.clone(true);
        clonedDisplayElement.searchCriteriaId = null;
    }

    clearCloneChartBlock(clonedBlock: BlockModel) {
        const clonedDisplayElement = (clonedBlock.displayElement as BriefingChartDisplayElementModel);

        (clonedDisplayElement?.chartBlock?.displayElement as ChartDisplayElementModel)?.series.forEach(series => {
            series.seriesUniqueUuid = uuid.v4();
        });

        clonedDisplayElement.mentionSearches.forEach(mentionSearch => {
            delete mentionSearch.id;
            delete mentionSearch.createdByUserId;
            delete mentionSearch.createdDate;
            delete mentionSearch.modifiedByUserId;
            delete mentionSearch.modifiedDate;
            delete mentionSearch.name;
        });
        clonedDisplayElement.searches = [];
    }

    /**
     * Updates the block type statuses of all of the block types
     */
    protected updateBlockTypeStatuses(): void {
        this.updateBlockTypeStatusAndReturnBlockCount();
        this.blockTypesProvider.next(this.blockTypes);
    }

    private countBlocks(blocks: BlockModel[]): BriefingBlockCount {
        const blockCounts: BriefingBlockCount = {
            totalCount: 0
        };

        for (const block of blocks) {
            const key = block.type.key;
            if (!blockCounts[key]) {
                blockCounts[key] = 0;
            }
            blockCounts[key]++;
            blockCounts.totalCount++;

            if (briefingLayoutBlockTypes.some(layoutBlock => layoutBlock.key === block.type.key)) {
                (block as LayoutBlock).blocks.forEach(innerBlocks => innerBlocks.forEach(innerBlock => {
                    const innerBlockKey = innerBlock.type.key;
                    if (!blockCounts[innerBlockKey]) {
                        blockCounts[innerBlockKey] = 0;
                    }
                    blockCounts[innerBlockKey]++;
                    blockCounts.totalCount++;
                    if (innerBlockKey === BriefingBlockTypeEnum.CHART) {
                        if ((innerBlock.displayElement as BriefingChartDisplayElementModel).chartBlock?.type) {
                            innerBlock.canAddChart = false;
                            innerBlock.canEditChart = true;
                        } else {
                            innerBlock.canAddChart = true;
                            innerBlock.canEditChart = false;
                        }
                    }
                }));
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                let countIncrease = 1;
                if ((block as CuratorBlock).displayElement.bulkAnalysisMessage.aiAnalysisType === AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT
                    && !(block as CuratorBlock).displayElement.hasGeneratedContent) {
                    countIncrease = (block as CuratorBlock).displayElement.bulkAnalysisMessage.numberKeyMessages;
                }
                (block as CuratorBlock).blocks.forEach(innerBlock => {
                    const innerBlockKey = innerBlock.type.key;

                    if (!blockCounts[innerBlockKey]) {
                        blockCounts[innerBlockKey] = 0;
                    }
                    blockCounts[innerBlockKey] = blockCounts[innerBlockKey] + countIncrease;
                    blockCounts.totalCount = blockCounts.totalCount + countIncrease;
                });
            }

            if (key === BriefingBlockTypeEnum.CHART) {
                if ((block.displayElement as BriefingChartDisplayElementModel).chartBlock?.type) {
                    block.canAddChart = false;
                    block.canEditChart = true;
                } else {
                    block.canAddChart = true;
                    block.canEditChart = false;
                }
            }
        }

        return blockCounts;
    }

    private updateBlockTypeStatusAndReturnBlockCount(): BriefingBlockCount {
        const blockCounts = this.countBlocks(this.blocks);

        if (blockCounts.totalCount >= this.briefingTotalBlockLimit) {
            this.briefingBlocks.forEach(block => block.canAdd = false);
            this.setCanDuplicateToBlocks(false);
        } else {
            this.briefingBlocks.forEach(block => block.canAdd = true);
            this.setCanDuplicateToBlocks(true);
            if (blockCounts[briefingAnalysisBlockType.key] >= this.briefingAnalysisBlockLimit) {
                briefingAnalysisBlockType.canAdd = false;
                this.setCanDuplicateToBlocks(false, briefingAnalysisBlockType.key);
            }
        }

        // if layout block contains ai analysis blocks, set canDuplicate to false if after duplicating, the total ai analysis block greater than the limit
        for (const block of this.blocks) {
            if (briefingLayoutBlockTypes.some(layoutBlock => layoutBlock.key === block.type.key)) {
                let numOfAiAnalysisInnerBlock = 0;
                (block as LayoutBlock).blocks.forEach(innerBlocks => innerBlocks.forEach(innerBlock => {
                    if (innerBlock.type.key === briefingAnalysisBlockType.key) {
                        numOfAiAnalysisInnerBlock++;
                    }
                }));

                if (blockCounts[briefingAnalysisBlockType.key] + numOfAiAnalysisInnerBlock > this.briefingAnalysisBlockLimit) {
                    block.canDuplicate = false;
                }
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                let numOfAiAnalysisInnerBlock = 0;
                let countIncrease = 1;
                if ((block as CuratorBlock).displayElement.bulkAnalysisMessage.aiAnalysisType === AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT
                    && !(block as CuratorBlock).displayElement.hasGeneratedContent) {
                    countIncrease = (block as CuratorBlock).displayElement.bulkAnalysisMessage.numberKeyMessages;
                }

                (block as CuratorBlock).blocks.forEach(innerBlock => {
                    if (innerBlock.type.key === briefingAnalysisBlockType.key) {
                        numOfAiAnalysisInnerBlock = numOfAiAnalysisInnerBlock + countIncrease;
                    }
                });

                if (blockCounts[briefingAnalysisBlockType.key] + numOfAiAnalysisInnerBlock > this.briefingAnalysisBlockLimit) {
                    block.canDuplicate = false;
                }
            }
        }

        this.totalBlockCount = blockCounts.totalCount;

        return blockCounts;
    }

    validateBlockCount(): boolean {
        let valid = true;
        const blockCounts = this.updateBlockTypeStatusAndReturnBlockCount();

        if (blockCounts.totalCount > this.briefingTotalBlockLimit) {
            valid = false;

            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Briefing.totalBlocks.error.heading',
                detail: this.localizationService.transform('Briefing.totalBlocks.error.message', {
                    number: blockCounts.totalCount
                })
            });
        }

        if (blockCounts[briefingAnalysisBlockType.key] && blockCounts[briefingAnalysisBlockType.key] > this.briefingAnalysisBlockLimit) {
            valid = false;
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Briefing.totalAiBlocks.error.heading',
                detail: this.localizationService.transform('Briefing.totalAiBlocks.error.message', {
                    number: blockCounts[briefingAnalysisBlockType.key]
                })
            });
        }

        return valid;
    }

    private setCanDuplicateToBlocks(canDuplicate: boolean, specificKey?: string) {
        for (const block of this.blocks) {
            if (specificKey) {
                if (specificKey === block.type.key) {
                    block.canDuplicate = canDuplicate;
                }
            } else {
                block.canDuplicate = canDuplicate;
            }

            if (briefingLayoutBlockTypes.some(layoutBlock => layoutBlock.key === block.type.key)) {
                (block as LayoutBlock).blocks.forEach(innerBlocks => innerBlocks.forEach(innerBlock => {
                    if (specificKey) {
                        if (specificKey === innerBlock.type.key) {
                            innerBlock.canDuplicate = canDuplicate;
                        }
                    } else {
                        innerBlock.canDuplicate = canDuplicate;
                    }
                }));
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks.forEach(innerBlock => {
                    if (specificKey) {
                        if (specificKey === innerBlock.type.key) {
                            innerBlock.canDuplicate = canDuplicate;
                        }
                    } else {
                        innerBlock.canDuplicate = canDuplicate;
                    }
                });
            }
        }
    }

    /**
     * Setter for blockToGeneralStylePropertiesMap
     */
    setBlockToGeneralStylePropertiesMap(blockGeneralStyles: Map<string, Map<string, string>>): void {
        this.blockToGeneralStylePropertiesMap = blockGeneralStyles;
    }

    /**
     * Setter for generalStylesToBlocks
     */
    setGeneralStylesToBlocksMap(generalStylesToBlock: Map<string, Map<string, string>>): void {
        this.generalStylesToBlocksMap = generalStylesToBlock;
    }

    /**
     * Gets a stream of requests to open the import dialog
     * NOTE: Import dialog is handled this way to avoid a circular dependency
     */
    public getImportDialogProvider(): Observable<BlockModel> {
        return this.importDialogProvider;
    }


    cacheTemplates(templates: Template[]) {
        templates.forEach(template => {
            if (template.isDefault && template.defaultReference) {
                this.defaultTemplates.set(
                    template.type.toUpperCase() + template.defaultReference.toUpperCase(),
                    template.template
                );
            }
        });

        this.hasDefaultTemplates$.next(true);
    }

    areLayoutBlocksAllowed(): boolean {
        return true;
    }

    /**
     * Sends a request to open the import dialog
     * NOTE: Import dialog is handled this way to avoid a circular dependency
     */
    public openImportDialog(block?: BlockModel): void {
        this.importDialogProvider.next(block);
    }

    /**
     * Sets the default template on the briefing block types
     */
    setDefaultBriefingTemplatesOnBlockTypes(blockTypes: BlockTypeModel[]): BlockTypeModel[] {
        return blockTypes.map(blockType => {
            blockType.twig = this.getDefaultTemplateByBlockType(
                blockType.key,
                BriefingStyleStateEnum.BRIEFING_1
            );

            return blockType;
        });
    }

    /**
     * Returns the default template for the provided block type
     */
    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        // Backwards compatibility and support for images
        if (!defaultReference || defaultReference === BriefingStyleStateEnum.DEFAULT) {
            defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        }
        return this.defaultTemplates.get(briefingBlockTypeEnumToStyleTypeEnum.get(blockType as BriefingBlockTypeEnum) + defaultReference?.toUpperCase());
    }

    /**
     * Handles changes to briefing block content style change
     */
    updateContentBlockStyle(block: BlockModel, property: string, value: any) {
        switch (value) {
            case BriefingStyleStateEnum.DEFAULT:
            case BriefingStyleStateEnum.BRIEFING_1:
            case BriefingStyleStateEnum.BRIEFING_2:
            case BriefingStyleStateEnum.BRIEFING_3:
            case BriefingStyleStateEnum.CANVAS_1:
                const defaultStyle = this.getDefaultStyle(<BriefingBlockTypeEnum>block.type.key, value);
                this.setPropertyValue(block.displayElement, 'styles', defaultStyle.clone(block.displayElement.styles));
                break;
            case BriefingStyleStateEnum.CUSTOM:
                break;
            default:
                break;
        }

        if (!this.blockToGeneralStylePropertiesMap) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(<BriefingBlockTypeEnum>block.type.key);
            this.setPropertyValue(this.generalStyles, generalStyleProperty, block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }

        block.displayElement.styles = block.displayElement.styles.clone();
        this.formFieldUpdated();
        this.selectBlock(block);
    }

    /**
     * Handles change to the selection for isBoundToGlobalStyles
     */
    isBoundToGlobalStylesChange(block: BlockModel, property: string, value: any) {
        if (!this.blockToGeneralStylePropertiesMap || !value) {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            const generalStyleProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(<BriefingBlockTypeEnum>block.type.key);
            this.setPropertyValue(this.generalStyles, generalStyleProperty, block.displayElement.styles.clone());
            this.updateBoundBlocksOfSameType(block, 'styles', block.displayElement.styles.clone());
        }
        this.formFieldUpdated();
    }

    /**
     * Handles changes made by the block form and updates the blocks to keep bound relationships in sync
     */
    blockFormUpdated(block: BlockModel, property: string, value: any): void {
        if (block) {
            const isDefault: boolean = value &&
                (value.styleState === BriefingStyleStateEnum.DEFAULT ||
                    value.styleState === BriefingStyleStateEnum.BRIEFING_1 ||
                    value.styleState === BriefingStyleStateEnum.BRIEFING_2 ||
                    value.styleState === BriefingStyleStateEnum.BRIEFING_3 ||
                    value.styleState === BriefingStyleStateEnum.CANVAS_1
                ) && value.displayStyleState !== BriefingStyleStateEnum.CUSTOM;

            if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) {
                this.updatedAutomaticBlock$.next(block);
            }

            if (block.type.key === BriefingBlockTypeEnum.IMAGE && property === 'images' && !value?.length) {
                (block.displayElement as BriefingImageDisplayElementModel).imageProperties = null;
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                const curatorDisplayElement = (block as CuratorBlock).displayElement;
                if (property === 'bulkAnalysisMessage.aiAnalysisType') {
                    // @Hack force form appear condition to refresh
                    this.selectBlock(this.selectedBlock);
                    this.validateBlockCount();
                }

                if (property === 'bulkAnalysisMessage.numberKeyMessages') {
                    this.validateBlockCount();
                }

                if (property === 'bulkAnalysisMessage.focus') {
                    (block as CuratorBlock).blocks?.forEach(subBlock => {
                        if (subBlock.type.key === BriefingBlockTypeEnum.ANALYSIS && subBlock.displayElement.automaticallyGenerated) {
                            const analysisDisplayElement = subBlock.displayElement as AnalysisDisplayElementModel;
                            if (!analysisDisplayElement.analysisField) {
                                analysisDisplayElement.analysisField = new CoverageInsights();
                            }
                            analysisDisplayElement.analysisField.focus = value;
                        }
                    });
                }

                if (property === 'bulkAnalysisMessage.selectedLanguageTag') {
                    curatorDisplayElement.bulkAnalysisMessage.selectedLanguageOption =
                        promptLanguageOptions.find(option => curatorDisplayElement.bulkAnalysisMessage.selectedLanguageTag === option.value);

                    (block as CuratorBlock).blocks?.forEach(subBlock => {
                        if (subBlock.type.key === BriefingBlockTypeEnum.ANALYSIS && subBlock.displayElement.automaticallyGenerated) {
                            const analysisDisplayElement = subBlock.displayElement as AnalysisDisplayElementModel;
                            if (!analysisDisplayElement.analysisField) {
                                analysisDisplayElement.analysisField = new CoverageInsights();
                            }
                            analysisDisplayElement.analysisField.selectedLanguageOption =
                                promptLanguageOptions.find(option => curatorDisplayElement.bulkAnalysisMessage.selectedLanguageTag === option.value);
                        }
                    });
                }
            }

            if (property === 'automaticallyGenerated') {
                if (block.displayElement.automaticallyGenerated) {
                    switch (block.type.key) {
                        case BriefingBlockTypeEnum.TITLE:
                            const titleDisplayElement = block.displayElement as BriefingTitleDisplayElementModel;
                            titleDisplayElement.cachedText = titleDisplayElement.text;
                            titleDisplayElement.text = '';
                            block.errors = [];
                            this.selectBlock(block);
                            break;
                        case BriefingBlockTypeEnum.ANALYSIS:
                        case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                            this.blocks.forEach(item => {
                                if (item.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                                    const curatorBlock = item as CuratorBlock;
                                    curatorBlock.blocks?.forEach(innerBlock => {
                                        if (innerBlock === block) {
                                            if (block.displayElement instanceof AnalysisDisplayElementModel) {
                                                block.displayElement.generateOnSend = true;
                                            }

                                            (block.displayElement as DynamicMentionListDisplayElementModel).backendDataSource
                                                = curatorBlock.displayElement.backendDataSource;

                                            (block.displayElement as DynamicMentionListDisplayElementModel).searchCriteriaId
                                                = curatorBlock.displayElement.searchCriteriaId;
                                            (block.displayElement as DynamicMentionListDisplayElementModel).sourceType = curatorBlock.displayElement.sourceType;
                                            (block.displayElement as DynamicMentionListDisplayElementModel).title = '';
                                        }
                                    });
                                }
                            });
                            block.errors = [];
                            this.selectBlock(block);
                            break;
                    }
                } else {
                    switch (block.type.key) {
                        case BriefingBlockTypeEnum.TITLE:
                            const titleDisplayElement = block.displayElement as BriefingTitleDisplayElementModel;
                            titleDisplayElement.text = titleDisplayElement.cachedText;
                            break;
                        case BriefingBlockTypeEnum.ANALYSIS:
                            const analysisDisplayElement = block.displayElement as AnalysisDisplayElementModel;
                            analysisDisplayElement.filterMentionSearchModel = analysisDisplayElement.filterMentionSearchModel.clone();
                            analysisDisplayElement.backendDataSource = analysisDisplayElement.backendDataSource.clone();
                            analysisDisplayElement.searchCriteriaId = null;
                            this.selectBlock(block);
                            break;
                        case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                            const mentionDisplayElement = block.displayElement as DynamicMentionListDisplayElementModel;
                            mentionDisplayElement.backendDataSource = mentionDisplayElement.backendDataSource.clone();
                            mentionDisplayElement.searchCriteriaId = null;
                            mentionDisplayElement.filterMentionSearchModel = mentionDisplayElement.filterMentionSearchModel.clone();
                            mentionDisplayElement.title = 'Mentions';
                            this.selectBlock(block);
                            break;
                    }
                }
            }

            if (value && value.hasOwnProperty('styleState') && isDefault) {
                this.updateContentBlockStyle(block, 'styles.styleState', value.styleState);

                return;
            }

            if (property === 'sortBy' && block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                // re-sort the mentions if curated block sort by changed
                const element = block.displayElement as CuratedMentionListDisplayElementModel;
                element.sortMentions();

                this.formFieldUpdated();
                return;
            }
            if (property === 'styles' && block.type.key === BriefingBlockTypeEnum.CHART) {
                const chartDisplayElement = (block.displayElement as BriefingChartDisplayElementModel)?.chartBlock?.displayElement as ChartDisplayElementModel;
                if (chartDisplayElement) {
                    const seriesData = chartDisplayElement.series[0].dataProvider.getValue();
                    if (!seriesData) {
                        this.refreshBlockData(block);
                    }
                }
            }

            if (property === 'isBoundToGlobalStyles' && value) {
                this.isBoundToGlobalStylesChange(block, property, value);

                return;
            }


            if (property !== 'styles') {
                this.formFieldUpdated();

                return;
            }

            if (!this.blockToGeneralStylePropertiesMap) {
                this.formFieldUpdated();

                return;
            }

            const generalStylesProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(<BriefingBlockTypeEnum>block.type.key);

            if (generalStylesProperty && block.displayElement.isBoundToGlobalStyles) {
                this.setPropertyValue(this.generalStyles, generalStylesProperty, block.displayElement.styles.clone());
                this.updateBoundBlocksOfSameType(block, property, value);
            }

            this.formFieldUpdated();
        }
    }

    getUpdatedAutomaticBlock(): Observable<BlockModel> {
        return this.updatedAutomaticBlock$;
    }

    /**
     * Updates all blocks of the same type that are bound to generalStyles
     */
    override updateBoundBlocksOfSameType(block: BlockModel, property: string, value: any): void {
        const keysToUpdate = [];

        // When either type of mention block is updated, update all mention blocks
        if (
            block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST
        ) {
            keysToUpdate.push(BriefingBlockTypeEnum.CURATED_MENTION_LIST, BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST);
        } else {
            keysToUpdate.push(block.type.key);
        }

        this.blocks.forEach(blockInLoop => {
            this.updateBoundBlockOfSameType(blockInLoop, block, property, value);
            if (blockInLoop.isLayoutBlockType()) {
                (blockInLoop as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.updateBoundBlockOfSameType(x, block, property, value);
                    });
                });
            }
            if (blockInLoop.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (blockInLoop as CuratorBlock).blocks.forEach(x => this.updateBoundBlockOfSameType(x, block, property, value));
            }

            return blockInLoop;
        });
    }

    /**
     * Updates the blocks when changes occur so that things are notified
     */
    formFieldUpdated(): void {
        super.formFieldUpdated();
    }

    /**
     * Applies the generalStyles properties to the provided block properties
     */
    applyGeneralStylesToBlock(block: BlockModel) {
        if (!this.blockToGeneralStylePropertiesMap) {
            return;
        }
        const blockPropertyGeneralStyleProperty = this.blockToGeneralStylePropertiesMap.get(block.type.key);

        blockPropertyGeneralStyleProperty.forEach((generalStyleProperty, blockProperty) => {
            const generalStylePropertyValue = this.getPropertyValue(this.generalStyles, generalStyleProperty);

            if (generalStylePropertyValue !== null && generalStylePropertyValue !== undefined && block.displayElement.isBoundToGlobalStyles) {
                this.setPropertyValue(block.displayElement, blockProperty, generalStylePropertyValue);
            }
        });

        // Set the template if needed
        if (
            block.displayElement.styles.styleState !== BriefingStyleStateEnum.CUSTOM &&
            block.displayElement.styles.styleState !== BriefingStyleStateEnum.DEFAULT
        ) {
            const savedStyle = this.styles?.find(style => style.id === block.displayElement.styles.styleState);
            if (savedStyle && block.displayElement.styles.setFromApiModel) {
                const style: GenericStyleModel = savedStyle.clone().style;
                style.styleState = savedStyle.id;
                if (savedStyle.template) {
                    style.template = savedStyle.template;
                }
                block.displayElement.styles.setFromApiModel(style);
            }
        }

        this.notifyBlockDisplayElementChanged(block, true);
    }

    /**
     * Handles change events to generalStyles
     */
    generalStylesBlockUpdated(blockType: BriefingBlockTypeEnum, blockStyles: GenericStyleModel): void {
        // Mention blocks, curated or automatic, share the same general style, if they updated, update all of both
        if (this.isBriefingMentionBlockType(blockType)) {
            this.blocks.forEach(blockInLoop => {
                if (blockInLoop.type.key === LayoutBlockTypesEnum.briefingOne) {
                    (blockInLoop as LayoutBlock).blocks.forEach(column => {
                        column.forEach(x => {
                            if (!this.isBriefingMentionBlockType(x.type.key as BriefingBlockTypeEnum)) {
                                return;
                            }
                            const displayElement = x.displayElement;
                            if (displayElement.isBoundToGlobalStyles) {
                                this.setPropertyValue(displayElement, 'styles', blockStyles.clone());
                            }
                        });
                    });
                } else if (blockInLoop.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                    (blockInLoop as CuratorBlock).blocks.forEach(x => {
                        if (!this.isBriefingMentionBlockType(x.type.key as BriefingBlockTypeEnum)) {
                            return;
                        }
                        const displayElement = x.displayElement;
                        if (displayElement.isBoundToGlobalStyles) {
                            this.setPropertyValue(displayElement, 'styles', blockStyles.clone());
                        }
                    });
                } else {
                    if (!this.isBriefingMentionBlockType(blockInLoop.type.key as BriefingBlockTypeEnum)) {
                        return;
                    }
                    const displayElement = blockInLoop.displayElement;
                    if (displayElement.isBoundToGlobalStyles) {
                        this.setPropertyValue(displayElement, 'styles', blockStyles.clone());
                    }
                }
            });
        } else {
            this.blocks.forEach(block => {
                if (blockType === block.type.key && block.displayElement.isBoundToGlobalStyles) {
                    this.setPropertyValue(block.displayElement, 'styles', blockStyles.clone(block.displayElement.styles as GenericStyleModel));
                    this.notifyBlockDisplayElementChanged(block);
                }
                if (block.isLayoutBlockType()) {
                    (block as LayoutBlock).blocks.forEach(column => {
                        column.forEach(x => {
                            if (blockType === x.type.key && x.displayElement.isBoundToGlobalStyles) {
                                this.setPropertyValue(x.displayElement, 'styles', blockStyles.clone(x.displayElement.styles as GenericStyleModel));
                                this.notifyBlockDisplayElementChanged(x);
                            }
                        });
                    });
                }
                if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                    (block as CuratorBlock).blocks.forEach(x => {
                        if (blockType === x.type.key && x.displayElement.isBoundToGlobalStyles) {
                            this.setPropertyValue(x.displayElement, 'styles', blockStyles.clone(x.displayElement.styles as GenericStyleModel));
                            this.notifyBlockDisplayElementChanged(x);
                        }
                    });
                }
            });
        }

        if (this.selectedBlock) {
            this.selectedBlockUpdated();
        }
        this.setTemplatesOnBlocks(this.currentBriefing?.blocks);
        this.generalStylesProvider.next(this.generalStyles);
    }

    private isBriefingMentionBlockType(blockType: BriefingBlockTypeEnum): boolean {
        return blockType === BriefingBlockTypeEnum.CURATED_MENTION_LIST || blockType === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST;
    }

    public showMentionLimitReachedDialog(): void {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'briefing.mentionLimitReachedDialog',
                heading: 'Briefing.showMentionLimitReachedHeading',
                message: this.localizationService.transform(
                    'Briefing.showMentionLimitReachedMessage',
                    { limitValue: this.maxNumberOfMentionsInBriefing }
                ),
                showCancel: false,
                okLabel: 'Briefing.showMentionLimitReachedOkButton'
            },
            outputs: {
                dialogClose: () => { }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public showMentionLimitReachedOnSaveDialog(numberofMentions: number): void {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'briefing.saveAsDraft.mentionLimitReachedDialog',
                heading: 'Briefing.saveAsDraft.showMentionLimitReachedHeading',
                message: this.localizationService.transform(
                    'Briefing.saveAsDraft.showMentionLimitReachedMessage',
                    {
                        value: numberofMentions,
                        limitValue: this.maxNumberOfMentionsInBriefing
                    }
                ),
                showCancel: false,
                okLabel: 'Briefing.showMentionLimitReachedOkButton'
            },
            outputs: {
                dialogClose: () => { }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public showMentionLimitReachedOnPreviewDialog(numberofMentions: number): void {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'briefing.preview.mentionLimitReachedDialog',
                heading: 'Briefing.preview.showMentionLimitReachedHeading',
                message: this.localizationService.transform(
                    'Briefing.preview.showMentionLimitReachedMessage',
                    {
                        value: numberofMentions,
                        limitValue: this.maxNumberOfMentionsInBriefing
                    }
                ),
                showCancel: false,
                okLabel: 'Briefing.showMentionLimitReachedOkButton'
            },
            outputs: {
                dialogClose: () => { }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public showMentionLimitReachedOnSendDialog(numberofMentions: number): void {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'briefing.send.mentionLimitReachedDialog',
                heading: 'Briefing.send.showMentionLimitReachedHeading',
                message: this.localizationService.transform(
                    'Briefing.send.showMentionLimitReachedMessage',
                    {
                        value: numberofMentions,
                        limitValue: this.maxNumberOfMentionsInBriefing
                    }
                ),
                showCancel: false,
                okLabel: 'Briefing.showMentionLimitReachedOkButton'
            },
            outputs: {
                dialogClose: () => { }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public addCurrentBriefingMentions(newMentions: Mention[]) {
        this.currentBriefingMentions.push(...newMentions);
        this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
    }

    /**
     * Updates the briefing to delete
     */
    public confirmDeleteBriefing(briefingToDelete: BriefingListItemModel): void {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Briefings.deleteBriefing',
                message: this.localizationService.transform('Briefing.confirmDeleteBriefingMessage', { name: briefingToDelete.name }),
                alertMessage: 'Briefing.confirmDeleteBriefings',
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete',
                id: 'BriefingsDelete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.deleteBriefing(briefingToDelete);
                    } else {
                        this.updateBriefingListItems();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    /**
     * Helper method that deletes a briefing from a BriefingListItemModel
     */
    public deleteBriefing(briefingListItem: BriefingListItemModel) {
        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: 'Briefing.deleteBriefingSuccessHeading',
            detail: this.localizationService.transform('Briefing.deleteBriefingSuccessMessage', {
                name: briefingListItem.name
            })
        } as ToastMessageOptions;

        const errorMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.error,
            summary: 'Briefing.deleteBriefingErrorHeading',
            detail: this.localizationService.transform('Briefing.deleteBriefingErrorMessage', {
                name: briefingListItem.name
            })
        } as ToastMessageOptions;

        this.briefingApi.deleteById(briefingListItem.id).subscribe({
            next: () => {
                this.messageService.add(successMessage);

                if (this.currentBriefing && briefingListItem.id === this.currentBriefing.id) {
                    this.routingService.gotoBriefings();
                }

                // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                this.updateBriefingListItems();
            },
            error: () => this.messageService.add(errorMessage)
        });
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    public confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'BriefingsBulkDelete',
                    heading: 'Briefings.bulkDeleteBriefing',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Briefing.confirmBulkDeleteBriefings.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Briefing.confirmBulkDeleteBriefings.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDeleteBriefings(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });

    }

    private bulkDeleteBriefings(ids: number[]): void {
        const observables = ids.map(id => this.briefingApi.deleteById(id));
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Briefing.bulkDeleteBriefingSuccessHeading',
                    detail: this.localizationService.transform('Briefing.bulkDeleteBriefingSuccessMessage', {
                        number: result.length
                    })
                });
                if (this.currentBriefing && ids.includes(this.currentBriefing.id)) {
                    this.routingService.gotoBriefings();
                }

                this.updateBriefingListItems();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Briefing.bulkDeleteBriefingErrorHeading',
                    detail: 'Briefing.bulkDeleteBriefingErrorMessage'
                });
                this.updateBriefingListItems();
            }
        });
    }


    /**
     * Saves the curated saved searches so that organizations can be maintained
     */
    public saveBriefingSavedSearches(): Observable<any[]> {
        const curatedBlocks = this.getAllBlocksWithSearchCriteria();
        const chartBlocks = BriefingsHelper.getChartBlocks(this.currentBriefing);
        const hasValidChart = chartBlocks.some(chartBlock => !!(chartBlock.displayElement as BriefingChartDisplayElementModel).chartBlock);
        if (curatedBlocks.length < 1 && !hasValidChart) {
            return of(null);
        }
        const elements: DisplayElementWithSearchCriteriaModel[] = curatedBlocks.map((block) => {
            return block.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
        });
        const topicRequests: Observable<any>[] = [];
        for (const el of elements) {
            const searchCriteria = el.getSearchCriteria();
            if (searchCriteria && !(el as unknown as DisplayElementModel).automaticallyGenerated) {
                if ((el instanceof DynamicMentionListDisplayElementModel) || (el instanceof AnalysisDisplayElementModel)) {

                    // For automatic block, B.E only set summary field when the mention happens to be saved to account before
                    // B/E logic:
                    // if (searchCriteria.snippetArea === SnippetAreaEnum.LEAD) -> set lead to mention.body to call Chiron
                    // else -> set contextual snippet to mention.body to call Chiron
                    if (el.styles.mentionContent === MentionContentCustomizationEnum.lead) {
                        searchCriteria.snippetArea = SnippetAreaEnum.LEAD;
                    } else {
                        delete searchCriteria.snippetArea;
                    }

                    this.processMentionSearch(searchCriteria);
                }

                if (!el.searchCriteriaId) {
                    if (!searchCriteria.name) {
                        // Name can't be blank, must be unique
                        searchCriteria.name = `${+new Date()}${Math.random()}`;
                    }
                    searchCriteria.type = SavedSearchType.BRIEFING;
                    if (!el.dateRangeAllowed && searchCriteria.searchCriteria && searchCriteria.searchCriteria.query) {
                        searchCriteria.searchCriteria.query.dateQuery = null;
                    }
                    // Make sure auto saving is off for these topics, it should never be on
                    if (searchCriteria?.autoSavingConfig) {
                        searchCriteria.autoSavingConfig.enabled = false;
                    } else {
                        searchCriteria.autoSavingConfig = new AutoSavingConfigModel();
                        searchCriteria.autoSavingConfig.enabled = false;
                    }
                    topicRequests.push(
                        this.savedSearchesApi.create(searchCriteria.toApiModel()).pipe(
                            map((id) => {
                                el.searchCriteriaId = id;
                                searchCriteria.id = id;

                                return id;
                            }))
                    );
                } else {
                    // @TODO only update when there's actually a change
                    if (!el.dateRangeAllowed && searchCriteria.searchCriteria && searchCriteria.searchCriteria.query) {
                        searchCriteria.searchCriteria.query.dateQuery = null;
                    }

                    const elementSearchCriteria = el.getSearchCriteria();
                    // Make sure auto saving is off for these topics, it should never be on
                    if (elementSearchCriteria?.autoSavingConfig) {
                        elementSearchCriteria.autoSavingConfig.enabled = false;
                    } else {
                        elementSearchCriteria.autoSavingConfig = new AutoSavingConfigModel();
                        elementSearchCriteria.autoSavingConfig.enabled = false;
                    }

                    topicRequests.push(this.savedSearchesApi.update(elementSearchCriteria.toApiModel()));
                }
            }
        }

        const chartElements = BriefingsHelper.getChartBlocks(this.currentBriefing).map(block => block.displayElement as BriefingChartDisplayElementModel);

        chartElements?.forEach((chartElement) => {
            chartElement.getSearchCriteria().forEach(searchCriteria => {
                this.processMentionSearch(searchCriteria);
                if (!searchCriteria.id) {
                    if (!searchCriteria.name) {
                        // Name can't be blank, must be unique
                        searchCriteria.name = `${+new Date()}${Math.random()}`;
                    }
                    searchCriteria.type = SavedSearchType.BRIEFING;

                    topicRequests.push(
                        this.savedSearchesApi.create(this.addProminenceAndFacetsToSearchCriteria(searchCriteria)).pipe(
                            map((id) => {
                                searchCriteria.id = id;

                                return id;
                            }))
                    );
                } else {
                    topicRequests.push(this.savedSearchesApi.update(this.addProminenceAndFacetsToSearchCriteria(searchCriteria)));
                }
            });
        });

        // @TODO delete saved searches that existed from a previous save and are no longer used
        if (topicRequests.length < 1) {
            return of(null);
        }
        return forkJoin(topicRequests);
    }


    // we only want to start the facetSearch & prominenceByReachRequest on search criterias that are in chart blocks
    addProminenceAndFacetsToSearchCriteria(searchCriteria: MentionSearchModel): SavedMentionSearchApiModel {
        // Make sure auto saving is off for these topics, it should never be on
        if (searchCriteria?.autoSavingConfig) {
            searchCriteria.autoSavingConfig.enabled = false;
        } else {
            searchCriteria.autoSavingConfig = new AutoSavingConfigModel();
            searchCriteria.autoSavingConfig.enabled = false;
        }

        const searchCriteriaApi = searchCriteria.toApiModel();

        searchCriteriaApi.facetSearch = {
            dateFacets: searchCriteria?.searchCriteria?.dateFacets,
            facets: searchCriteria?.searchCriteria?.facets,
            neoFacets: searchCriteria?.searchCriteria?.neoFacets
        };

        if (searchCriteria?.searchCriteria?.prominenceByReachRequest) {
            searchCriteriaApi.prominenceByReachRequest = searchCriteria?.searchCriteria?.prominenceByReachRequest;
        }

        searchCriteriaApi.isWordCloudRequest = searchCriteria?.searchCriteria?.isWordCloudRequest;

        return searchCriteriaApi;
    }


    private processMentionSearch(searchCriteria: MentionSearchModel) {
        // convert currency to USD as B.E currently only supports USD
        if (searchCriteria.searchCriteria?.query?.ave?.min) {
            searchCriteria.searchCriteria.query.ave.min =
                this.currencyConversionService.convertUserCurrencyToUSD(searchCriteria.searchCriteria?.query?.ave?.min);
        }

        if (searchCriteria.searchCriteria?.query?.ave?.max && searchCriteria.searchCriteria?.query?.ave?.max !== Mention.maxAudienceReach) {
            searchCriteria.searchCriteria.query.ave.max =
                this.currencyConversionService.convertUserCurrencyToUSD(searchCriteria.searchCriteria?.query?.ave?.max);
        }

        if (searchCriteria.searchCriteria?.query?.dateQuery?.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            // Overwrite default/user preference timezone with briefing timezone for dates like Today, Yesterday
            if (this.currentBriefing?.sendAt?.timeZone) {
                searchCriteria.searchCriteria.query.dateQuery.timezone = this.currentBriefing?.sendAt?.timeZone;
            } else {
                searchCriteria.searchCriteria.query.dateQuery.timezone = this.preferences.timeZone;
            }
        }
    }

    private saveToMediaService(): Observable<boolean> {
        const requests: Observable<boolean>[] = [];
        this.currentBriefing?.blocks.forEach(block => {
            this.checkAndCreateImage(block, requests);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.checkAndCreateImage(x, requests);
                    });
                });
            }
        });

        if (requests?.length) {
            return combineLatest(requests).pipe(map(validateMedia => {
                return !validateMedia.some(x => x === false);
            }));
        } else {
            return of(true);
        }
    }

    private checkAndCreateImage(block: BlockModel, requests: Observable<boolean>[]): void {
        const displayElement = block.displayElement as BriefingChartDisplayElementModel;

        if (block.type.key === BriefingBlockTypeEnum.CHART) {
            if (displayElement.chart.generateOnSend) {
                if (displayElement?.chart?.chartUrl && !displayElement?.chart?.chartUrl?.includes('agilitypr.media')) {
                    delete displayElement.chart.chartUrl;
                }
                return;
            }
            if (displayElement?.chart?.chartUrl && !displayElement?.chart?.chartUrl?.includes('agilitypr.media')) {
                const file = base64ToFile(displayElement.chart.chartUrl, `briefing_chart_${Date.now()}`);

                delete displayElement.chart.chartUrl;

                requests.push(
                    this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC, 'false').pipe(
                        map((result) => {
                            const imageMediaResult: MediaResultModel = result?.mediaResults?.find(mediaResult => {
                                return (mediaResult?.media?.name === file.name) || (mediaResult?.metadata?.submissionName === file.name);
                            });

                            if (imageMediaResult) {
                                const url = imageMediaResult?.media?.url;
                                const errorCode = imageMediaResult?.codedError?.code;

                                if (errorCode) {
                                    this.handleMediaErrorCode(errorCode);
                                    return (false);
                                }
                                if (url) {
                                    displayElement.chart.chartUrl = url;
                                } else {
                                    return (false);
                                }
                            } else {
                                const error = result.mediaResults.find(mediaResult => !!mediaResult.codedError);
                                this.handleMediaErrorCode(error?.codedError?.code);
                                return false;
                            }
                            return true;
                        }),
                        catchError(error => {
                            if (error.status === 429) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                                    detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                                });
                                return of(false);
                            }
                            return throwError(() => error);
                        })
                    )
                );
            }
        }
    }

    handleMediaErrorCode(errorCode: MediaServiceErrorCodesEnum) {
        if (errorCode === MediaServiceErrorCodesEnum.insufficientSpace) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'PageBuilder.error.imageLimitHeader',
                    messageHtml: 'PageBuilder.error.imageLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'PageBuilder.error.imageLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {

                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.fileMaxSize) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage')
            });
            return;
        }

        if (errorCode !== null) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('PageBuilder.error.imageUploadFailed'),
                detail: this.localizationService.transform('PageBuilder.error.imageUploadFailedMessage')
            });
        }
    }

    setSaving(saving: boolean) {
        this.saving$.next(saving);
    }

    getSaving(): Observable<boolean> {
        return this.saving$;
    }
    /**
     * Saves the current briefing to the API
     */
    public saveCurrentBriefing(): Observable<null> {
        this.deduplicateRecipients();

        // hack for CIB, the image would set it back to the base64 in BriefingChartBlockComponent
        // after thesaveToMediaService would generate a media
        this.setSaving(true);

        return this.validateFilters().pipe(
            mergeMap(validate => {
                if (!validate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.saveBriefingErrorHeading',
                        detail: this.localizationService.transform('Briefing.saveBriefingInvalidFilter.error')
                    });

                    this.setSaving(false);
                    return of(null);
                }

                // save all of the curated saved searches first
                return this.saveBriefingSavedSearches().pipe(
                    tap(() => this.deselectBlock(true)),
                    mergeMap(() => {
                        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.EMAIL) {
                            return this.saveToMediaService();
                        } else {
                            return of(null);
                        }
                    }),
                    mergeMap(() => {
                        return this.briefingApi.save(this.currentBriefing.toApiModel());
                    }),
                    mergeMap((response: BriefingApiModel) => {
                        return this.patchDigestChanges(this.currentBriefing?.id ? this.currentBriefing.id : response.briefingId).pipe(map(() => response));
                    }),
                    mergeMap((response: BriefingApiModel) => {
                        // Carry forward tracking fields if the save response omits them.
                        if (response.enableClickTracking === undefined) {
                            response.enableClickTracking = this.currentBriefing.clickTracking;
                        }
                        if (response.enableOpenTracking === undefined) {
                            response.enableOpenTracking = this.currentBriefing.openTracking;
                        }

                        // Copy the current briefing to the cached briefing because they should now be in sync
                        this.cachedCurrentBriefing = cloneDeep(this.currentBriefing);
                        // Update the list in case save added a new one or changed the name of an existing one
                        this.updateBriefingListItems();

                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: 'Briefing.saveBriefingSuccessHeading',
                            detail: this.localizationService.transform('Briefing.saveBriefingSuccessMessage', {
                                name: this.currentBriefing.name
                            })
                        });
                        this.setSaving(false);

                        const isCreate = !this.currentBriefing.id;
                        if (isCreate) {
                            this.briefingInitialCreate = false;
                            this.currentBriefing.id = response.briefingId;
                        }
                        const briefingId = this.currentBriefing.id;

                        // Await the model rebuild so it cannot race with subsequent GET requests.
                        // Previously this was fire-and-forget, which allowed a stale emission to
                        // overwrite the correct value loaded by the send/report-route GET.
                        return from(this.setCurrentBriefingFromApi(briefingId, null, response)).pipe(
                            map(() => {
                                if (isCreate) {
                                    this.canLeaveBypass = true;
                                    const isCanvas = this.currentBriefing?.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE;
                                    if (isCanvas) {
                                        this.routingService.gotoCanvas(response.briefingId);
                                    } else {
                                        this.routingService.gotoBriefing(response.briefingId);
                                    }
                                }
                                return null as null;
                            })
                        );
                    })
                );
            }),
            catchError((error) => {
                this.updateBriefingListItems();

                // not sure this is valid anymore? when there are multiple blocks with errors, it will return details?.length > 1
                // we can maybe look at adding the generic error here, but not a high priority
                if (error?.error?.details?.length > 1) {
                    const invalidEmails: string[] = [];
                    error.error.details.forEach(x => {
                        if (x.code === BriefingsApiErrorEnum.invalidEmail) {
                            invalidEmails.push(x.invalidValue);
                        }
                    });

                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.saveBriefingErrorHeading',
                        detail: 'Briefing.saveBriefingInvalidEmail.error'
                    });
                    this.invalidEmailPrompt(invalidEmails);

                } else {
                    switch (error?.error?.details?.[0]?.code) {
                        case BriefingsApiErrorEnum.nameDuplicate:
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefing.saveBriefingErrorHeading',
                                detail: this.localizationService.transform('Briefing.saveBriefingDuplicateNameErrorMessage', {
                                    name: this.currentBriefing.name
                                })
                            });
                            break;
                        case BriefingsApiErrorEnum.invalidEmail:
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefing.saveBriefingErrorHeading',
                                detail: 'Briefing.saveBriefingInvalidEmail.error'
                            });
                            this.invalidEmailPrompt([error?.error?.details?.[0]?.invalidValue]);
                            break;
                        case BriefingsApiErrorEnum.alreadySent:
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefing.updateBriefing.alreadySent.heading',
                                detail: 'Briefing.updateBriefing.alreadySent.message'
                            });
                            break;
                        default:
                            console.error('Error saving briefing: ', error);
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefing.saveBriefingErrorHeading',
                                detail: this.localizationService.transform('Briefing.saveBriefingErrorMessage', {
                                    name: this.currentBriefing.name
                                })
                            });
                            break;
                    }
                }

                this.setSaving(false);
                throw error;
            }),
        );
    }

    public patchDigestChanges(briefingId: number): Observable<boolean> {
        const current = this.currentBriefing.digests || [];
        const cached = this.cachedCurrentBriefing?.digests || [];

        const idsToAdd = difference(current, cached);
        const idsToRemove = difference(cached, current);

        if (idsToAdd.length || idsToRemove.length) {
            const patchApi: DigestPatchApi = {
                idsToAdd,
                idsToRemove
            };

            const patchKey = JSON.stringify(patchApi);
            if (patchKey === this.lastDigestPatchKey) {
                return of(false);
            }

            this.lastDigestPatchKey = patchKey;

            return this.digestService.patchDigestsForBriefing(briefingId, patchApi)
                .pipe(
                    tap(() => {
                        if (this.cachedCurrentBriefing) {
                            this.cachedCurrentBriefing.digests = [...current];
                        }
                    }),
                    map(() => true),
                );
        }

        return of(false);
    }

    private validateFilters(): Observable<boolean> {
        const requests: Observable<boolean>[] = [];
        this.currentBriefing?.blocks.forEach(block => {
            this.validateFiltersForBlock(block, requests);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.validateFiltersForBlock(x, requests);
                    });
                });
            }
        });

        if (requests.length) {
            return combineLatest(requests).pipe(map(validateArray => {
                return !validateArray.some(x => x === false);
            }));
        } else {
            return of(true);
        }
    }

    private validateFiltersForBlock(block: BlockModel, requests: Observable<boolean>[]): void {
        const displayElement = block.displayElement as DynamicMentionListDisplayElementModel;
        if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || block.type.key === BriefingBlockTypeEnum.ANALYSIS)
            && displayElement.backendDataSource.type === MonitoringSeriesTypeEnum.topic
        ) {
            if (this.topics.find(x => x.id === displayElement.backendDataSource.id)) {
                requests.push(
                    this.savedSearchesApi.getById(displayElement.backendDataSource.id)
                        .pipe(
                            mergeMap(savedSearchApiModel => {
                                const mentionSearchModel = new MentionSearchModel(savedSearchApiModel);
                                if (displayElement.filterMentionSearchModel) {
                                    mentionSearchModel.searchCriteria.filterQuery = displayElement.filterMentionSearchModel.searchCriteria.query;
                                } else if (displayElement.backendDataSource?.filterSearchCriteria) {
                                    // eslint-disable-next-line max-len
                                    mentionSearchModel.searchCriteria.filterQuery = displayElement.backendDataSource.filterSearchCriteria.query;
                                }

                                if (mentionSearchModel.searchCriteria.filterQuery.dateQuery) {
                                    mentionSearchModel.searchCriteria.filterQuery.dateQuery.timezone = this.userTimezonePreference;
                                }
                                return this.mentionsApi.validateFilters(mentionSearchModel.searchCriteria.getApiEntity());
                            }),
                            map(() => true),
                            catchError(error => {
                                if (error.status === 400) {
                                    let message = 'Briefings.error.invalidFilter';

                                    if (error.error.details?.length) {
                                        message = error.error.details[0].message;
                                    }

                                    if (block.errors.indexOf(message) === -1) {
                                        block.errors.push(message);
                                    }

                                    return of(false);
                                }

                                throw error;
                            })
                        )
                );
            }
        }
    }

    /**
     * Display a dialog informing the user of invalid emails contained in their briefing.
     * Provide option to remove mailing lists and/or individual recipients that contain invalid emails
     */
    invalidEmailPrompt(emails: string[]) {
        const invalidEmails = uniq(emails);

        const io: ConfirmDialogModel = {
            inputs: {
                id: 'briefing_saveBriefing_invalidEmail',
                heading: 'Briefing.dialog.saveBriefingInvalidEmail.header',
                messageHtml:
                    `<div>
                        <p> ${this.localizationService.transform('Briefing.dialog.saveBriefingInvalidEmail.message1')} </p>
                        <ul style="margin-left: 20px; font-weight: 600;">
                            ${invalidEmails.map(email => `<li>• ${email}</li>`).join('')}
                        </ul>
                        <p> ${this.localizationService.transform('Briefing.dialog.saveBriefingInvalidEmail.message2')} </p>
                    </div>`,
                showCancel: true,
                okLabel: 'Briefing.dialog.removeSave'
            },
            outputs: {
                dialogClose: async (confirm: boolean) => {
                    if (!confirm) {
                        return;
                    }

                    this.loadingSubject.next(true);

                    // remove mailingLists that contain recipients with invalid emails
                    const containInvalidEmails: Observable<boolean>[] = [];

                    if (invalidEmails.includes(this.currentBriefing.replyTo)) {
                        this.currentBriefing.replyTo = '';
                    }

                    this.currentBriefing.mailingListIds.forEach(mailingListId => {
                        const mailingList = this.mailingLists.find(x => x.id === mailingListId);

                        containInvalidEmails.push(
                            this.mailingListsService.getRecipientsForMailingList(mailingList).pipe(
                                map(recipients => recipients?.some(x => invalidEmails.includes(x.email)))
                            )
                        );
                    });

                    forkJoin(containInvalidEmails).pipe(
                        mergeMap((result: boolean[]) => {
                            this.currentBriefing.mailingListIds = this.currentBriefing.mailingListIds.filter((value, index) => !result[index]);
                            this.currentBriefing.recipientList = this.currentBriefing.recipientList.filter(email => !invalidEmails.includes(email));
                            this.setCurrentBriefing(this.currentBriefing);
                            return this.saveCurrentBriefing();
                        }),
                    ).subscribe({
                        next: () => this.loadingSubject.next(false),
                        error: () => this.loadingSubject.next(false)
                    });


                    if (containInvalidEmails.length === 0) {
                        this.currentBriefing.recipientList = this.currentBriefing.recipientList.filter(email => !invalidEmails.includes(email));
                        this.setCurrentBriefing(this.currentBriefing);
                        this.saveCurrentBriefing().subscribe({
                            next: () => this.loadingSubject.next(false),
                            error: () => this.loadingSubject.next(false)
                        });
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public sendCurrentBriefing(): Observable<null> {
        let successSummary = 'Briefing.sendBriefingSuccessHeading';
        let successMsg = 'Briefing.sendBriefingSuccessMessage';
        if (this.currentBriefing.sendAt.sendLater) {
            successSummary = 'Briefing.scheduleBriefingSuccessHeading';
            successMsg = 'Briefing.scheduleBriefingSuccessMessage';
        } else if (this.currentBriefing.sendAt.recurring) {
            successSummary = 'Briefing.recurringBriefingSuccessHeading';
            successMsg = 'Briefing.recurringBriefingSuccessMessage';
        }
        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: successSummary,
            detail: this.localizationService.transform(successMsg, { name: this.currentBriefing.name })
        } as ToastMessageOptions;

        const errorMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.error,
            summary: 'Briefing.sendBriefingErrorHeading',
            detail: this.localizationService.transform('Briefing.sendBriefingErrorMessage', {
                name: this.currentBriefing.name
            })
        } as ToastMessageOptions;

        const sendBriefingResponse = this.briefingApi.send(this.currentBriefing.id);
        sendBriefingResponse.subscribe({
            next: () => {
                this.messageService.add(successMessage);

                // Update the list to show the new status of the briefing
                this.updateBriefingListItems();
                this.briefingInitialCreate = true;
                this.setCurrentBriefingFromApi(this.currentBriefing.id);
            },
            error: (error) => {
                if (error?.status === 409 && error?.error?.details[0].code === BriefingsApiErrorEnum.alreadySent) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.sendBriefing.alreadySent.heading',
                        detail: 'Briefing.sendBriefing.alreadySent.message'
                    });
                } else {
                    this.messageService.add(errorMessage);
                }
                this.updateBriefingListItems();
            }
        });

        return sendBriefingResponse;
    }

    /**
     * Sends a briefing as a preview
     */
    public sendPreview(briefing: BriefingApiModel): Observable<null> {
        return this.briefingApi.sendPreview(briefing);
    }

    /**
     * Opens a preview of a briefing in a new window/tab
     */
    public openPreview(briefing: BriefingApiModel): Observable<string> {
        const previewRequest = this.briefingApi.getPreviewHtml(briefing);
        previewRequest.subscribe(
            (html) => {
                const previewWindow = window.open();
                if (!previewWindow || !previewWindow.document) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.previewPopupBlockedHeading',
                        detail: 'Briefing.previewPopupBlockedMessage'
                    });

                    return;
                }
                previewWindow.document.open();
                previewWindow.document.write(html);
                previewWindow.document.title = briefing.briefingSubject;
                previewWindow.document.close();
            }
        );

        return previewRequest;
    }

    /**
     * Cancels the current briefing, showing a growl message on success, nothing on failure because the component will show a popup
     */
    public cancelCurrentBriefing(): Observable<null> {
        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: 'Briefing.cancelBriefingSuccessHeading',
            detail: this.localizationService.transform('Briefing.cancelBriefingSuccessMessage', { name: this.currentBriefing.name })
        } as ToastMessageOptions;

        const cancelBriefingResponse = this.briefingApi.cancel(this.currentBriefing.id);
        cancelBriefingResponse.subscribe({
            next: () => {
                this.messageService.add(successMessage);

                // Update the list to show the new status of the briefing
                this.updateBriefingListItems();
                this.setCurrentBriefingFromApi(this.currentBriefing.id);
            },
            error: () => { }
        });

        return cancelBriefingResponse;
    }

    /**
     * Pauses the current briefing, showing a growl message on success/failure
     */
    public pauseCurrentBriefing(): Observable<null> {
        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: 'Briefing.pauseBriefingSuccessHeading',
            detail: this.localizationService.transform('Briefing.pauseBriefingSuccessMessage', { name: this.currentBriefing.name })
        } as ToastMessageOptions;

        const errorMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.error,
            summary: 'Briefing.pauseBriefingErrorHeading',
            detail: this.localizationService.transform('Briefing.pauseBriefingErrorMessage', {
                name: this.currentBriefing.name
            })
        } as ToastMessageOptions;

        // The cancel endpoint handles both cancel and pause, this isn't a typo
        const cancelBriefingResponse = this.briefingApi.cancel(this.currentBriefing.id);
        cancelBriefingResponse.subscribe({
            next: () => {
                this.messageService.add(successMessage);

                // Update the list to show the new status of the briefing
                this.updateBriefingListItems();
                this.setCurrentBriefingFromApi(this.currentBriefing.id);
            },
            error: () => {
                this.updateBriefingListItems();
                this.messageService.add(errorMessage);
            }
        });

        return cancelBriefingResponse;
    }

    /**
     * Gets the list of briefings, a simplified model for displaying in lists/dropdowns
     */
    public getBriefingListItems(): Observable<BriefingListItemModel[]> {
        if (!this.briefingListItems) {
            this.updateBriefingListItems();
        }

        return this.briefingListItemsProvider;
    }

    public getFilterListItems(): Observable<AuditableModel[]> {
        return this.getBriefingListItems();
    }

    getForceReportRefresh(): Observable<boolean> {
        return this.forceReportRefresh$;
    }
    /**
     * Fetches the briefingList from the API and pushes it to provider
     */
    public updateBriefingListItems(actionButton?: LeftSidebarListItemButton): void {
        this.briefingApi.getList().subscribe(
            listItems => {
                this.briefingListItems = listItems.map(listItem => new BriefingListItemModel(listItem));

                // the websocket calls updateBriefingListItems once the briefing goes from pending -> sent
                // and after that we would like to reget the current briefing to get the updated values &
                const briefingId = this.currentBriefing?.id;
                if (briefingId) {
                    const currentListBriefing = this.briefingListItems.find(briefing => briefing.id === briefingId);
                    if ((currentListBriefing?.status !== this.currentBriefing?.status) && (currentListBriefing?.status === BriefingStatusEnum.SENT)) {
                        this.forceReportRefresh$.next(true);
                        this.setCurrentBriefingFromApi(this.currentBriefing.id);
                    }
                }

                this.briefingListItemsProvider.next(this.briefingListItems);

                if (actionButton) {
                    actionButton.loading = false;
                }
            }
        );
    }

    /**
     * Fetches a briefing by ID from the back end, clones it and sets up the providers as if it were a new briefing
     */
    public cloneFromExistingBriefing(id: number): void {
        this.loadingSubject.next(true);
        this.makeBriefingClean();
        const isCanvas = this.briefingListItems?.find(b => b.id === id)?.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE;
        if (isCanvas) {
            this.routingService.gotoCanvas();
        } else {
            this.routingService.gotoBriefing();
        }

        this.briefingApi.getById(id).pipe(
            catchError(err => {
                this.loadingSubject.next(false);
                throw err;
            }),
            mergeMap(briefingApi => {
                return this.fetchDigestsForBriefing(briefingApi);
            })
        ).subscribe(
            async briefingApiModel => {
                this.currentBriefing = new BriefingModel(briefingApiModel).clone();

                this.setDefaultLanguage();

                this.currentBriefing.blocks.forEach(block => {
                    // if showSectionHeading is true in general styles AND
                    // & isBoundToGlobalStyles is true
                    // & mention block is not in layout block
                    // set showSectionHeading to true in the mention block
                    if (isMentionBlockTypes(block.type) &&
                        this.currentBriefing.generalStyles.mention.showSectionHeading &&
                        block.displayElement.isBoundToGlobalStyles) {
                        block.displayElement.styles.showSectionHeading = true;
                    }
                });
                this.setTemplatesOnBlocks(this.currentBriefing.blocks);

                await this.loadSearches();

                this.setCustomStyles();
                for (const block of this.getAllBlocksWithSearchCriteria()) {
                    const element = block.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
                    element.searchCriteriaId = null;
                    if (element.getSearchCriteria()) {
                        element.getSearchCriteria().id = null;
                        element.getSearchCriteria().name = null;
                    }
                }
                for (const block of BriefingsHelper.getChartBlocks(this.currentBriefing)) {
                    const displayElement = block.displayElement as BriefingChartDisplayElementModel;
                    displayElement.searches.forEach(x => {
                        delete x.filterSearchCriteriaId;
                        delete x.searchReferenceId;
                    });
                    displayElement.mentionSearches.forEach(x => {
                        delete x.id;
                        delete x.name;
                    });
                    (displayElement.chartBlock?.displayElement as ChartDisplayElementModel)?.series?.forEach(x => {
                        x.seriesUniqueUuid = uuid.v4();
                    });
                }

                // Set it to null so it always appears dirty until saved
                this.cachedCurrentBriefing = null;

                this.setCurrentBriefing(this.currentBriefing);
                this.loadCuratedMentions();
                this.loadingSubject.next(false);
            }
        );
    }

    /**
     * Based on route changes, determines what the currentBriefing should be and pushes it to the provider
     */
    private async updateBriefingFromRoute(route: MonitoringRouteModel): Promise<void> {
        switch (route.type) {
            case RouteType.briefing:
            case RouteType.canvas:
            case RouteType.briefingReport:
            case RouteType.canvasReport:
            case RouteType.recurringBriefingReport:
            case RouteType.recurringCanvasReport:
                if (route.value) {
                    if (
                        !this.briefingInitialCreate
                        && (+route.value === this.currentBriefing?.id)
                        && (isNaN(+route.subId) || +route.subId === this.briefingHistory?.sentId)
                    ) {
                        return;
                    }
                    this.briefingInitialCreate = false;

                    // Viewing a specific briefing
                    const error = await this.setCurrentBriefingFromApi(+route.value, +route.subId);

                    if (error?.status === 404) {
                        // failed to load briefing from the api, show user feedback before
                        // navigating to briefings hype
                        this.routingService.gotoBriefings({
                            queryParams: { notFound: 'true' }
                        });
                        return;
                    }
                } else if (
                    !route
                    || !route.queryParamMap
                    || route.queryParamMap.keys.indexOf('quickBriefing') === -1
                ) {
                    this.setNewBriefing(new BriefingModel());
                }
                break;

            case RouteType.briefingsHype:
            default:
                // Hype page
                this.currentBriefing = null;
                break;
        }
    }

    /**
     * Sets the current briefing to a default briefing, this is required for the case of pressing new when you're on new
     */
    public setNewBriefing(briefing: BriefingModel): void {

        if (briefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            this.routingService.gotoCanvas(undefined, true);
        } else {
            this.routingService.gotoBriefing(undefined, true);
        }

        this.currentBriefing = briefing;
        this.setDefaultLanguage();
        this.currentBriefingAllocatedMentionsCountProvider.next(0);
        this.setTemplatesOnBlocks(this.currentBriefing.blocks);
        this.cachedCurrentBriefing = null;
        this.setCustomStyles();
        this.loadCuratedMentions();
        this.setCurrentBriefing(this.currentBriefing);
        this.setDefaultReplyToAndFromName();
    }

    /**
     * Creates a quick briefing
     */
    public createQuickBriefing(quickModel: BriefingQuickModel, briefing: BriefingModel): void {
        this.routingService.gotoBriefing(null, true);

        this.currentBriefingAllocatedMentionsCountProvider.next(0);
        briefing.name = quickModel.name;

        this.currentBriefingMentions = [];
        this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
        this.setCurrentBriefing(briefing);
        this.setDefaultLanguage();
        this.setSortBy(quickModel.sortBy);

        if (
            quickModel.dataSourceModel.type === MonitoringSeriesTypeEnum.folder
            || quickModel.dataSourceModel.type === MonitoringSeriesTypeEnum.tag
            || quickModel.dataSourceModel.type === MonitoringSeriesTypeEnum.tagGroup
            || quickModel.dataSourceModel.type === MonitoringSeriesTypeEnum.sourceGroup
            || quickModel.dataSourceModel.type === MonitoringSeriesTypeEnum.aiAgent
        ) {
            this.currentBriefing.organizeBy = quickModel.organizeBy;

            this.getBatchOfCuratedMentions(
                quickModel.dataSourceModel,
                quickModel.numberOfMentions
            ).pipe(
                mergeMap(mentions => {
                    const tags$ = !!quickModel.tagIds?.length ? this.tagsService.getTagsByIds(quickModel.tagIds) : of([]);
                    return combineLatest([
                        this.setGeoAndOutletAvatarOnMentions(mentions),
                        tags$
                    ]);
                })
            ).subscribe(
                ([mentionsWithGeo, tags]) => {
                    this.setOrganizeBy(mentionsWithGeo, quickModel.organizeBy, tags);
                    this.setSortBy(quickModel.sortBy);
                    this.addMentionModelsToCuratedBlocks(mentionsWithGeo, quickModel.dataSourceModel.dataSourceName);
                }
            );
        } else {
            this.addDynamicBlock(
                quickModel.dataSourceModel,
                quickModel.numberOfMentions,
                quickModel.dataSourceModel.dataSourceName,
                quickModel.organizeBy,
                quickModel.sortBy
            );
        }

        this.setDefaultReplyToAndFromName();
        this.cachedCurrentBriefing = null;
        this.setCustomStyles();
        this.setCurrentBriefing(this.currentBriefing);
    }

    /**
     * Creates a new dynamic block
     */
    public createDynamicBlock(dataSource: MonitoringBackEndDataSourceModel, maxMentions: number, title: string): BlockModel {
        const dynamicBlock = new BlockModel(
            null,
            dynamicMentionListBlockType,
            new (dynamicMentionListBlockType.displayElementType)()
        );

        const dynamicDisplay = (dynamicBlock.displayElement as DynamicMentionListDisplayElementModel);
        if (this.currentBriefing?.generalStyles?.mention) {
            dynamicDisplay.styles = new MentionCustomizationModel(this.currentBriefing.generalStyles.mention.clone());
        } else {
            dynamicDisplay.styles = this.getDefaultStyle(BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST);
        }
        dynamicDisplay.backendDataSource = dataSource;
        dynamicDisplay.pageSize = maxMentions;
        dynamicDisplay.title = title;
        dynamicDisplay.onUpdate(); // Fixes an issue where the data source was not getting saved correctly
        dynamicBlock.type.twig = this.getDefaultTemplateByBlockType(
            BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST,
            BriefingStyleStateEnum.BRIEFING_1
        );
        dynamicDisplay.includeInsightsLink = this.currentBriefing?.includeInsightsLink;
        dynamicDisplay.blockReferenceId = uuid.v4();
        dynamicDisplay.mentionPreviewCount = 5;
        if (this.currentBriefing?.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            dynamicDisplay.displayType = BlockDisplayTypeEnum.ONLINE;
        } else {
            dynamicDisplay.displayType = BlockDisplayTypeEnum.TWIG;
        }

        return dynamicBlock;
    }

    /**
     * Adds a dynamic block to the current briefing
     */
    public addDynamicBlock(
        dataSource: MonitoringBackEndDataSourceModel,
        maxMentions: number,
        title: string,
        organizeBy: BriefingOrganizeByEnum,
        sortBy: BriefingSortByEnum
    ): void {
        if (!dataSource.sourceSearchCriteria) {
            dataSource.sourceSearchCriteria = new MentionSearchCriteriaModel();
        }

        if (!dataSource.filterSearchCriteria) {
            dataSource.filterSearchCriteria = new MentionSearchCriteriaModel();
        }

        this.setSortByToSearchCriteria(sortBy, dataSource.filterSearchCriteria);

        switch (organizeBy) {
            case BriefingOrganizeByEnum.ALL:
                const block = this.createDynamicBlock(dataSource, maxMentions, title);
                this.addLayoutDynamicBlock(block, title);
                break;
            case BriefingOrganizeByEnum.SENTIMENT:
                this.availableSentiments.forEach(sentiment => {
                    dataSource.filterSearchCriteria.query.sentiments = [sentiment];
                    const sentimentBlock = this.createDynamicBlock(
                        dataSource.clone(),
                        maxMentions,
                        title + ' - ' + this.localizationService.transform(`Sentiment.${sentiment.id}`)
                    );
                    this.addLayoutDynamicBlock(sentimentBlock, title + ' - ' + this.localizationService.transform(`Sentiment.${sentiment.id}`));
                });
                break;
            case BriefingOrganizeByEnum.MEDIA_TYPE:
                let mediaTypes = this.availableMediaTypes;
                if (dataSource.type === MonitoringSeriesTypeEnum.topic) {
                    if (dataSource.filterSearchCriteria.query.includedMediaTypes?.length) {
                        mediaTypes = dataSource.filterSearchCriteria.query.includedMediaTypes;
                    } else {
                        mediaTypes = dataSource.sourceSearchCriteria.query.includedMediaTypes;
                    }
                }

                mediaTypes.forEach(mediaType => {
                    dataSource.filterSearchCriteria.query.includedMediaTypes = [mediaType];
                    const mediaTypeBlock = this.createDynamicBlock(
                        dataSource.clone(),
                        maxMentions,
                        title + ' - ' + this.localizationService.transform(`MediaType.${mediaType.id}`)
                    );
                    this.addLayoutDynamicBlock(mediaTypeBlock, title + ' - ' + this.localizationService.transform(`MediaType.${mediaType.id}`));
                });
                break;
        }

        this.updateCurrentBriefingAllocatedMentionsCount();
        this.setCurrentBriefing(this.currentBriefing);
    }

    addLayoutDynamicBlock(block: BlockModel, title: string) {
        this.applyGeneralStylesToBlock(block);

        if (block?.displayElement?.styles?.showSectionHeading === false
            || (block?.displayElement.displayType === BlockDisplayTypeEnum.ONLINE)
        ) {
            const titleBlock = new BlockModel(null, titleBlockType, new titleBlockType.displayElementType());
            (titleBlock.displayElement as BriefingTextDisplayElementModel).text = title;
            (titleBlock.displayElement as BriefingTitleDisplayElementModel).headingLevel = BriefingHeadingLevelEnum.HEADING_3;

            if (block?.displayElement.displayType === BlockDisplayTypeEnum.ONLINE) {
                (titleBlock.displayElement as BriefingTextDisplayElementModel).displayType = BlockDisplayTypeEnum.ONLINE;
            } else {
                (titleBlock.displayElement as BriefingTextDisplayElementModel).displayType = BlockDisplayTypeEnum.TWIG;
            }

            this.applyGeneralStylesToBlock(titleBlock);

            const layoutBlock = new LayoutBlock(null,
                briefingOneColumnLayoutBlockType,
                new briefingOneColumnLayoutBlockType.displayElementType() as LayoutDisplayElement,
                [[titleBlock, block]]
            );
            this.currentBriefing.blocks.push(layoutBlock);
        } else {
            this.currentBriefing.blocks.push(block);
        }
    }

    /**
     * Set sort by and sort order to search query based on sortby
     */
    private setSortByToSearchCriteria(sortBy: BriefingSortByEnum, searchCriteria: MentionSearchCriteriaModel): void {

        switch (sortBy) {
            case BriefingSortByEnum.PUBLISHED_DATE_OLDEST:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.published;
                searchCriteria.sortOrder = SortOrderEnum.ascending;
                break;
            case BriefingSortByEnum.POTENTIAL_AUDIENCE_MOST:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.audience_reach;
                searchCriteria.sortOrder = SortOrderEnum.descending;
                break;
            case BriefingSortByEnum.POTENTIAL_AUDIENCE_LEAST:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.audience_reach;
                searchCriteria.sortOrder = SortOrderEnum.ascending;
                break;
            case BriefingSortByEnum.DOMAIN_AUTHORITY:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.domainAuthority;
                searchCriteria.sortOrder = SortOrderEnum.descending;
                break;
            case BriefingSortByEnum.ENGAGEMENT_MOST:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.engagement_total;
                searchCriteria.sortOrder = SortOrderEnum.descending;
                break;
            default:
                searchCriteria.sortField = SearchCriteriaSortFieldEnum.published;
                searchCriteria.sortOrder = SortOrderEnum.descending;
                break;
        }
    }

    private async setCurrentBriefingFromApi(
        id: number,
        subId?: number,
        briefingApiModel?: BriefingApiModel
    ): Promise<any> {
        this.currentBriefingAllocatedMentionsCountProvider.next(0);
        this.loadingSubject.next(true);

        try {
            const briefing$ = briefingApiModel
                ? of(briefingApiModel)
                : this.briefingApi.getById(id);
            const result = await lastValueFrom(
                combineLatest([
                    briefing$.pipe(
                        mergeMap(briefing => {
                            if (briefing.status === BriefingStatusApiEnum.SENT || briefing.status === BriefingStatusApiEnum.DRAFT) {
                                return this.briefingApi.getHistory(briefing.briefingId).pipe(
                                    tap(response => {
                                        if (response && response.length) {
                                            let briefingHistoryApi = response[0];
                                            response.forEach((item, index) => {
                                                // find the most recent submit date, which reflects the most recent briefing status
                                                if (index !== 0 && new Date(briefingHistoryApi.submittedDate) < new Date(item.submittedDate)) {
                                                    briefingHistoryApi = item;
                                                }
                                            });

                                            this.briefingHistory = new BriefingHistoryModel(briefingHistoryApi);
                                            if (briefing.status === BriefingStatusApiEnum.SENT) {
                                                briefing.sentDate = this.briefingHistory.date;
                                            }
                                            this.briefingHistoryProvider.next(this.briefingHistory);
                                        } else {
                                            this.briefingHistoryProvider.next(null);
                                        }
                                    }),
                                    map(() => briefing)
                                );
                            } else if (subId) {
                                return this.briefingApi.getSingleHistory(subId).pipe(
                                    tap(response => {
                                        if (response) {
                                            this.briefingHistory = new BriefingHistoryModel(response);
                                            this.briefingHistory.sentId = subId;
                                            briefing.sentDate = this.briefingHistory.date;
                                            this.briefingHistoryProvider.next(this.briefingHistory);
                                        }
                                    }),
                                    map(() => briefing)
                                );
                            } else {
                                return of(briefing);
                            }
                        }),
                        mergeMap(briefingApi => {
                            return this.fetchDigestsForBriefing(briefingApi);
                        })
                    ),
                    this.hasDefaultTemplates(),
                    this.stylesService.all$.pipe(filter(x => x !== null)),
                    this.routingService.getCurrentRoute(),
                ]).pipe(take(1))
            );

            const newBriefingApiModel = result[0];
            const route = result[3];

            this.currentBriefing = new BriefingModel(newBriefingApiModel);

            // If the API response omits tracking fields (null or undefined), restore from the
            // cached briefing which was set at save time and holds the user's explicit choices.
            if (this.cachedCurrentBriefing?.id === this.currentBriefing.id) {
                if (newBriefingApiModel.enableClickTracking == null) {
                    this.currentBriefing.clickTracking = this.cachedCurrentBriefing.clickTracking;
                }
                if (newBriefingApiModel.enableOpenTracking == null) {
                    this.currentBriefing.openTracking = this.cachedCurrentBriefing.openTracking;
                }
            }
            const isCanvas = this.currentBriefing?.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE;

            if (((route.type === RouteType.briefing) || (route.type === RouteType.canvas)) &&
                (this.currentBriefing?.sendAt?.sendNow || (this.currentBriefing?.sendAt?.sendLater)) && (
                (this.currentBriefing.status === BriefingStatusEnum.SENT) ||
                    (this.currentBriefing.status === BriefingStatusEnum.SCHEDULED)
            )) {
                this.currentBriefing = null;
                // initial load, if briefing is sent, go to report directly
                if (isCanvas) {
                    this.routingService.gotoCanvasReport(newBriefingApiModel.briefingId);
                } else {
                    this.routingService.gotoBriefingReport(newBriefingApiModel.briefingId);
                }

                return;
            }

            if ((route.type === RouteType.briefingReport || route.type === RouteType.canvasReport) &&
                (this.currentBriefing.status !== BriefingStatusEnum.SENT) &&
                (this.currentBriefing.status !== BriefingStatusEnum.SCHEDULED)
            ) {
                this.currentBriefing = null;
                // initial load, if briefing is not sent, go to briefing/canvas
                if (isCanvas) {
                    this.routingService.gotoCanvas(newBriefingApiModel.briefingId);
                } else {
                    this.routingService.gotoBriefing(newBriefingApiModel.briefingId);
                }

                return;
            }

            if (!!this.currentBriefing.sendAt.recurring && this.preferences) {
                this.currentBriefing.sendAt.recurring.setTimeFormat(this.preferences.timeFormat);
            }

            this.setDefaultLanguage();

            this.setTemplatesOnBlocks(this.currentBriefing.blocks);

            this.styles = result[2];
            this.setCustomStyles();

            this.cachedCurrentBriefing = cloneDeep(this.currentBriefing);

            await this.loadSearches();
            this.lockCuratorChildSource();
            this.loadCuratedMentions();

            // don't think it's needed anymore, since we will always allow them to send
            // await this.loadMentionCounts();

            this.loadingSubject.next(false);
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    setDefaultLanguage() {
        if (!this.preferences) {
            return;
        }

        this.currentBriefing.blocks.forEach(block => {
            this.setLanguage(block);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.setLanguage(x);
                    });
                });
            }
        });

        if (this.currentBriefing.generalStyles) {
            if (!this.currentBriefing.generalStyles.mention.locale) {
                this.currentBriefing.generalStyles.mention.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            } else {
                if (this.currentBriefing.generalStyles.mention.locale === 'en') {
                    this.currentBriefing.generalStyles.mention.locale = LanguageTagEnum.en_US;
                }
                if (this.currentBriefing.generalStyles.mention.locale === 'fr') {
                    this.currentBriefing.generalStyles.mention.locale = LanguageTagEnum.fr_FR;
                }
            }
            if (!this.currentBriefing.generalStyles.date.locale) {
                this.currentBriefing.generalStyles.date.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            } else {
                if (this.currentBriefing.generalStyles.date.locale === 'en') {
                    this.currentBriefing.generalStyles.date.locale = LanguageTagEnum.en_US;
                }
                if (this.currentBriefing.generalStyles.date.locale === 'fr') {
                    this.currentBriefing.generalStyles.date.locale = LanguageTagEnum.fr_FR;
                }
            }
        }
    }

    setLanguage(block: BlockModel) {
        if ((block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST ||
            block.type.key === BriefingBlockTypeEnum.DATE) &&
            !block.id && block.displayElement.styles
        ) {
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            } else {
                if (block.displayElement.styles.locale === 'en') {
                    block.displayElement.styles.locale = LanguageTagEnum.en_US;
                }
                if (block.displayElement.styles.locale === 'fr') {
                    block.displayElement.styles.locale = LanguageTagEnum.fr_FR;
                }
            }
        }
    }

    /**
     * iterates over the blocks and loads the styles into the block when a customStyle is used, make sure this.styles is up to date before calling
     */
    private setCustomStyles(): void {
        if (!this.styles) {
            return;
        }
        this.currentBriefing.blocks.forEach(block => {
            this.setBlockCustomStyles(block);
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.setBlockCustomStyles(x);
                    });
                });
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks?.forEach(item => this.setBlockCustomStyles(item));
            }
        });

        if (this.currentBriefing.generalStyles) {
            // Check to see if any of these generalStyles need populated by saved styles
            for (const genericStyleModel of this.currentBriefing.generalStyles.getStyles()) {
                if (typeof genericStyleModel.styleState === 'number') {
                    const savedStyle = this.styles.find(style => style.id === genericStyleModel.styleState);
                    if (!savedStyle) {
                        // This savedStyle doesn't exist anymore?
                        continue;
                    }
                    const styleWithTemplate: GenericStyleModel = savedStyle.clone().style;
                    if (savedStyle.template) {
                        styleWithTemplate.template = savedStyle.template;
                    }
                    genericStyleModel.setFromApiModel(styleWithTemplate);
                } else if (genericStyleModel.styleState === BriefingStyleStateEnum.DEFAULT) {
                    genericStyleModel.styleState = BriefingStyleStateEnum.BRIEFING_1;
                    (genericStyleModel as any).displayStyleState = BriefingStyleStateEnum.BRIEFING_1;
                }
            }
            for (const field in this.currentBriefing.generalStyles) {
                if (!this.currentBriefing.generalStyles.hasOwnProperty(field)) {
                    continue;
                }
                let defaultReference: BriefingStyleStateEnum;
                if (
                    typeof this.currentBriefing.generalStyles[field].styleState === 'number' ||
                    this.currentBriefing.generalStyles[field].styleState === BriefingStyleStateEnum.CUSTOM
                ) {
                    defaultReference = this.currentBriefing.generalStyles[field].defaultReference || BriefingStyleStateEnum.BRIEFING_1;
                } else {
                    defaultReference = this.currentBriefing.generalStyles[field].styleState || BriefingStyleStateEnum.BRIEFING_1;
                }

                // if contains bad data, set it to default
                if (!this.getDefaultStyleSet(defaultReference)) {
                    defaultReference = BriefingStyleStateEnum.BRIEFING_1;
                }

                this.currentBriefing.setDefaultStyles(
                    this.getDefaultStyleSet(defaultReference)[field],
                    this.currentBriefing.generalStyles[field]
                );
            }
            if (
                this.preferences
            ) {
                if (!this.currentBriefing.generalStyles.mention.locale) {
                    this.currentBriefing.generalStyles.mention.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
                } else {
                    if (this.currentBriefing.generalStyles.mention.locale === 'en') {
                        this.currentBriefing.generalStyles.mention.locale = LanguageTagEnum.en_US;
                    }
                    if (this.currentBriefing.generalStyles.mention.locale === 'fr') {
                        this.currentBriefing.generalStyles.mention.locale = LanguageTagEnum.fr_FR;
                    }
                }
                if (!this.currentBriefing.generalStyles.date.locale) {
                    this.currentBriefing.generalStyles.date.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
                } else {
                    if (this.currentBriefing.generalStyles.date.locale === 'en') {
                        this.currentBriefing.generalStyles.date.locale = LanguageTagEnum.en_US;
                    }
                    if (this.currentBriefing.generalStyles.date.locale === 'fr') {
                        this.currentBriefing.generalStyles.date.locale = LanguageTagEnum.fr_FR;
                    }
                }
            }

        }
    }

    private setBlockCustomStyles(block: BlockModel): void {
        // hack @TODO NEP-3392
        if (
            block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST &&
            (!block.displayElement.styles)
        ) {
            block.displayElement.styles = new MentionCustomizationModel(
                this.getDefaultStyle(block.type.key as BriefingBlockTypeEnum)
            );
        }
        // end hack
        if (block.displayElement.styles.isSavedStyle && block.displayElement.styles.isSavedStyle()) {
            const savedStyle = this.styles.find(style => style.id === block.displayElement.styles.styleState);
            if (savedStyle && block.displayElement.styles.setFromApiModel) {
                const style: GenericStyleModel = savedStyle.clone().style;
                if (savedStyle.template) {
                    style.template = savedStyle.template;
                }
                block.displayElement.styles.setFromApiModel(style);
            }
        } else if (block.displayElement.styles.styleState === BriefingStyleStateEnum.DEFAULT) {
            block.displayElement.styles.styleState = BriefingStyleStateEnum.BRIEFING_1;
            (block.displayElement.styles as any).displayStyleState = BriefingStyleStateEnum.BRIEFING_1;
        }
        if (this.getDefaultStyleSet(block.displayElement.styles.getDefaultReference())) {
            this.currentBriefing.setDefaultStyles(
                this.getDefaultStyleSet(block.displayElement.styles.getDefaultReference())[block.type.key],
                block.displayElement.styles
            );
        }
    }

    /**
     * Re-fetches the current briefing from the API
     */
    public refreshCurrentBriefing(): void {
        this.setCurrentBriefingFromApi(this.currentBriefing.id);
    }

    private getBlankBriefing(): BriefingModel {
        return new BriefingModel();
    }

    public getTemplateOne(): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();

        const defaultGeneralStyles: any = this.getDefaultStyleSet(BriefingStyleStateEnum.BRIEFING_1);
        this.setDefaultGeneralStylesWithUserPreferences(defaultGeneralStyles);
        newBriefing.setGeneralStyles(defaultGeneralStyles);

        // Add default blocks.
        const blockTypeMap: Map<BriefingBlockTypeEnum, BlockTypeModel> = new Map<BriefingBlockTypeEnum, BlockTypeModel>();
        briefingsBlockTypes.forEach(
            briefingsBlockType => {
                blockTypeMap.set(briefingsBlockType.key as BriefingBlockTypeEnum, briefingsBlockType);
            }
        );

        let blockType: BlockTypeModel;
        let block: BlockModel;


        let url = 'https://apigateway.agilitypr.com/api/v1/images/9add18eb-52d6-4049-9874-218db6cc8106';
        if (this.languageTag === LanguageTagEnum.fr_CA || this.languageTag === LanguageTagEnum.fr_FR) {
            url = 'https://public.agilitypr.media/a08095b7-9ebd-4f85-a4ff-96d0aa155eed';
        }

        // Image
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.IMAGE);
        block = new BlockModel(
            null,
            blockType,
            new BriefingImageDisplayElementModel(
                <BriefingImageDisplayElementApiModel>{
                    styles: this.getDefaultStyle(BriefingBlockTypeEnum.IMAGE, BriefingStyleStateEnum.BRIEFING_1),
                    images: [{
                        altText: this.localizationService.transform('Briefings.input.defaultOne.headingImage.altText'),
                        url
                    }]
                }
            )
        );
        newBriefing.blocks.push(block);

        // Date
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.DATE);
        block = new BlockModel(
            null,
            blockType,
            new DateDisplayElementModel(
                undefined,
                undefined,
                this.getDefaultStyle(BriefingBlockTypeEnum.DATE, BriefingStyleStateEnum.BRIEFING_1)
            )
        );
        this.localizeDateBlock(block);
        newBriefing.blocks.push(block);

        // Heading
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.TITLE);
        block = new BlockModel(
            null,
            blockType,
            new BriefingTitleDisplayElementModel(
                this.localizationService.transform('Briefings.input.defaultHeadingValue'),
                this.getDefaultStyle(BriefingBlockTypeEnum.TITLE, BriefingStyleStateEnum.BRIEFING_1)
            )
        );
        newBriefing.blocks.push(block);

        return newBriefing;
    }

    public getTemplateTwo(): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();

        const defaultGeneralStyles: any = this.getDefaultStyleSet(BriefingStyleStateEnum.BRIEFING_2);
        this.setDefaultGeneralStylesWithUserPreferences(defaultGeneralStyles);
        newBriefing.setGeneralStyles(defaultGeneralStyles);

        // Add default blocks.
        const blockTypeMap: Map<BriefingBlockTypeEnum, BlockTypeModel> = new Map<BriefingBlockTypeEnum, BlockTypeModel>();
        briefingsBlockTypes.forEach(
            briefingsBlockType => {
                blockTypeMap.set(briefingsBlockType.key as BriefingBlockTypeEnum, briefingsBlockType);
            }
        );

        let blockType: BlockTypeModel;
        let block: BlockModel;

        // Image
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.IMAGE);

        const url = 'https://apigateway.agilitypr.com/api/v1/images/ff0e61f5-f545-4176-b67a-ff788df408a2';

        block = new BlockModel(
            null,
            blockType,
            new BriefingImageDisplayElementModel(
                <BriefingImageDisplayElementApiModel>{
                    styles: this.getDefaultStyle(BriefingBlockTypeEnum.IMAGE, BriefingStyleStateEnum.BRIEFING_2),
                    images: [{ url }]
                }
            )
        );
        newBriefing.blocks.push(block);

        // Date
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.DATE);
        block = new BlockModel(
            null,
            blockType,
            new DateDisplayElementModel(
                undefined,
                undefined,
                this.getDefaultStyle(BriefingBlockTypeEnum.DATE, BriefingStyleStateEnum.BRIEFING_2)
            )
        );
        this.localizeDateBlock(block);
        newBriefing.blocks.push(block);

        // Heading
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.TITLE);
        block = new BlockModel(
            null,
            blockType,
            new BriefingTitleDisplayElementModel(
                this.localizationService.transform('Briefings.input.defaultHeadingValue'),
                this.getDefaultStyle(BriefingBlockTypeEnum.TITLE, BriefingStyleStateEnum.BRIEFING_2)
            )
        );
        newBriefing.blocks.push(block);

        return newBriefing;
    }

    public getTemplateThree(): BriefingModel {
        const newBriefing: BriefingModel = new BriefingModel();

        const defaultGeneralStyles: any = this.getDefaultStyleSet(BriefingStyleStateEnum.BRIEFING_3);
        this.setDefaultGeneralStylesWithUserPreferences(defaultGeneralStyles);
        newBriefing.setGeneralStyles(defaultGeneralStyles);

        // Add default blocks.
        const blockTypeMap: Map<BriefingBlockTypeEnum, BlockTypeModel> = new Map<BriefingBlockTypeEnum, BlockTypeModel>();
        briefingsBlockTypes.forEach(
            briefingsBlockType => {
                blockTypeMap.set(briefingsBlockType.key as BriefingBlockTypeEnum, briefingsBlockType);
            }
        );

        let blockType: BlockTypeModel;
        let block: BlockModel;

        // Date
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.DATE);
        block = new BlockModel(
            null,
            blockType,
            new DateDisplayElementModel(
                undefined,
                undefined,
                this.getDefaultStyle(BriefingBlockTypeEnum.DATE, BriefingStyleStateEnum.BRIEFING_3)
            )
        );
        this.localizeDateBlock(block);
        newBriefing.blocks.push(block);

        // Heading
        blockType = blockTypeMap.get(BriefingBlockTypeEnum.TITLE);
        block = new BlockModel(
            null,
            blockType,
            new BriefingTitleDisplayElementModel(
                this.localizationService.transform('Briefings.input.defaultHeadingValue'),
                this.getDefaultStyle(BriefingBlockTypeEnum.TITLE, BriefingStyleStateEnum.BRIEFING_3)
            )
        );
        newBriefing.blocks.push(block);

        return newBriefing;
    }

    createMediaCoverageBriefing(briefing: BriefingModel, options: OnlineBriefingTemplateConfigModel) {
        this.setNewBriefing(briefing);

        if (options.deliverySchedulingType === deliverySchedulingTypesEnum.immediate && !options.allowAi) {
            this.getBatchOfCuratedMentions(
                options.dataSource,
                this.MAX_CANVAS_BLOCK_MENTIONS
            ).pipe(
                mergeMap(mentions => {
                    return combineLatest([
                        this.setGeoAndOutletAvatarOnMentions(mentions),
                        of([])
                    ]);
                })
            ).subscribe(
                ([mentionsWithGeo, tags]) => {
                    this.addMentionModelsToCuratedBlocks(mentionsWithGeo, options.dataSource.dataSourceName);
                    this.setCoverageImage();
                    this.notifyAllBlocksChanged();
                }
            );
        }
    }

    private notifyAllBlocksChanged(): void {
        this.currentBriefing?.blocks?.forEach(block => {
            this.notifyBlockDisplayElementChanged(block);

            if (block.isLayoutBlockType()) {

                (block as LayoutBlock).blocks?.forEach(column => {
                    column?.forEach(innerBlock => {
                        this.notifyBlockDisplayElementChanged(innerBlock);
                    });
                });
            }
        });
    }

    createCompetitorBriefing(briefing: BriefingModel, options: OnlineBriefingTemplateConfigModel) {
        this.setNewBriefing(briefing);

        if (options.deliverySchedulingType === deliverySchedulingTypesEnum.immediate && !options.allowAi) {
            const mentionCalls: Observable<MentionModel[]>[] = [];
            // will leave like this, incase we want to add more sources in the future
            const sources = [options.dataSource];

            sources.forEach(source => {
                mentionCalls.push(
                    this.getBatchOfCuratedMentions(
                        source,
                        this.MAX_CANVAS_BLOCK_MENTIONS
                    ).pipe(
                        mergeMap(mentions => this.setGeoAndOutletAvatarOnMentions(mentions))
                    )
                );
            });

            combineLatest(mentionCalls).subscribe({
                next: (mentionsArrays: MentionModel[][]) => {
                    const addedMentionIds = [];
                    const curatedBlocks = this.currentBriefing.getAllCuratedBlocks();

                    mentionsArrays.forEach((mentions, index) => {
                        mentions = mentions.filter(mention => {
                            if (mention.id) {
                                return !addedMentionIds.some(id => id === mention.id);
                            } else {
                                return !addedMentionIds.some(id => id === mention.mercuryId);
                            }
                        });

                        if (mentions.length > this.MAX_CANVAS_BLOCK_MENTIONS) {
                            mentions.length = this.MAX_CANVAS_BLOCK_MENTIONS;
                        }

                        const mentionIds = [];
                        mentions.forEach(mention => {
                            if (mention.id) {
                                mentionIds.push(mention.id);
                            } else {
                                mentionIds.push(mention.mercuryId);
                            }
                        });

                        if (curatedBlocks?.[index]) {
                            (curatedBlocks[index].displayElement as CuratedMentionListDisplayElementModel).mentions = mentions;
                            (curatedBlocks[index].displayElement as CuratedMentionListDisplayElementModel).mentionIds = mentionIds;
                        }

                        addedMentionIds.push(mentionIds);
                    });

                    this.sortCuratedMentions();
                    this.setCurrentBriefingBlocks(this.currentBriefing.blocks);
                    this.setCoverageImage();


                    this.notifyAllBlocksChanged();
                    this.loadingCuratedMentions$.next(false);
                },
                error: (err) => {
                    this.loadingCuratedMentions$.next(false);
                },
            });
        }
    }


    public loadCanvasMentions(options: OnlineBriefingTemplateConfigModel): void {
        this.getBatchOfCuratedMentions(options.dataSource, this.MAX_CANVAS_BLOCK_MENTIONS).pipe(
            mergeMap(mentions => this.setGeoAndOutletAvatarOnMentions(mentions))
        ).subscribe(mentions => {
            this.addMentionModelsToCuratedBlocks(mentions, options.dataSource.dataSourceName);
            this.setCoverageImage();
        });
    }

    public getStylesByType(type: BriefingBlockTypeEnum) {
        return this.styles.filter(
            (style) => {
                return styleTypeApiEnumToBriefingBlockTypeEnum.get(style.type) === type;
            }
        );
    }

    /**
     * Finds the number of requests needed to get all of the mentions from a folder
     */
    private getNumberOfRequestsNeededForAllMentions(totalMentions: number): number {
        return Math.ceil(totalMentions / 200);
    }

    /**
     * Returns a list of mentions to display in the briefing
     *
     * @param mentionSearchCriteriaModel - search model to lookup
     *
     * @param totalMentions - number of mentions to include
     */
    private getBatchOfCuratedMentions(monitoringBackEndDataSource: MonitoringBackEndDataSourceModel, totalMentions: number): Observable<MentionModel[]> {
        if (totalMentions === 0) {
            return of([]);
        }
        this.loadingCuratedMentions$.next(true);
        const mentionSearchCriteriaModel = monitoringBackEndDataSource.combinedSearchCriteria;

        if (totalMentions > this.maxNumberOfMentionsInBriefing) {
            totalMentions = this.maxNumberOfMentionsInBriefing;
        }
        const numOfRequests = this.getNumberOfRequestsNeededForAllMentions(totalMentions);
        const requests: Observable<MentionsSearchResponseModel>[] = [];
        mentionSearchCriteriaModel.pageSize = 200;
        mentionSearchCriteriaModel.facets = [];
        mentionSearchCriteriaModel.dateFacets = [];

        for (let i = 0; i < numOfRequests; i++) {
            mentionSearchCriteriaModel.page = i + 1;
            const searchModel = mentionSearchCriteriaModel.getApiEntity();

            if (searchModel.query.publicationDate && !searchModel.query.publicationDate.timeZone) {
                searchModel.query.publicationDate.timeZone = this.userTimezonePreference;
            }
            if (searchModel.query.harvestedDate && !searchModel.query.harvestedDate.timeZone) {
                searchModel.query.harvestedDate.timeZone = this.userTimezonePreference;
            }

            const request = monitoringBackEndDataSource.isSavedSource() ?
                this.mentionsApi.searchAccount(searchModel) : this.mentionsApi.searchMentions(searchModel);
            requests.push(request);
        }

        return forkJoin(requests).pipe(
            map<MentionsSearchResponseModel[], MentionModel[]>((responses) => {
                const mentions = [];
                responses.forEach((response) => {
                    mentions.push(...response.mentions);
                });

                return mentions;
            })
        );
    }

    /**
     * Loads the curated saved searches so organization can be maintained
     */
    private loadSearches(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const blocks: BlockModel[] = this.getAllBlocksWithSearchCriteria();
            const displayElements: DisplayElementWithSearchCriteriaModel[] = blocks.map((block) => {
                return block.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
            });

            let searchIds: number[] = [];

            for (const el of displayElements) {
                if (el.searchCriteriaId && !searchIds.includes(el.searchCriteriaId)) {
                    searchIds.push(el.searchCriteriaId);

                    if (((el instanceof CuratorDisplayElement) || (el instanceof AnalysisDisplayElementModel)) && el.backendDataSource?.id) {
                        searchIds = union(searchIds, [el.backendDataSource?.id]);
                    }
                } else {
                    const search = new MentionSearchModel();
                    search.searchCriteria.query.keywordGroups = [];
                    el.setSearchCriteria(search);

                    // We need to update the cached version as well, else dirty checking will fail
                    this.updateCacheWithSearch(el.searchCriteriaId, search);
                }
            }

            const chartElements = BriefingsHelper.getChartBlocks(this.currentBriefing).map(block => block.displayElement as BriefingChartDisplayElementModel);
            chartElements?.forEach(chartElement => {
                chartElement.searches.forEach(mentionSearch => {
                    if (mentionSearch.filterSearchCriteriaId && !searchIds.includes(mentionSearch.filterSearchCriteriaId)) {
                        searchIds.push(mentionSearch.filterSearchCriteriaId);
                    }
                });
            });

            if (searchIds.length === 0) {
                this.setCurrentBriefing(this.currentBriefing);
                resolve();
                return;
            }

            this.savedSearchesApi.getSavedSearchBulk(searchIds).pipe(
                mergeMap((searches) => {
                    displayElements.forEach(el => {
                        if (!!el.searchCriteriaId) {

                            let initialSourceMentionSearchModel: MentionSearchModel;
                            let initialSourceId: number;

                            const mentionSearchModel = new MentionSearchModel(searches[el.searchCriteriaId]);
                            if ((el instanceof DynamicMentionListDisplayElementModel) || (el instanceof AnalysisDisplayElementModel)) {
                                this.processSavedSearchFromResponse(mentionSearchModel);
                            }
                            el.setSearchCriteria(mentionSearchModel);


                            if (
                                ((el instanceof CuratorDisplayElement) || (el instanceof AnalysisDisplayElementModel)) &&
                                el.backendDataSource.id && searches[el.backendDataSource.id]
                            ) {
                                initialSourceId = el.backendDataSource.id;
                                initialSourceMentionSearchModel = new MentionSearchModel(searches[initialSourceId]);
                                el.setInitialSourceSearchCriteria(initialSourceMentionSearchModel);
                            }



                            // We need to update the cached version as well, else dirty checking will fail
                            this.updateCacheWithSearch(el.searchCriteriaId, mentionSearchModel, initialSourceMentionSearchModel);

                        }
                    });

                    const loadRegions: Observable<void>[] = [];
                    chartElements.forEach(chartElement => {
                        chartElement.mentionSearches = [];
                        chartElement.searches.forEach(search => {
                            const mentionSearchModel = new MentionSearchModel(searches[search.filterSearchCriteriaId]);
                            this.processSavedSearchFromResponse(mentionSearchModel);
                            loadRegions.push(this.loadSavedSearchRegions(mentionSearchModel));
                            // on load, set filter search criteria back on series
                            this.setFiltersOnChartSeries(chartElement, search, mentionSearchModel);
                            chartElement.mentionSearches.push(mentionSearchModel);
                        });
                    });

                    // assign searches to cachedCurrentBriefing to align with currentBriefing, passing dirty checking
                    const cachedChartElements = BriefingsHelper.getChartBlocks(this.cachedCurrentBriefing).map(block =>
                        block.displayElement as BriefingChartDisplayElementModel);
                    cachedChartElements.forEach(chartElement => {
                        chartElement.mentionSearches = [];
                        chartElement.searches.forEach(search => {
                            const mentionSearchModel = new MentionSearchModel(searches[search.filterSearchCriteriaId]);
                            this.processSavedSearchFromResponse(mentionSearchModel);
                            loadRegions.push(this.loadSavedSearchRegions(mentionSearchModel));
                            // on load, set filter search criteria back on series
                            this.setFiltersOnChartSeries(chartElement, search, mentionSearchModel);
                            chartElement.mentionSearches.push(mentionSearchModel);
                        });
                    });
                    // Convert `rawIncludedRegion` to `includedRegions` for any chart search criteria that has regions filter
                    return loadRegions.length ? forkJoin(loadRegions) : of (null);
                })
            ).subscribe({
                next: () => {
                    this.setCurrentBriefing(this.currentBriefing);
                    resolve();
                },
                error: (error) => {
                    console.log(error);
                    reject();
                }
            });
        });
    }

    // checks to see if the filter search searchReferenceId matches the series seriesUniqueUuid
    // if so, set the series.mentions.filters to the filter search criteria
    setFiltersOnChartSeries(chartElement: BriefingChartDisplayElementModel, briefingSearch: BriefingSearchApi, filterSearch: MentionSearchModel) {
        if (filterSearch.searchCriteria.query.includedMediaTypes?.length === Object.keys(MediaTypeEnum).length) {
            filterSearch.searchCriteria.query.includedMediaTypes = [];
        }
        if (filterSearch.searchCriteria.query.sentiments?.length === Object.keys(SearchCriteriaQuerySentimentsEnum).length) {
            filterSearch.searchCriteria.query.sentiments = [];
        }
        (chartElement.chartBlock.displayElement as ChartDisplayElementModel).series.forEach(serie => {
            if (serie.seriesUniqueUuid === briefingSearch.searchReferenceId) {
                serie.mentions.filters = filterSearch;
            }
        });
        (chartElement.oldChartBlock.displayElement as ChartDisplayElementModel).series.forEach(serie => {
            if (serie.seriesUniqueUuid === briefingSearch.searchReferenceId) {
                serie.mentions.filters = filterSearch;
            }
        });
    }

    private processSavedSearchFromResponse(mentionSearchModel: MentionSearchModel) {
        // convert currency from USD to user currency as B.E currently only saves USD
        if (mentionSearchModel.searchCriteria?.query?.ave?.min) {
            mentionSearchModel.searchCriteria.query.ave.min =
                this.currencyConversionService.convertCurrencyWithoutISOCode(mentionSearchModel.searchCriteria?.query?.ave?.min);
        }

        if (mentionSearchModel.searchCriteria?.query?.ave?.max
            && mentionSearchModel.searchCriteria?.query?.ave?.max !== Mention.maxAudienceReach) {

            mentionSearchModel.searchCriteria.query.ave.max =
                this.currencyConversionService.convertCurrencyWithoutISOCode(mentionSearchModel.searchCriteria?.query?.ave?.max);
        }
    }

    /**
     * Loads the regions for a search object
     */
    private loadSavedSearchRegions(search: MentionSearchModel): Observable<void> {
        const query = search.searchCriteria?.query;
        const includeRegions = query?.rawIncludedRegions;

        if (MentionSearchQueryModel.isRawRegionEmpty(includeRegions)) {
            return of(void 0);
        }
        return this.journodexRegionsService.mapToComplexRegionInfo(includeRegions).pipe(
            tap(regions => {
                query.includedRegions = regions;
                query.excludedRegions = [];
                query.rawIncludedRegions = null;
                query.rawExcludedRegions = null;
            }),
            map(() => void 0)
        );
    }

    /**
     * Updates the cache with the savedSearch after it's loaded/set as new so that it doesn't always appear as dirty
     */
    private updateCacheWithSearch(
        searchCriteriaId: number,
        search: MentionSearchModel,
        initialSourceSearch?: MentionSearchModel
    ): void {
        if (!this.cachedCurrentBriefing) {
            return;
        }
        // This code could be a lot cleaner if cachedCurrentBriefing was a briefingModel
        this.cachedCurrentBriefing.blocks.forEach((block) => {
            this.updateCacheWithSearchByBlock(block, searchCriteriaId, search);
            // cacheCurrentBriefing is sometimes created from JSON.stringify, so it is not a BriefingModel but an object
            // so block.isLayoutBlockType() won't work
            if (block.type.key === LayoutBlockTypesEnum.briefingOne ||
                block.type.key === LayoutBlockTypesEnum.briefingTwo ||
                block.type.key === LayoutBlockTypesEnum.briefingThree
            ) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.updateCacheWithSearchByBlock(x, searchCriteriaId, search, initialSourceSearch);
                    });
                });
            }
            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks?.forEach(innerBlock => {
                    this.updateCacheWithSearchByBlock(innerBlock, searchCriteriaId, search, initialSourceSearch);
                });
            }
        });
    }

    private updateCacheWithSearchByBlock(
        block: BlockModel,
        searchCriteriaId: number,
        search: MentionSearchModel,
        initialSourceSearch?: MentionSearchModel
    ): void {
        if (
            block.displayElement &&
            (block.displayElement as unknown as DisplayElementWithSearchCriteriaModel).searchCriteriaId === searchCriteriaId
        ) {
            const cachedMentionSearch = search.clone(false);
            if (block.type.key === curatedMentionListBlockType.key) {
                const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                displayElement.searchCriteria = cachedMentionSearch;
                displayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;
            } else if (block.type.key === dynamicMentionListBlockType.key) {
                const displayElement = block.displayElement as DynamicMentionListDisplayElementModel;
                displayElement.filterMentionSearchModel = cachedMentionSearch;
                displayElement.backendDataSource.filterSearchCriteria = cachedMentionSearch.searchCriteria;
                displayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;
            } else if (block.type.key === briefingAnalysisBlockType.key) {
                const displayElement = block.displayElement as AnalysisDisplayElementModel;
                displayElement.filterMentionSearchModel = cachedMentionSearch;
                displayElement.backendDataSource.filterSearchCriteria = cachedMentionSearch.searchCriteria;
                displayElement.initialSourceMentionSearchModel = initialSourceSearch?.clone(false);
            } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                const displayElement = block.displayElement as CuratorDisplayElement;
                displayElement.filterMentionSearchModel = cachedMentionSearch;
                displayElement.backendDataSource.filterSearchCriteria = cachedMentionSearch.searchCriteria;
                displayElement.initialSourceMentionSearchModel = initialSourceSearch?.clone(false);
            }
        }
    }

    private setGeoAndOutletAvatarOnMentions(mentions: MentionModel[]): Observable<MentionModel[]> {
        return this.setGeographyStringOnMentions(mentions).pipe(
            switchMap(mentionsWithGeo => this.setOutletAvatarUrlOnMentions(mentionsWithGeo))
        );
    }

    // TODO: Put this in a more generic service?  Might be needed outside of briefings
    private setGeographyStringOnMentions(mentions: MentionModel[]): Observable<MentionModel[]> {
        const journodexRegionSearch: GeographyQueryModel = {
            cities: [],
            continents: [],
            counties: [],
            countries: [],
            provinces: []
        };

        const mentionRegionIdMap = new Map<MentionModel, number>();
        for (const mention of mentions) {
            let regionId: number = 0;
            if (mention.cityId) {
                journodexRegionSearch.cities.push(+mention.cityId);
                regionId = +mention.cityId;
            } else if (mention.countyId) {
                journodexRegionSearch.counties.push(+mention.countyId);
                regionId = +mention.countyId;
            } else if (mention.provinceId) {
                journodexRegionSearch.provinces.push(+mention.provinceId);
                regionId = +mention.provinceId;
            } else if (mention.countryId) {
                journodexRegionSearch.countries.push(+mention.countryId);
                regionId = +mention.countryId;
            } else if (mention.continentId) {
                journodexRegionSearch.continents.push(+mention.continentId);
                regionId = +mention.continentId;
            }
            mentionRegionIdMap.set(mention, regionId);
        }

        return this.journodexRegionsService.mapToComplexRegionInfo(journodexRegionSearch).pipe(
            map(regionInfo => {
                mentions.forEach((mention) => {
                    const regionId = mentionRegionIdMap.get(mention);
                    if (regionId) {
                        mention.geographyDisplayString = regionInfo.find((region) => {
                            return +region.id === +regionId;
                        })?.displayString;
                    }
                });
                return mentions;
            }));
    }

    private setOutletAvatarUrlOnMentions(mentions: MentionModel[]): Observable<MentionModel[]> {
        const ids = mentions
            .filter(mention => mention.outletUuid)
            .map(mention => mention.outletUuid);
        return ids && ids.length > 0
            ? this.journodexApiService.getOutletAvatar(ids).pipe(
                map(avatars => {
                    mentions.forEach(mention => {
                        const avatar = avatars.find(itm => itm.id === mention.outletUuid);
                        if (avatar) {
                            mention.outletAvatarUrl = avatar.name;
                        }
                    });
                    return mentions;
                }),
                catchError(() => of(mentions))
            )
            : of(mentions);
    }

    /**
     * Loads the Mentions for the Curated blocks when changing briefings
     * note: had to make it async because setGeoAndOutletAvatarOnMentions would return after the
     * loadCuratedMentions function and override the mentions array
     */
    async loadCuratedMentions(reorganizeMentions: boolean = false) {
        if (!this.currentBriefing) {
            return;
        }

        const curatedBlocks = [];
        this.currentBriefing.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (x.type && x.type.key === curatedMentionListBlockType.key) {
                            curatedBlocks.push(x);
                        }
                    });
                });
            } else if(block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks?.forEach(item => {
                    if (item.type?.key === curatedMentionListBlockType.key) {
                        curatedBlocks.push(item);
                    }
                });
            } else {
                if (block.type?.key === curatedMentionListBlockType.key) {
                    curatedBlocks.push(block);
                }
            }
        });
        const curatedDisplayElements: CuratedMentionListDisplayElementModel[] = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });
        const mentionIds: string[] = [];
        const mercuryIds: string[] = [];

        curatedDisplayElements.forEach((displayElement) => {
            displayElement.mentionIds?.forEach(id => {
                if (this.MERCURY_ID_PATTERN.test(id)) {
                    mercuryIds.push(id);
                } else {
                    mentionIds.push(id);
                }
            });
        });

        if (mentionIds.length === 0 && mercuryIds.length === 0) {
            this.currentBriefingMentions = [];
            this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
            this.updateCurrentBriefingAllocatedMentionsCount();
            this.loadingCuratedMentions$.next(false);
            return;
        }

        this.loadingCuratedMentions$.next(true);

        const mentions = await lastValueFrom(this.mentionsApi.getMentionsBatched(mentionIds));
        const mercuryMentions = await lastValueFrom(
            this.mentionsApi.getMercuryMentionsBatched(mercuryIds, true).pipe(catchError(() => of([])))
        );
        mercuryMentions?.forEach(mention => {
            mentions[mention.mercuryId] = mention;
        });

        for (const displayElement of curatedDisplayElements) {
            const mentionsForThisBlock = [];

            for (const id of displayElement.mentionIds) {
                if (mentions[id]) {
                    mentionsForThisBlock.push(mentions[id]);
                }
            }

            // Wait for setGeoAndOutletAvatarOnMentions to finish before continuing
            const mentionsWithGeoAndOutletAvatar = await lastValueFrom(this.setGeoAndOutletAvatarOnMentions(mentionsForThisBlock));
            this.currentBriefing?.cachedMentionsWithInsight?.forEach(cachedMention => {
                mentionsWithGeoAndOutletAvatar.forEach(mention => {
                    if (mention.mercuryId === cachedMention.mentionId && cachedMention.insight) {
                        if (!cachedMention.blockReferenceId || cachedMention.blockReferenceId === displayElement.blockReferenceId) {
                            mention.insights = [cachedMention.insight];
                        }
                    }
                });
            });
            displayElement.mentions = mentionsWithGeoAndOutletAvatar;

            this.formFieldUpdated();

            // Update the mentionIds to remove any that no longer exist
            displayElement.mentionIds = mentionsForThisBlock.map(mention => {
                if (mention.id) {
                    return mention.id;
                } else {
                    return mention.mercuryId;
                }
            });
            this.blockDisplayElementChanged$.next({
                block: curatedBlocks.find(block => block.displayElement.blockReferenceId === displayElement.blockReferenceId),
            });
        }

        // We may have removed mentionIds from curated blocks that are no longer available, we need to loop over all the
        // curatedMentionBlocks in the cachedCurrentBriefing to make sure we don't have invisible unsaved changed
        if (this.cachedCurrentBriefing) {
            for (const block of this.cachedCurrentBriefing.blocks) {
                if (!block.type || block.type.key !== BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                    continue;
                }

                const cachedCuratedDisplayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                cachedCuratedDisplayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;
                const nonNullMentionIds = [];
                for (const mentionId of cachedCuratedDisplayElement.mentionIds) {
                    if (mentions[mentionId]) {
                        nonNullMentionIds.push(mentionId);
                    }
                }
                cachedCuratedDisplayElement.mentionIds = nonNullMentionIds;
            }
        }

        // Set currentBriefingMentions to be front end models where the apiModel wasn't null
        this.currentBriefingMentions = Object.values(mentions)
            .filter(mention => !!mention)
            .map(apiMention => new Mention(apiMention));

        this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
        this.updateCurrentBriefingAllocatedMentionsCount();

        if (reorganizeMentions) {
            this.reorganizeMentions();
        }

        this.formFieldUpdated();
        this.loadingCuratedMentions$.next(false);
    }

    /**
     * Gets all of the mention blocks on the current page (not analysis blocks)
     */
    getAllMentionBlocks(): BlockModel[] {
        const mentionBlocks = [];
        this.currentBriefing?.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        // TODO: REMOVE saved source check
                        if (isMentionBlockTypes(x.type) &&
                            (x.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                            (
                                (x.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                                (x.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                                (x.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                            )
                        ) {
                            mentionBlocks.push(x);
                        }
                    });
                });
            } else if ((block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR)) {
                (block as CuratorBlock).blocks.forEach(item => {
                    if (isMentionBlockTypes(item.type) &&
                        (item.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                        (
                            (item.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                            (item.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                            (item.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                        )
                    ) {
                        mentionBlocks.push(item);
                    }
                });
            } else {
                // TODO: REMOVE saved source check
                if (isMentionBlockTypes(block.type) &&
                    (block.displayElement as DynamicMentionListDisplayElementModel)?.blockReferenceId &&
                    (
                        (block.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isSavedSource() ||
                        (block.displayElement as DynamicMentionListDisplayElementModel)?.backendDataSource?.isUnsavedSource() ||
                        (block.displayElement as CuratedMentionListDisplayElementModel)?.mentionIds?.length
                    )
                ) {
                    mentionBlocks.push(block);
                }
            }
        });

        return mentionBlocks;
    }

    /**
     * Gets all the blocks that have a searchCriteria
     */
    private getAllBlocksWithSearchCriteria(): BlockModel[] {
        const mentionBlocks = [];
        this.currentBriefing.blocks.forEach(block => {
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (isMentionBlockTypes(x.type) || (x.type.key === BriefingBlockTypeEnum.ANALYSIS)) {
                            mentionBlocks.push(x);
                        }
                    });
                });
            }
            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                mentionBlocks.push(block);
                (block as CuratorBlock).blocks.forEach( x => {
                    if ((isMentionBlockTypes(x.type) || (x.type.key === BriefingBlockTypeEnum.ANALYSIS)) && !block.displayElement.automaticallyGenerated) {
                        mentionBlocks.push(x);
                    }
                });

                (block as CuratorBlock).originalBlocks?.forEach( x => {
                    if ((isMentionBlockTypes(x.type) || (x.type.key === BriefingBlockTypeEnum.ANALYSIS)) && !block.displayElement.automaticallyGenerated) {
                        mentionBlocks.push(x);
                    }
                });
            } else {
                if (isMentionBlockTypes(block.type) || (block.type.key === BriefingBlockTypeEnum.ANALYSIS)) {
                    mentionBlocks.push(block);
                }
            }
        });

        return mentionBlocks;
    }

    updateIncludeInsightsLink() {
        const mentionBlocks = this.getAllBlocksWithSearchCriteria();
        mentionBlocks.map(block => {
            (block.displayElement as DynamicMentionListDisplayElementModel).includeInsightsLink = this.currentBriefing.includeInsightsLink;
        });
    }

    /*
    * Updates the currentBriefingAutomaticMentionCount by getting count from each automatic block
    */
    private getCurrentBriefingAutomaticMentionCount(): number {
        let automaticMentionsCount: number = 0;
        const automaticBlocks: BlockModel[] = [];
        this.currentBriefing.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (x.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) {
                            automaticBlocks.push(x);
                        }
                    });
                });
            } else {
                if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) {
                    automaticBlocks.push(block);
                }
            }
        });
        const automaticDisplayElements: DynamicMentionListDisplayElementModel[] = automaticBlocks.map((block) => {
            return block.displayElement as DynamicMentionListDisplayElementModel;
        });

        automaticDisplayElements.forEach((displayElement) => {
            automaticMentionsCount += displayElement.pageSize;
        });

        return automaticMentionsCount;
    }

    /**
     * Updates the currentBriefingMentions value by scraping the mentions off the current list of curated blocks
     */
    private updateCurrentBriefingMentions(): void {
        const curatedBlocks = [];
        this.currentBriefing.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (x.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                            curatedBlocks.push(x);
                        }
                    });
                });
            } if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks.forEach(x => {
                    if (x.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                        curatedBlocks.push(x);
                    }
                });
            } else {
                if (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                    curatedBlocks.push(block);
                }
            }
        });
        const curatedDisplayElements: CuratedMentionListDisplayElementModel[] = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });
        const mentions: MentionModel[] = [];

        curatedDisplayElements.forEach((displayElement) => {
            mentions.push(...displayElement.mentions);
        });

        this.currentBriefingMentions = mentions.map(apiMention => new Mention(apiMention));
        this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
        this.updateCurrentBriefingAllocatedMentionsCount();
    }

    /**
     * Updates the currentBriefingtotalAllocatedMentionsCount value by scraping the mentions off the current list of curated blocks
     */
    public updateCurrentBriefingAllocatedMentionsCount(): void {
        const currentBriefingAutomaticMentionCount = this.getCurrentBriefingAutomaticMentionCount();
        if (this.currentBriefingMentions.length > 0) {
            this.currentBriefingAllocatedMentionsCount = this.currentBriefingMentions.length + currentBriefingAutomaticMentionCount;
        } else {
            this.currentBriefingAllocatedMentionsCount = currentBriefingAutomaticMentionCount;
        }
        this.currentBriefingAllocatedMentionsCountProvider.next(this.currentBriefingAllocatedMentionsCount);
    }

    /**
     * Checks if maxNumberOfMentionsInBriefing limit will be exceeded
    */
    public checkMaxMentionsExceeded(): Observable<boolean> {
        let limitExceeded: boolean = false;
        if (this.currentBriefingAllocatedMentionsCount > this.maxNumberOfMentionsInBriefing) {
            limitExceeded = true;
        }
        return of(limitExceeded);
    }

    /**
     * Adds a curated block to the current list.
     */
    public addCuratedBlock(heading: string, searchCriteria?: MentionSearchQueryModel): BlockModel {
        const block = this.createCuratedBlock(heading, searchCriteria);
        this.applyGeneralStylesToBlock(block);

        if (this.preferences && block.displayElement.styles) {
            block.displayElement.styles.timezone = this.preferences.timeZone;
            if (!block.displayElement.styles.locale) {
                block.displayElement.styles.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
            }
        }

        if (block?.displayElement?.styles?.showSectionHeading === false
            || block?.displayElement.displayType === BlockDisplayTypeEnum.ONLINE
        ) {
            const titleBlock = new BlockModel(null, titleBlockType, new titleBlockType.displayElementType());
            (titleBlock.displayElement as BriefingTextDisplayElementModel).text = heading;
            (titleBlock.displayElement as BriefingTitleDisplayElementModel).headingLevel = BriefingHeadingLevelEnum.HEADING_3;

            if (block?.displayElement.displayType === BlockDisplayTypeEnum.ONLINE) {
                (titleBlock.displayElement as BriefingTextDisplayElementModel).displayType = BlockDisplayTypeEnum.ONLINE;
            } else {
                (titleBlock.displayElement as BriefingTextDisplayElementModel).displayType = BlockDisplayTypeEnum.TWIG;
            }

            this.applyGeneralStylesToBlock(titleBlock);

            const layoutBlock = new LayoutBlock(null,
                briefingOneColumnLayoutBlockType,
                new briefingOneColumnLayoutBlockType.displayElementType() as LayoutDisplayElement,
                [[titleBlock, block]]
            );
            this.currentBriefing.blocks.push(layoutBlock);
        } else {
            this.currentBriefing.blocks.push(block);
        }

        return block;
    }

    /**
     * Creates a curated block.
     */
    public createCuratedBlock(heading: string, query?: MentionSearchQueryModel): BlockModel {
        const mentionSearch: MentionSearchModel = new MentionSearchModel();
        mentionSearch.type = SavedSearchType.BRIEFING;
        // need unique names for the DB
        mentionSearch.name = `${+new Date()}${Math.random()}`;

        if (query) {
            mentionSearch.searchCriteria.query = query;
        }
        mentionSearch.searchCriteria.query.dateQuery = null;

        const displayElement = new CuratedMentionListDisplayElementModel(
            heading ? heading : this.getMentionBlockName(),
            [],
            [],
            null,
            mentionSearch,
            this.currentBriefing.generalStyles.mention.clone(),
            uuid.v4(),
            5
        );
        displayElement.isBoundToGlobalStyles = true;
        displayElement.includeInsightsLink = this.currentBriefing.includeInsightsLink;

        if (this.currentBriefing.generalStyles.mention.dateFormat) {
            displayElement.styles.dateFormat = this.generalStyles['mention'].dateFormat;
        } else {
            displayElement.styles.dateFormat = this.preferences.getDateFormatString(true);
        }
        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            displayElement.displayType = BlockDisplayTypeEnum.ONLINE;
        } else {
            displayElement.displayType = BlockDisplayTypeEnum.TWIG;
        }

        const block = new BlockModel(
            null,
            curatedMentionListBlockType,
            displayElement
        );
        block.type.twig = this.getDefaultTemplateByBlockType(
            BriefingBlockTypeEnum.CURATED_MENTION_LIST,
            BriefingStyleStateEnum.BRIEFING_1
        );

        return block;
    }

    // setting the name to the next currated block
    // "Mentions {curratedID}"
    getMentionBlockName() {
        let newIndex = -1;
        const mentionBlocks = this.getAllMentionBlocks();

        const blockNameIndices = mentionBlocks.filter(block =>
            /^mentions [0-9]+$/g.test(
                (block.displayElement as CuratedMentionListDisplayElementModel).title?.trim().toLowerCase()
            )
        ).map(block => +(block.displayElement as CuratedMentionListDisplayElementModel).title?.split(' ')[1]);

        for (let i = 1; i <= blockNameIndices.length; i++) {
            if (blockNameIndices.indexOf(i) === -1) {
                newIndex = i;
                break;
            }
        }
        newIndex = newIndex === -1 ? mentionBlocks.length + 1 : newIndex;
        return this.localizationService.transform('Reports.mentions') + ' ' + newIndex;
    }

    /**
     * Adds curated blocks for all media types contained in mentions
     */
    private addCuratedMediaTypeBlocks(mentions: MentionModel[]): void {

        const mediaTypes = new Set<MediaTypeEnum>();

        mentions?.forEach(mention => {
            mediaTypes.add(mention.mediaType);
        });

        for (const id of mediaTypes) {
            const mediaType = new MediaTypeModel(id);

            if (!mediaType.isSavedSearch && !this.availableMediaTypes.some(x => x.id === mediaType.id)) {
                continue;
            }

            const query = new MentionSearchQueryModel();
            query.includedMediaTypes = [mediaType];

            // default add all twitter sub-filters
            if (mediaType.id === MediaTypeEnum.TWITTER) {
                query.includedTweetTypes.push(
                    ...Object.values(TwitterSubTypeEnum).map(
                        type => new MediaTypeModel(MediaTypeEnum.TWITTER, type)
                    )
                );
            }

            this.addCuratedBlock(this.localizationService.transform(mediaType.name), query);
        }
    }

    /**
     * Adds curated blocks for Sentiment values
     */
    private addCuratedSentimentBlocks(): void {
        for (const sentiment of this.availableSentiments) {
            const query = new MentionSearchQueryModel();
            query.sentiments.push(sentiment);
            this.addCuratedBlock(this.localizationService.transform(sentiment.name), query);
        }
    }

    private addCuratedTagBlocks(tags: TagModel[]): void {
        for (const tag of tags) {
            const query = new MentionSearchQueryModel();
            query.tag.include.push(tag.id);
            this.addCuratedBlock(tag.name, query);
        }
    }

    private getMentionsOfInterestBlock(): BlockModel {
        return this.addCuratedBlock(
            this.getMentionBlockName()
        );
    }

    /**
     * Removes all curated blocks
     */
    private removeCuratedBlocks(): void {
        const blocks = this.currentBriefing.blocks;
        const newBlocks = [];

        for (const block of blocks) {
            if (!block.type || block.type.key !== curatedMentionListBlockType.key) {
                newBlocks.push(block);
            }
        }
        this.currentBriefing.blocks = newBlocks;
    }

    /**
     * Creates a set of Curated Mention List blocks based on the currently selected "organize by" field.
     */
    private setCuratedBlocksByOrganization(mentions: MentionModel[], tags?: TagModel[]): Promise<void> {
        if (this.currentBriefing.organizeBy === BriefingOrganizeByEnum.MANUAL) {
            // remove any search criteria when switching to manual
            const curatedBlocks = this.currentBriefing.getAllCuratedBlocks();
            curatedBlocks.forEach((block) => {
                (block.displayElement as CuratedMentionListDisplayElementModel).searchCriteria = null;
            });

            if (!curatedBlocks.length) {
                // if no mentions add an empty block
                this.getMentionsOfInterestBlock();
            }
            return;
        }

        this.removeCuratedBlocks();

        switch (this.currentBriefing.organizeBy) {
            case BriefingOrganizeByEnum.MEDIA_TYPE:
                this.addCuratedMediaTypeBlocks(mentions);
                break;
            case BriefingOrganizeByEnum.SENTIMENT:
                this.addCuratedSentimentBlocks();
                break;
            case BriefingOrganizeByEnum.TAGS:
                this.addCuratedTagBlocks(tags);
                break;
        }

        this.sortCuratedMentions();
    }

    /**
     * Filter a list of new mentions to ones that don't already exist on the current page/briefing
     */
    private filterOnlyNewMentions(mentions: MentionModel[]): MentionModel[] {
        const existingMentions = this.getExistingMentions();;
        let newMentions = [];

        if (existingMentions.length < 1) {
            return mentions;
        }

        if (existingMentions.length > 0) {
            // compare the list of existing mentions to the new ones
            for (const mention of mentions) {
                const existingMention = existingMentions.find(
                    (item) => {
                        if (item.id && mention.id) {
                            return item.id === mention.id;
                        } else if (item.mercuryId && mention.mercuryId) {
                            return item.mercuryId === mention.mercuryId;
                        } else {
                            return false;
                        }
                    }
                );
                if (existingMention) {
                    continue;
                }
                newMentions.push(mention);
            }
        } else {
            newMentions = mentions;
        }
        return newMentions;
    }

    private getExistingMentions(): MentionModel[] {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        const existingMentions = [];

        if (curatedBlocks.length < 1) {
            return [];
        }

        // Get all the mentions that exist on curated mention lists
        for (const block of curatedBlocks) {
            const blockDisplayElement = (block.displayElement as CuratedMentionListDisplayElementModel);
            existingMentions.push(...blockDisplayElement.mentions);
        }

        return existingMentions;
    }

    containDuplicateMentions(): number {
        const existingMentionIds = this.getExistingMentions().map(item => item.id);
        return existingMentionIds.length - new Set(existingMentionIds).size;
    }

    deduplicateMentions(): void {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        if (curatedBlocks.length < 1) {
            return;
        }

        const uniqueMentionIds = [...new Set(this.getExistingMentions().map(item => item.id))];

        for (const block of curatedBlocks) {
            const blockDisplayElement = (block.displayElement as CuratedMentionListDisplayElementModel);
            const newMentions = [];
            blockDisplayElement.mentionIds.forEach((id, index) => {

                if (uniqueMentionIds.includes(id)) {
                    newMentions.push(blockDisplayElement.mentions[index]);
                    uniqueMentionIds.splice(uniqueMentionIds.indexOf(id), 1);
                }
            });
            blockDisplayElement.mentions = newMentions;
            blockDisplayElement.mentionIds = newMentions.map(x => x.id);
        }
    }

    /**
     * Sorts new mentions into their appropriate curated blocks
     */
    private organizeMentions(mentions: MentionModel[]): void {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        const curatedDisplayElements = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });
        const curatedWithRules = curatedDisplayElements.filter(
            displayElement => displayElement.searchCriteria && !displayElement.searchCriteria.searchCriteria.query.isEmpty()
        );
        const unmatchedMentions = [];

        for (const mention of mentions) {
            let gotMatch = false;
            const aveUser = mention.adValueUsd ? this.currencyConversionService.convertCurrencyWithoutISOCode(mention.adValueUsd) : mention.adValueUsd;
            for (const curated of curatedWithRules) {
                if (curated.searchCriteria && curated.searchCriteria.searchCriteria.query.apiMentionMatchesSearch(mention, aveUser)) {
                    // New mentions get added to the top

                    if (!curated.mentions.some(item => {
                        if (item.id && mention.id) {
                            return item.id === mention.id;
                        } else {
                            return item.mercuryId === mention.mercuryId;
                        }
                    })) {
                        curated.mentions = [mention, ...curated.mentions];
                        curated.mentionIds = [mention.id ? mention.id : mention.mercuryId, ...curated.mentionIds];
                    }
                    gotMatch = true;
                    if (!this.currentBriefing.enableDuplicateMentions) {
                        break;
                    }
                }
            }
            if (!gotMatch) {
                unmatchedMentions.push(mention);
            }
        }

        if (unmatchedMentions.length > 0) {
            let curatedNoRules = curatedDisplayElements.find(
                displayElement => !displayElement.searchCriteria || displayElement.searchCriteria.searchCriteria.query.isEmpty()
            );

            if (!curatedNoRules) {
                const mentionsOfInterest = this.getMentionsOfInterestBlock();
                curatedNoRules = mentionsOfInterest.displayElement as CuratedMentionListDisplayElementModel;
            }

            for (const mention of unmatchedMentions) {
                if (!curatedNoRules.mentions.some(item => {
                    if (item.id && mention.id) {
                        return item.id === mention.id;
                    } else {
                        return item.mercuryId === mention.mercuryId;
                    }
                })) {
                    curatedNoRules.mentions = [mention, ...curatedNoRules.mentions];
                    curatedNoRules.mentionIds = [mention.id ? mention.id : mention.mercuryId, ...curatedNoRules.mentionIds];
                }
            }

            curatedNoRules.mentionIds = curatedNoRules.mentions.map((mention) => {
                if (mention.id) {
                    return mention.id;
                } else {
                    return mention.mercuryId;
                }
            });
        } else {
            const emptyMentionsOfInterestBlock = curatedBlocks.find(block => {
                const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;
                return displayElement.title === this.getMentionsOfInterestTitle() && displayElement.mentions.length === 0;
            });
            if (emptyMentionsOfInterestBlock) {
                this.removeBlock(emptyMentionsOfInterestBlock);
            }
        }
    }

    public refreshAndReorganizeMentions(): void {
        this.loadCuratedMentions(true);
    }

    public refreshCuratedMentions(): void {
        this.loadCuratedMentions(false);
    }

    public reorganizeMentions(): void {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        const curatedDisplayElements = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });

        // Remove all mentions from curated blocks
        curatedDisplayElements.forEach((curated) => {
            curated.mentionIds = [];
            curated.mentions = [];
        });

        this.organizeMentions(
            this.currentBriefingMentions.map(mention => mention.convertToApiMention())
        );
    }

    public clearMentions(): void {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        const curatedDisplayElements = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });

        // Remove all mentions from curated blocks
        curatedDisplayElements.forEach((curated) => {
            curated.mentionIds = [];
            curated.mentions = [];
        });

        curatedBlocks.forEach((curatedBlock) => {
            this.blockDisplayElementChanged$.next({ block: curatedBlock});
        });

        this.currentBriefingMentions = [];
        this.currentBriefingMentionsProvider.next(this.currentBriefingMentions);
        this.updateCurrentBriefingAllocatedMentionsCount();
    }

    /**
     * Sorts the mentions that already exist in curated blocks
     */
    private sortCuratedMentions(): void {
        const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
        const displayElements: CuratedMentionListDisplayElementModel[] = curatedBlocks.map((block) => {
            return block.displayElement as CuratedMentionListDisplayElementModel;
        });

        for (const element of displayElements) {
            element.sortMentions();
            // Notify the blocks of the changes
            this.blockDisplayElementChanged$.next({
                block: curatedBlocks.find(block => block.displayElement === element),
            });
        }
    }

    /**
     * Getter for the briefing history model
     */
    public getBriefingHistory(): Observable<BriefingHistoryModel> {
        return this.briefingHistoryProvider;
    }

    /**
     * Sets the organize by value and organizes the curated blocks
     */
    private setOrganizeBy(
        mentions: MentionModel[],
        organizeBy: BriefingOrganizeByEnum,
        tags?: TagModel[]
    ): void {
        this.currentBriefing.organizeBy = organizeBy;
        this.setCuratedBlocksByOrganization(mentions, tags);
        this.setCurrentBriefing(this.currentBriefing);
    }

    /**
     * Sets the sort by value and sorts any curated mention lists
     */
    public setSortBy(value: BriefingSortByEnum): void {
        const curatedBlocks = this.currentBriefing.getAllCuratedBlocks();
        curatedBlocks.forEach((block) => {
            (block.displayElement as CuratedMentionListDisplayElementModel).sortBy = value;
        });
        this.sortCuratedMentions();
        this.setCurrentBriefing(this.currentBriefing);
    }

    /**
     * Adds a set of MentionModels to the curated blocks
     */
    public addMentionModelsToCuratedBlocks(mentionModels: MentionModel[], folderName?: string): void {
        let newMentions = mentionModels;
        if (!this.currentBriefing.enableDuplicateMentions) {
            newMentions = this.filterOnlyNewMentions(mentionModels);
        }
        this.organizeMentions(newMentions);
        this.sortCuratedMentions();
        this.setCurrentBriefingBlocks(this.currentBriefing.blocks);

        if (folderName && this.currentBriefing.organizeBy === BriefingOrganizeByEnum.MANUAL) {
            const curatedBlocks: BlockModel[] = this.currentBriefing.getAllCuratedBlocks();
            if (curatedBlocks.length > 0) {
                (curatedBlocks[0].displayElement as CuratedMentionListDisplayElementModel).title = folderName;
            }
        }
        this.loadingCuratedMentions$.next(false);
    }

    /**
     * Adds a set of mentions to the curated blocks
     */
    addMentionsToCuratedBlocks(mentions: Mention[]) {
        // since the twig template will also be rendered in the backend, the curated blocks must use the api model to render
        const mentionApiModels = mentions.map((mention) => mention.convertToApiMention());

        this.setGeoAndOutletAvatarOnMentions(mentionApiModels).subscribe(
            mentionsWithGeoAndOutletAvatar => this.addMentionModelsToCuratedBlocks(mentionsWithGeoAndOutletAvatar)
        );
    }

    /**
     * Adds a set of mentions to the specified curated block
     */
    public addMentionsToBlock(block: BlockModel, mentions: Mention[]): void {
        // since the twig template will also be rendered in the backend, the curated blocks must use the api model to render
        let newMentions = mentions.map((mention) => {
            return mention.convertToApiMention();
        });

        if (!this.currentBriefing.enableDuplicateMentions) {
            newMentions = this.filterOnlyNewMentions(newMentions);
        }

        const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;

        let duplicatesDetected = false;
        newMentions.forEach(mention => {
            if (!displayElement.mentions.some(x =>
                (x.id && mention.id && x.id === mention.id)
                || (x.mercuryId && mention.mercuryId && x.mercuryId === mention.mercuryId))
            ) {
                displayElement.mentions.push(mention);
            } else {
                duplicatesDetected = true;
            }
        });

        if (duplicatesDetected) {
            const io = {
                inputs: {
                    heading: 'Briefings.duplicateMentionsDetected',
                    alertMessage: this.localizationService.transform('Briefings.duplicateMentionsDetected.summary', { section: displayElement.title }),
                    alertType: messageSeverityEnum.info,
                    showCancel: false
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        }

        displayElement.sortMentions();

        this.blockDisplayElementChanged$.next({
            block: block,
        });
        this.setCurrentBriefingBlocks(this.currentBriefing.blocks);
    }

    /**
     * Determines if there are changes made that aren't saved
     */
    public canLeaveCurrentBriefing(): boolean {
        if (
            !this.currentBriefing
            || this.currentBriefing.status === BriefingStatusEnum.SENT
            || this.cachedCurrentBriefing?.blocks.length < 1
            || this.currentRoute.type === RouteType.briefingReport
            || this.currentRoute.type === RouteType.recurringBriefingReport
            || this.currentRoute.type === RouteType.recurringCanvasReport
            || this.currentRoute.type === RouteType.canvasReport
        ) {
            // Null case for hype page, sent briefings can't (shouldn't) have save-able changes, empty briefing
            this.canLeaveBypass = false;
            return true;
        }

        if (this.canLeaveBypass) {
            this.canLeaveBypass = false;
            return true;
        }

        return this.currentBriefing.equals(this.cachedCurrentBriefing);
    }

    /**
     * This function is ONLY for the new briefing dialog, and should not be used in any other context
     *
     * This sets the cached copy to be the same as the current copy so that the authguard won't trigger a dialog
     */
    public makeBriefingClean(): void {
        this.cachedCurrentBriefing = cloneDeep(this.currentBriefing);
    }

    /**
     * Used by the de-activate guard and new briefing modal to determine if it should show the dialog
     */
    public showCanLeaveDialog(): Observable<boolean> {
        if (this.canLeaveCurrentBriefing()) {
            return of(true);
        }

        return new Observable(
            (observer: Observer<boolean>) => {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'DeactivateGuard.heading',
                        messageHtml: 'DeactivateGuard.message',
                        okLabel: 'continue',
                        id: 'BriefingsContinue'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            }
        );
    }

    /**
     * Checks if the provided string matches the name of a briefing
     *
     * @param name - name to check
     *
     * @returns boolean
     */
    public isBriefingNameDuplicate(name: string, id?: number) {
        if (!this.briefingListItems || !name) {
            return false;
        }
        name = name.trim();
        const duplicateBriefing = this.briefingListItems.find(listItem => {
            if (id) {
                return listItem.name.toLocaleLowerCase() === name.toLocaleLowerCase() && listItem.id !== id;
            }
            return listItem.name.toLocaleLowerCase() === name.toLocaleLowerCase();
        });
        if (duplicateBriefing) {
            return true;
        }
        return false;
    }

    /// TODO: Investigate whether this can be added to a class and extended instead of repeated in alerts and briefings

    /**
     * Sets up the default 'reply to' email address and 'from name' value based on the logged in user.
    */
    private setDefaultReplyToAndFromName(): void {
        if (!this.user || !this.currentBriefing) {
            return;
        }

        // Set the default 'reply to' if the briefing doesn't already have a value for it.
        if (this.user.email && !this.currentBriefing.replyTo) {
            this.currentBriefing.replyTo = this.user.email;

            // Update the cached briefing.
            if (this.cachedCurrentBriefing) {
                this.cachedCurrentBriefing.replyTo = this.user.email;
            }
        }

        // Set the default 'from name' if the briefing doesn't already have a value for it.
        const userDisplayName: string = this.user.getDisplayName();
        if (userDisplayName && !this.currentBriefing.fromName) {
            this.currentBriefing.fromName = userDisplayName;

            // Update the cached briefing.
            if (this.cachedCurrentBriefing) {
                this.cachedCurrentBriefing.fromName = userDisplayName;
            }
        }
    }

    /**
     * Sets the default twig template on each block
     */
    setTemplatesOnBlocks(blocks: BlockModel[]): void {
        blocks?.forEach(block => {
            if (block?.displayElement?.styles?.template?.template) {
                // Expert template case
                block.type.twig = block.displayElement.styles.template.template;
            } else {
                // Default template case
                let defaultReference = block.displayElement.styles.getDefaultReference();
                if (block.displayElement.styles.styleState === BriefingStyleStateEnum.CANVAS_1) {
                    defaultReference = BriefingStyleStateEnum.BRIEFING_1;
                }
                block.type.twig = this.getDefaultTemplateByBlockType(
                    block.type.key,
                    defaultReference
                );
            }
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks?.forEach(column => {
                    this.setTemplatesOnBlocks(column);
                });
            }

            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                this.setTemplatesOnBlocks((block as CuratorBlock).blocks);
            }
        });
    }

    getDefaultStyleSet(defaultReference: StyleStateEnum | string): any {
        const defaultStyleSet = this.defaultStyles[defaultReference];
        return defaultStyleSet;
    }

    getDefaultStyle(blockTypeEnum: BriefingBlockTypeEnum, defaultReference?: BriefingStyleStateEnum) {
        if (defaultReference === BriefingStyleStateEnum.DEFAULT || !defaultReference) {
            defaultReference = BriefingStyleStateEnum.BRIEFING_1;
        }

        const generalStylesProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(blockTypeEnum);
        const defaultValue = BriefingModel.defaultBriefingStyles[defaultReference][generalStylesProperty].clone();
        defaultValue.displayStyleState = defaultValue.styleState;

        // when switching from custom back to default, we need to set the locale
        if ((blockTypeEnum === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST ||
            blockTypeEnum === BriefingBlockTypeEnum.CURATED_MENTION_LIST ||
            blockTypeEnum === BriefingBlockTypeEnum.DATE) && !defaultValue.locale && this.preferences
        ) {
            defaultValue.locale = this.preferences.uiLanguage.languageTag as LanguageTagEnum;
        }

        if (this.preferences && blockTypeEnum === BriefingBlockTypeEnum.DATE) {
            const dateFormatModel = dateFormats.find(f => f.id === this.preferences.dateFormatId);
            if (dateFormatModel) {
                defaultValue.dateFormat = dateFormatModel.twigFormat;
            }
            const timeFormatModel = timeFormats.find(f => f.id === this.preferences.timeFormat);
            if (timeFormatModel) {
                defaultValue.timeFormat = timeFormatModel.momentFormat;
            }
            defaultValue.timezone = this.userTimezonePreference;
        }

        return defaultValue;
    }

    blockEditingStateChanged(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        if (blockModel.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            blockModel.canMove = true;
        } else {
            blockModel.canMove = !isEditing;
        }
        this.blockDisplayElementChanged$.next({ block: blockModel });
        this.updateMoveability();
        this.updateDraggability(blockModel, isEditing, blockPosition);
    }

    protected updateMoveability(): void {
        if (this.blocks.length === 0) {
            return;
        }
        for (let i = 0, l = this.blocks.length; i < l; i++) {
            this.blocks[i].canMoveUp = true;
            this.blocks[i].canMoveUpIntoLayout = false;
            this.blocks[i].canMoveUpIntoCurator = false;
            this.blocks[i].canMoveDown = true;
            this.blocks[i].canMoveDownIntoLayout = false;
            this.blocks[i].canMoveDownIntoCurator = false;
            this.blocks[i].canMoveLeft = false;
            this.blocks[i].canMoveRight = false;
            if (this.blocks[i].isLayoutBlockType()) {
                const layoutBlock = this.blocks[i] as LayoutBlock;
                this.updateLayoutBlockMoveability(layoutBlock);
            } else if(this.blocks[i].type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                const curatorBlock = this.blocks[i] as CuratorBlock;
                this.updateCoverageCuratorBlockMoveability(curatorBlock);
            } else {
                if (isMentionBlockTypes(this.blocks[i].type)) {
                    if (this.blocks[i - 1]?.type.key === LayoutBlockTypesEnum.briefingOne
                    ) {
                        this.blocks[i].canMoveUpIntoLayout = true;
                    }

                    if (this.blocks[i + 1]?.type.key === LayoutBlockTypesEnum.briefingOne
                    ) {
                        this.blocks[i].canMoveDownIntoLayout = true;
                    }

                    if (this.blocks[i - 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                        this.blocks[i].canMoveUpIntoCurator = true;
                    }

                    if (this.blocks[i + 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                        this.blocks[i].canMoveDownIntoCurator = true;
                    }
                } else {
                    if (this.blocks[i - 1]?.isLayoutBlockType()
                    ) {
                        this.blocks[i].canMoveUpIntoLayout = true;
                    }

                    if (this.blocks[i + 1]?.isLayoutBlockType()) {
                        this.blocks[i].canMoveDownIntoLayout = true;
                    }

                    if (this.blocks[i - 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR
                        && this.blocks[i].type.key !== BriefingBlockTypeEnum.CHART
                    ) {
                        this.blocks[i].canMoveUpIntoCurator = true;
                    }

                    if (this.blocks[i + 1]?.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR
                        && this.blocks[i].type.key !== BriefingBlockTypeEnum.CHART
                    ) {
                        this.blocks[i].canMoveDownIntoCurator = true;
                    }

                }
            }
        }
        this.blocks[0].canMoveUp = false;
        this.blocks[this.blocks.length - 1].canMoveDown = false;
    }

    private updateLayoutBlockMoveability(layoutBlock: LayoutBlock): void {
        const columnLength = layoutBlock.blocks.length;
        layoutBlock.blocks.forEach((column, columnIndex) => {
            const blockLength = column.length;
            column.forEach((block, blockIndex) => {
                block.canMoveUp = true;
                block.canMoveDown = true;
                block.canMoveLeft = true;
                block.canMoveRight = true;
                if (blockIndex === 0) {
                    block.canMoveUp = false;
                }
                if (blockIndex === blockLength - 1) {
                    block.canMoveDown = false;
                }
                if (columnIndex === 0) {
                    block.canMoveLeft = false;
                }
                if (columnIndex === columnLength - 1) {
                    block.canMoveRight = false;
                }
            });
        });
    }

    private updateCoverageCuratorBlockMoveability(curatorBlock: CuratorBlock): void {
        const blockLength = curatorBlock.blocks.length;
        curatorBlock.blocks.forEach((block, blockIndex) => {
            block.canMoveUp = true;
            block.canMoveDown = true;
            if (blockIndex === 0) {
                block.canMoveUp = false;
            }
            if (blockIndex === blockLength - 1) {
                block.canMoveDown = false;
            }
        });
    }

    private updateDraggability(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        blockModel.draggable = !isEditing;
        const currentBlock = this.blocks[parseInt(blockPosition, 10)];
        if (currentBlock && currentBlock.isLayoutBlockType()) {
            currentBlock.draggable = !isEditing;
        }
    }

    confirmCopyBriefing(briefingToCopy: BriefingListItemModel) {

        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Briefings.copyBriefing',
                initialValue: briefingToCopy.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: briefingToCopy.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name: string) => {
                    if (this.isBriefingNameDuplicate(name)) {
                        return [
                            'Briefing.copyBriefingDuplicateNameErrorMessage'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    const keyMapReferenceId = {};
                    this.briefingApi.getById(briefingToCopy.id).pipe(
                        mergeMap(briefing => {
                            const briefingModel = new BriefingModel(briefing);

                            const blockCount = this.countBlocks(briefingModel.blocks).totalCount;
                            if (blockCount > this.briefingTotalBlockLimit) {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'Briefing.totalBlocks.error.heading',
                                    detail: this.localizationService.transform('Briefing.totalBlocks.limitExceeded.tooltip', {
                                        number: blockCount
                                    })
                                });
                                return EMPTY;
                            }

                            const _briefing = briefingModel.toApiModel();

                            const mentionBlocks = [];
                            _briefing.blocks.forEach(block => {
                                if (block.columns) {
                                    block.columns.forEach(column => {
                                        column.blocks.forEach(x => {
                                            if (
                                                x.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                                x.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                                x.type === BriefingBlockTypeApiEnum.ANALYSIS ||
                                                x.type === BriefingBlockTypeApiEnum.CHART
                                            ) {
                                                if ((x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId) {
                                                    const newRefId = uuid.v4();
                                                    keyMapReferenceId[(x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId] = newRefId;
                                                    (x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId = newRefId;
                                                }
                                                mentionBlocks.push(x);
                                            }
                                        });
                                    });
                                } else if (block.type === BriefingBlockTypeApiEnum.COVERAGE_CURATOR) {
                                    mentionBlocks.push(block);
                                    (block as BriefingCoverageCuratorDisplayElementApiModel).blocks.forEach(x => {
                                        if (
                                            x.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                            x.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                            x.type === BriefingBlockTypeApiEnum.ANALYSIS
                                        ) {
                                            if ((x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId) {
                                                const newRefId = uuid.v4();
                                                keyMapReferenceId[(x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId] = newRefId;
                                                (x as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId = newRefId;
                                            }


                                            mentionBlocks.push(x);
                                        }
                                    });
                                } else {
                                    if (
                                        block.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                        block.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                        block.type === BriefingBlockTypeApiEnum.ANALYSIS ||
                                        block.type === BriefingBlockTypeApiEnum.CHART
                                    ) {
                                        if ((block as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId) {
                                            const newRefId = uuid.v4();
                                            keyMapReferenceId[(block as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId] = newRefId;
                                            (block as BriefingCuratedMentionListDisplayElementApiModel).blockReferenceId = newRefId;
                                        }
                                        mentionBlocks.push(block);
                                    }
                                }
                            });

                            if (mentionBlocks.length === 0) {
                                return of({
                                    _briefing: _briefing,
                                    savedSearches: []
                                });
                            }

                            const uniqueSearchIds =
                                Array.from(
                                    new Set(
                                        mentionBlocks.map(mentionBlock => {
                                            if (mentionBlock.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST) {
                                                return (mentionBlock as BriefingCuratedMentionListDisplayElementApiModel).searchCriteriaId;
                                            } else {
                                                return (mentionBlock as BriefingAutomaticMentionListDisplayElementApiModel).filterSearchCriteriaId;
                                            }
                                        })
                                    )
                                ).filter(x => !!x);

                            mentionBlocks.map(mentionBlock => {
                                if (mentionBlock.type === BriefingBlockTypeApiEnum.CHART) {
                                    mentionBlock.searches.forEach(search => {
                                        if (search.filterSearchCriteriaId) {
                                            uniqueSearchIds.push(search.filterSearchCriteriaId);
                                        }
                                    });
                                }
                            });

                            // Search for all the saved searches associated with this briefing
                            return this.savedSearchesApi.getSavedSearchBulk(uniqueSearchIds).pipe(
                                map(savedSearches => {
                                    return {
                                        _briefing: _briefing,
                                        savedSearches: savedSearches
                                    };
                                })
                            );
                        }),
                        mergeMap(combinedResponse => {
                            const _briefing = combinedResponse._briefing;
                            _briefing.briefingName = name;
                            _briefing.status = BriefingStatusApiEnum.DRAFT;
                            _briefing.displayGroupId = group?.id;
                            delete _briefing.briefingId;

                            const mentionBlocks = [];
                            _briefing.blocks.forEach(block => {
                                if (block.columns) {
                                    block.columns.forEach(column => {
                                        column.blocks.forEach(x => {
                                            if (
                                                x.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                                x.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                                x.type === BriefingBlockTypeApiEnum.ANALYSIS ||
                                                x.type === BriefingBlockTypeApiEnum.CHART
                                            ) {
                                                if ((x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId &&
                                                    keyMapReferenceId[(x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId]
                                                ) {
                                                    (x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId =
                                                        keyMapReferenceId[(x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId];
                                                }
                                                mentionBlocks.push(x);
                                            }
                                        });
                                    });
                                } else if (block.type === BriefingBlockTypeApiEnum.COVERAGE_CURATOR) {
                                    mentionBlocks.push(block);
                                    (block as BriefingCoverageCuratorDisplayElementApiModel).blocks.forEach(x => {
                                        if (
                                            x.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                            x.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                            x.type === BriefingBlockTypeApiEnum.ANALYSIS
                                        ) {
                                            if ((x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId &&
                                                keyMapReferenceId[(x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId]
                                            ) {
                                                (x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId =
                                                    keyMapReferenceId[(x as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId];
                                            }
                                            mentionBlocks.push(x);
                                        }
                                    });
                                } else {
                                    if (
                                        block.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST ||
                                        block.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST ||
                                        block.type === BriefingBlockTypeApiEnum.ANALYSIS ||
                                        block.type === BriefingBlockTypeApiEnum.CHART
                                    ) {
                                        if ((block as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId &&
                                            keyMapReferenceId[(block as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId]
                                        ) {
                                            (block as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId =
                                                keyMapReferenceId[(block as BriefingAnalysisDisplayElementApiModel).sourceBlockReferenceId];
                                        }
                                        mentionBlocks.push(block);
                                    }
                                }
                            });

                            if (mentionBlocks.length === 0) {
                                return of({
                                    _briefing: _briefing,
                                    savedSearches: combinedResponse.savedSearches,
                                    clonedSavedSearches: []
                                });
                            }

                            const savedMentionSearchCreateCalls: Observable<number>[] = mentionBlocks.map(
                                mentionBlock => {
                                    let searchCriteriaToBeCloned;
                                    if (mentionBlock.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST) {
                                        searchCriteriaToBeCloned = combinedResponse.savedSearches[(mentionBlock as any).searchCriteriaId];
                                    } else {
                                        searchCriteriaToBeCloned = combinedResponse.savedSearches[(mentionBlock as any).filterSearchCriteriaId];
                                    }
                                    if (searchCriteriaToBeCloned) {
                                        const searchCriteriaToBeClonedId = searchCriteriaToBeCloned?.id;
                                        searchCriteriaToBeCloned.name = `${+new Date()}${Math.random()}`;
                                        delete searchCriteriaToBeCloned.id;
                                        return this.savedSearchesApi.create(searchCriteriaToBeCloned).pipe(
                                            tap(
                                                searchCriteriaCloneId => {
                                                    // eslint-disable-next-line max-len
                                                    // When the response to create comes back, iterate through the blocks and update the old ids with the new ones
                                                    for (const briefingMentionBlock of mentionBlocks) {
                                                        if (
                                                            briefingMentionBlock.type === BriefingBlockTypeApiEnum.CURATED_MENTION_LIST &&
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingCuratedMentionListDisplayElementApiModel).searchCriteriaId === searchCriteriaToBeClonedId
                                                        ) {
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingCuratedMentionListDisplayElementApiModel).searchCriteriaId = searchCriteriaCloneId;
                                                        } else if (
                                                            briefingMentionBlock.type === BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST &&
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingAutomaticMentionListDisplayElementApiModel).filterSearchCriteriaId === searchCriteriaToBeClonedId
                                                        ) {
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingAutomaticMentionListDisplayElementApiModel).filterSearchCriteriaId = searchCriteriaCloneId;
                                                        } else if (
                                                            briefingMentionBlock.type === BriefingBlockTypeApiEnum.ANALYSIS &&
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingAnalysisDisplayElementApiModel).filterSearchCriteriaId === searchCriteriaToBeClonedId
                                                        ) {
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingAnalysisDisplayElementApiModel).filterSearchCriteriaId = searchCriteriaCloneId;
                                                        } else if (
                                                            briefingMentionBlock.type === BriefingBlockTypeApiEnum.COVERAGE_CURATOR &&
                                                            (briefingMentionBlock as BriefingCoverageCuratorDisplayElementApiModel).searches?.length &&
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingCoverageCuratorDisplayElementApiModel).searches[0].searchCriteriaId === searchCriteriaToBeClonedId
                                                        ) {
                                                            // eslint-disable-next-line max-len
                                                            (briefingMentionBlock as BriefingCoverageCuratorDisplayElementApiModel).searches[0].searchCriteriaId = searchCriteriaCloneId;
                                                        }
                                                    }
                                                }
                                            )
                                        );
                                    } else {
                                        // when analysis block is set to all curated for brief of cov by section
                                        return null;
                                    }
                                }
                            ).filter(x => !!x);

                            const savedChartSearchCreateCalls: Observable<number>[] = [];
                            mentionBlocks.forEach(mentionBlock => {
                                if (mentionBlock.type !== BriefingBlockTypeApiEnum.CHART) {
                                    return;
                                }
                                (mentionBlock as BriefingChartDisplayElementApiModel).searches.forEach(search => {
                                    const searchCriteriaToBeCloned = combinedResponse.savedSearches[(search.filterSearchCriteriaId)];
                                    if (!searchCriteriaToBeCloned) {
                                        // when analysis block is set to all curated for brief of cov by section
                                        return;
                                    }
                                    searchCriteriaToBeCloned.name = `${+new Date()}${Math.random()}`;
                                    delete searchCriteriaToBeCloned.id;
                                    savedChartSearchCreateCalls.push(
                                        this.savedSearchesApi.create(searchCriteriaToBeCloned).pipe(
                                            tap(
                                                searchCriteriaCloneId => {
                                                    search.filterSearchCriteriaId = searchCriteriaCloneId;
                                                    const uid = uuid.v4();
                                                    const oldUid = search.searchReferenceId;
                                                    search.searchReferenceId = uid;
                                                    (mentionBlock as BriefingChartDisplayElementApiModel).sources.forEach(source => {
                                                        if (source.searchReferenceId === oldUid) {
                                                            source.searchReferenceId = uid;
                                                        }
                                                    });
                                                }
                                            )
                                        )
                                    );
                                });
                            });

                            return forkJoin([...savedMentionSearchCreateCalls, ...savedChartSearchCreateCalls]).pipe(
                                map(clonedSavedSearches => {
                                    return {
                                        _briefing: _briefing,
                                        savedSearches: combinedResponse.savedSearches,
                                        clonedSavedSearches: clonedSavedSearches
                                    };
                                })
                            );
                        }),
                        mergeMap(combinedResponse => {
                            return this.briefingApi.createFull(combinedResponse._briefing);
                        })
                    ).subscribe({
                        next: (x) => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: 'Briefings.copyBriefing',
                                detail: 'Discover.confirmDialog.copy.success'
                            });
                            const isCanvas = briefingToCopy.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE;
                            if (isCanvas) {
                                this.routingService.gotoCanvas(x.briefingId);
                            } else {
                                this.routingService.gotoBriefing(x.briefingId);
                            }
                            // AD-15472 - REMOVE AFTER WE FIX WEBSOCKETS
                            this.updateBriefingListItems();
                        },
                        error: () => {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefings.copyBriefing',
                                detail: 'Discover.confirmDialog.copy.error'
                            });
                        }
                    });

                    return of(null);
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    showSendConfirmationOnLoad(showSendConfirmationOnLoad: boolean): void {
        this.showSendConfirmationOnLoad$.next(showSendConfirmationOnLoad);
    }

    /**
     * For same email with different capitalizations, we only keep the first one.
     */
    deduplicateRecipients(): void {
        const res: string[] = [];
        this.currentBriefing?.recipientList?.forEach(recipient => {
            if (!res.some(x => x.toLocaleLowerCase() === recipient.toLocaleLowerCase())) {
                res.push(recipient);
            }
        });

        this.currentBriefing.recipientList = res;
    }

    publishDraft(id: number, showMessage: boolean = true): Observable<BriefingSendHistoryApiModel> {
        return this.briefingApi.publishDraft(id).pipe(
            tap(response => {
                if (response) {
                    this.briefingHistory = new BriefingHistoryModel(response);
                    this.briefingHistoryProvider.next(this.briefingHistory);
                }

                if (showMessage) {
                    this.messageService.add(
                        {
                            severity: messageSeverityEnum.success,
                            summary: 'Briefings.reservedUrl.publishedDrafts.growlMessage.heading',
                            detail: 'Briefings.reservedUrl.publishedDrafts.growlMessage.message'
                        }
                    );
                }
            }),
            catchError(error => {
                if (error?.status === 409 && error?.error?.details[0].code === BriefingsApiErrorEnum.alreadySent) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.publishDraft.error.heading',
                        detail: 'Briefing.publishDraft.alreadySent.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefing.publishDraft.error.heading',
                        detail: 'Briefing.publishDraft.error.message'
                    });
                }
                throw error;
            })
        );
    }


    /**
     * Sets the attributes for the Scheduled BriefingDetails attribute list
     */
    setScheduledDetails(isScheduled: boolean): AttributeModel[] {
        if (!this.currentBriefing || !isScheduled || !this.preferences) {
            return [];
        }

        const dateSentAttribute: AttributeModel = {
            id: SentBriefingEnum.dateSent,
            label: 'Briefing.dateScheduled',
            value: this.currentBriefing.sendAt.getDisplayString(this.preferences.getDateFormatString(true, true)),
            type: attributeTypesEnum.simple
        };

        const subjectLineAttribute: AttributeModel = {
            id: SentBriefingEnum,
            label: 'Briefings.input.subjectLine',
            value: this.currentBriefing.subject,
            type: attributeTypesEnum.simple
        };

        const fromName: AttributeModel = {
            id: SentBriefingEnum.fromName,
            label: 'Briefings.input.fromName',
            value: this.currentBriefing.fromName,
            type: attributeTypesEnum.simple
        };

        const replyTo: AttributeModel = {
            id: SentBriefingEnum.replyTo,
            label: 'Briefings.input.replyTo',
            value: this.currentBriefing.replyTo,
            type: attributeTypesEnum.simple
        };

        let recipients: AttributeModel = new AttributeModel();
        if (!(this.currentBriefing.recipientList == null) && this.currentBriefing.recipientList?.length !== 0) {
            recipients = {
                id: SentBriefingEnum.recipients,
                label: 'Briefings.input.recipients',
                value: this.currentBriefing.recipientList,
                type: attributeTypesEnum.simpleList
            };
        }

        // only put mailing list name for now, because the recipients inside the lists might have changed, so it would mislead the users.
        // It will require a backend change to make it valid later on.
        let mailingLists: AttributeModel = new AttributeModel();
        if (this.currentBriefing.mailingListIds?.length > 0) {
            const len = this.currentBriefing.mailingListIds?.length;
            const listNames = this.mailingLists?.filter(list => this.currentBriefing.mailingListIds.includes(list.id))
                .map((list) => list.name);

            mailingLists = {
                id: SentBriefingEnum.recipients,
                label: 'Briefings.input.mailingLists',
                value: listNames,
                type: attributeTypesEnum.simpleList
            };
        }

        const feeds: AttributeModel = {
            id: SentBriefingEnum.rssFeeds,
            label: 'Briefings.confirmationDialog.rssFeeds',
            value: null,
            type: attributeTypesEnum.customList,
            customTemplate: null,
            message: { summary: 'Briefing.copyFeedUrlMesage.summary', detail: 'Briefing.copyFeedUrlMessage.detail' }
        };
        if (this.currentBriefing.feeds?.length) {
            feeds.value = this.currentBriefing.feeds;
        }

        const reservedUrlString = this.getReservedUrl();
        const reservedUrl: AttributeModel = {
            id: SentBriefingEnum.reservedUrl,
            label: 'Briefings.confirmationDialog.url',
            value: reservedUrlString,
            url: reservedUrlString,
            type: attributeTypesEnum.url
        };

        return [
            dateSentAttribute,
            subjectLineAttribute,
            fromName,
            replyTo,
            recipients,
            mailingLists,
            reservedUrl,
            feeds
        ].filter(x => !!x.value);
    }

    getBriefingDetails(): AttributeModel[] {
        if (!this.currentBriefing) {
            return [];
        }

        let createdBy: AttributeModel;
        let createdDate: AttributeModel;
        let lastModifiedBy: AttributeModel;
        let lastModifiedDate: AttributeModel;

        if (this.currentBriefing.id) {
            createdBy = {
                id: BriefingDetailsEnum.createdBy,
                label: 'createdBy',
                value: this.currentBriefing.createdByUserId,
                type: attributeTypesEnum.user
            };

            createdDate = {
                id: BriefingDetailsEnum.createdDate,
                label: 'createdDate',
                value: this.currentBriefing.createdDate,
                type: attributeTypesEnum.date,
            };

            lastModifiedBy = {
                id: BriefingDetailsEnum.lastModifiedBy,
                label: 'lastModifiedBy',
                value: this.currentBriefing.modifiedByUserId,
                type: attributeTypesEnum.user,

            };

            lastModifiedDate = {
                id: BriefingDetailsEnum.lastModifiedDate,
                label: 'lastModifiedDate',
                value: this.currentBriefing.modifiedDate,
                type: attributeTypesEnum.date,
            };
        } else {
            createdBy = {
                id: BriefingDetailsEnum.createdBy,
                label: 'createdBy',
                value: 'Monitor.briefings.detailsUnavailable',
                type: attributeTypesEnum.simple
            };

            createdDate = {
                id: BriefingDetailsEnum.createdDate,
                label: 'createdDate',
                value: 'Monitor.briefings.detailsUnavailable',
                type: attributeTypesEnum.simple,
            };

            lastModifiedBy = {
                id: BriefingDetailsEnum.lastModifiedBy,
                label: 'lastModifiedBy',
                value: 'Monitor.briefings.detailsUnavailable',
                type: attributeTypesEnum.simple,

            };

            lastModifiedDate = {
                id: BriefingDetailsEnum.lastModifiedDate,
                label: 'lastModifiedDate',
                value: 'Monitor.briefings.detailsUnavailable',
                type: attributeTypesEnum.simple,
            };
        }


        return [
            createdBy,
            createdDate,
            lastModifiedBy,
            lastModifiedDate
        ].filter(x => !!x.value);
    }

    getSentDetails$(): Observable<AttributeModel[]> {
        let briefing;
        let briefingHistory;

        return combineLatest([
            this.currentBriefingProvider.pipe(filter(x => !!x)),
            this.briefingHistoryProvider.pipe(filter(x => !!x))
        ]).pipe(
            mergeMap(([currBriefing, history]) => {
                briefing = currBriefing;
                briefingHistory = history;

                return this.rssFeedService.getByIds(history.feedIds);
            }),
            map(selectedFeeds => {
                const reservedUrlValue = this.getReservedUrl();
                const dateSentAttribute: AttributeModel = {
                    id: SentBriefingEnum.dateSent,
                    label: 'Briefing.dateSent',
                    value: this.getSentBriefingDisplayString(false, briefing.deliveryMethod !== BriefingDeliveryMethodEnum.ONLINE),
                    type: attributeTypesEnum.simple
                };

                const subjectLineAttribute: AttributeModel = {
                    id: SentBriefingEnum,
                    label: 'Briefings.input.subjectLine',
                    value: briefingHistory.subject,
                    type: attributeTypesEnum.simple
                };

                const fromName: AttributeModel = {
                    id: SentBriefingEnum.fromName,
                    label: 'Briefings.input.fromName',
                    value: briefing.fromName,
                    type: attributeTypesEnum.simple
                };

                const replyTo: AttributeModel = {
                    id: SentBriefingEnum.replyTo,
                    label: 'Briefings.input.replyTo',
                    value: briefingHistory.replyTo,
                    type: attributeTypesEnum.simple
                };

                const recipients: AttributeModel = {
                    id: SentBriefingEnum.recipients,
                    label: 'Briefings.input.recipients',
                    value: briefingHistory.recipientList,
                    type: attributeTypesEnum.simpleList
                };

                const reservedUrl: AttributeModel = {
                    id: SentBriefingEnum.reservedUrl,
                    label: 'Briefings.confirmationDialog.url',
                    value: reservedUrlValue,
                    url: reservedUrlValue,
                    type: attributeTypesEnum.url
                };

                const urlExpiryDate: AttributeModel = {
                    id: SentBriefingEnum.urlExpiryDate,
                    label: 'Briefings.reservedUrl.urlExpiry',
                    value: reservedUrl.value ? this.getSentBriefingDisplayString(true, briefing.deliveryMethod !== BriefingDeliveryMethodEnum.ONLINE) : null,
                    type: attributeTypesEnum.simple
                };

                const feeds: AttributeModel = {
                    id: SentBriefingEnum.rssFeeds,
                    label: 'Briefings.confirmationDialog.rssFeeds',
                    value: null,
                    type: attributeTypesEnum.customList,
                    message: { summary: 'Briefing.copyFeedUrlMesage.summary', detail: 'Briefing.copyFeedUrlMessage.detail' }
                };

                if (selectedFeeds?.length > 0) {
                    feeds.value = selectedFeeds;
                }
                return [
                    dateSentAttribute,
                    subjectLineAttribute,
                    fromName,
                    replyTo,
                    recipients,
                    reservedUrl,
                    urlExpiryDate,
                    feeds
                ];
            }),
            take(1)
        );
    }

    /**
     * Creates the display string for an already sent briefing
     *
     * @param getExpiryDate
     * @param useSendAtTime
     *
     * @return string
     */
    public getSentBriefingDisplayString(getExpiryDate?: boolean, useSendAtTime?: boolean): string {

        let date = moment(this.briefingHistory.date);

        if (getExpiryDate) {
            // B.E set url expiry date 30 days after the published date
            date = moment(this.briefingHistory.date).add(30, 'd');
        }

        const sendAtTimeZone = this.currentBriefing?.sendAt?.timeZone;
        const preferencesTimeZone = this.preferences?.timeZone;
        const dateFormat = this.preferences?.getDateFormatString(true, true, true) ?? TimeZoneFormatEnum.timeZoneFormat;

        if (useSendAtTime && sendAtTimeZone) {
            return date.tz(sendAtTimeZone).format(dateFormat);
        }

        if (preferencesTimeZone) {
            return date.tz(preferencesTimeZone).format(dateFormat);
        }

        return date.format(TimeZoneFormatEnum.timeZoneFormat);
    }

    getReservedUrl(): string {
        if (!this.currentBriefing) {
            return '';
        }

        if (this.currentBriefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            return this.currentBriefing.reservedGuid ? this.getCanvasUrl(this.currentBriefing.reservedGuid) : '';
        } else {
            return this.getEmailReservedUrl();
        }
    }

    getEmailReservedUrl(): string {
        let res;
        const baseURL: string = this.configService.getConfig().apiUrl.replace('api/v1', 'briefings/history');

        if (this.currentBriefing?.reservedGuid) {
            res = `${baseURL}/${this.currentBriefing.reservedGuid}`;
        }

        return res;
    }

    getCanvasUrl(canvasUuid: string): string {
        return `${this.configService.getConfig().bmieUrl}briefings/${canvasUuid}`;
    }

    getSentResources(id: number | string): Observable<BriefingHistorySentModel> {
        return this.briefingApi.getSentResources(id);
    }

    getMentionsOfInterestTitle(): string {
        return this.localizationService.transform('Briefings.mentionsOfInterest');
    }

    getReferenceBlock(referenceId: string): BlockModel {
        for (const block of this.currentBriefing.blocks) {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                for (const column of (block as LayoutBlock).blocks) {
                    for (const x of column) {
                        if ((x.displayElement as CuratedMentionListDisplayElementModel)?.blockReferenceId === referenceId) {
                            return (x);
                        }
                    };
                };
            } else {
                if ((block.displayElement as CuratedMentionListDisplayElementModel)?.blockReferenceId === referenceId) {
                    return (block);
                }
            }
        }
        return null;
    }

    getMentionIdsOfCuratedBlock(referenceId: string): string[] {
        for (const block of this.currentBriefing.blocks) {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                for (const column of (block as LayoutBlock).blocks) {
                    for (const x of column) {
                        if ((x.displayElement as CuratedMentionListDisplayElementModel)?.blockReferenceId === referenceId) {
                            return (x.displayElement as CuratedMentionListDisplayElementModel).mentionIds;
                        }
                    };
                };
            } else {
                if ((block.displayElement as CuratedMentionListDisplayElementModel)?.blockReferenceId === referenceId) {
                    return (block.displayElement as CuratedMentionListDisplayElementModel).mentionIds;
                }
            }
        }
        return [];
    }

    localizeDateBlock(block: BlockModel) {
        const styles = block.displayElement.styles as MonitoringDateStyleModel;
        styles.viewInBrowserLabel = this.localizationService.transform('Briefings.formField.date.showViewInBrowser');
    }

    updateChart(block: BlockModel): void {
        if (!block) {
            return;
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const chartBlock = (block.displayElement as BriefingChartDisplayElementModel)?.chartBlock;
        const oldChartBlock = chartBlock?.clone();
        const oldBlockType = oldChartBlock?.type?.key;
        const oldChartDisplayElement = oldChartBlock?.displayElement as ChartDisplayElementModel;

        const ioModel = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                selectedBlock: chartBlock,
                hideDataConnectorCharts: true,
                hideMentionCharts: true,
                hideMetadataContainer: true,
                hidePreviewChartTooltip: true,
                globalDateQuery: this.cachedSelectedDate,
                displayEntityProfile: block.displayElement.displayType === BlockDisplayTypeEnum.ONLINE,
                displaySourceAsLink: false,
                accentColor: this.currentBriefing.generalStyles['accentColor']
            },
            outputs: {
                dialogClose: (output) => {
                    if (output?.block) {
                        const briefingChartDisplayElement = block.displayElement as BriefingChartDisplayElementModel;
                        briefingChartDisplayElement.chartBlock = output.block;
                        block.canAddChart = false;
                        block.canEditChart = true;
                        block.canAnnotate = output.block.canAnnotate;

                        if (output.block.type.key !== oldBlockType) {
                            const rows = reportChartInfoMap.get(output.block.type.key as ReportBlockTypeEnum)?.dimensions?.height;
                            if (rows) {
                                briefingChartDisplayElement.chart.heightPx = this.chartRowHeight * rows;
                            }

                            const isMapChartWithRegionColors = output.block.type.key === ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
                                && output.block.displayElement?.chartSpecificType === ChartSpecificTypesEnum.MAP
                                && output.block.displayElement?.mapChartColorScheme === MapChartColorSchemeEnum.REGION_COLORS;
                            if (isMapChartWithRegionColors) {
                                briefingChartDisplayElement.chart.heightPx = this.mapChartWithRegionColorsChartHeight;
                            }

                            if (output.block.type.key === ReportBlockTypeEnum.COVERAGE_METRIC) {
                                briefingChartDisplayElement.chart.heightPx = 200;
                            }
                        }

                        const chartDisplayElement = briefingChartDisplayElement.chartBlock.displayElement as ChartDisplayElementModel;

                        if (chartDisplayElement?._series?.length
                            && chartDisplayElement._series[0].dateRange.type === DateQueryTypeEnum.ALL
                        ) {
                            // previousPeriod is not applicable for date range all
                            chartDisplayElement.styles.chartDisplaySettings.previousPeriod = false;
                        }

                        const trackedColorMapKeys = reportBlockTypesToNewAccentColorMapKeysMap.get(output.block.type.key)
                            || reportBlockTypesToSetupDialogAccentColorMapKeysMap.get(output.block.type.key);
                        if (trackedColorMapKeys) {
                            const oldColors = oldChartBlock && oldChartBlock.type.key === output.block.type.key ?
                                trackedColorMapKeys.map(x => oldChartDisplayElement.styles.colorMap[x])
                                : trackedColorMapKeys.map(x => this.currentBriefing.generalStyles['accentColor']);
                            const newColors = trackedColorMapKeys.map(x => (output.block.displayElement as ChartDisplayElementModel).styles.colorMap[x]);
                            chartDisplayElement.trackedColorChanged = chartDisplayElement.trackedColorChanged || oldColors.some((x, i) => x !== newColors[i]);
                        }

                        let refreshBlockData = false;
                        chartDisplayElement.series?.forEach((series, i) => {
                            const mentionFilter = series.mentions?.filters;
                            series.cacheResult = !briefingChartDisplayElement.chart.generateOnSend;

                            if (mentionFilter) {
                                mentionFilter.type = SavedSearchType.BRIEFING;
                                mentionFilter.searchCriteria = series.cibFilterSearchCriteriaModel();
                            }

                            // we check to see if there is any filters where changed to a series
                            // if so, refresh the block data
                            if (oldChartDisplayElement?.series?.[i]?.mentions?.filters) {
                                if (!mentionFilter?.searchCriteria?.query.equals(
                                    oldChartDisplayElement.series?.[i]?.mentions?.filters?.searchCriteria?.query)
                                ) {
                                    refreshBlockData = true;
                                }
                            }

                            if (i < briefingChartDisplayElement.mentionSearches.length && mentionFilter) {
                                const id = briefingChartDisplayElement.mentionSearches[i].id;
                                const name = briefingChartDisplayElement.mentionSearches[i].name;
                                briefingChartDisplayElement.mentionSearches[i] = mentionFilter;
                                briefingChartDisplayElement.mentionSearches[i].id = id;
                                briefingChartDisplayElement.mentionSearches[i].name = name;
                            } else if (mentionFilter) {
                                delete mentionFilter.id;
                                delete mentionFilter.name;
                                briefingChartDisplayElement.mentionSearches.push(mentionFilter);
                            }

                            const neoFacets = series.mentions?.neoFacets;
                            if (neoFacets) {
                                briefingChartDisplayElement.mentionSearches[i].searchCriteria.neoFacets = neoFacets;
                            }

                            if (!oldChartDisplayElement ||
                                series.label !== oldChartDisplayElement.series[i]?.label ||
                                series.styles.geographyScale !== oldChartDisplayElement.series[i]?.styles.geographyScale ||
                                series.styles.networkSelection !== oldChartDisplayElement.series[i]?.styles.networkSelection ||
                                series.prominenceTrendGroup !== oldChartDisplayElement.series[i]?.prominenceTrendGroup) {
                                refreshBlockData = true;
                            }
                        });

                        if (this.needsDataRefresh(chartDisplayElement, oldChartDisplayElement)) {
                            refreshBlockData = true;
                        }

                        this.setBlocks(this.blocks);
                        this.notifyBlockDisplayElementChanged(block);
                        if (refreshBlockData) {
                            this.refreshBlockData(block);
                        } else {
                            const oldSeries = (chartBlock.displayElement as ChartDisplayElementModel).series;
                            chartDisplayElement.series?.forEach((newSeriesItem, i) => {
                                const oldSeriesItem = oldSeries?.[i];
                                if (oldSeriesItem?.dataProvider.value !== null) {
                                    newSeriesItem.dataProvider.next(oldSeriesItem.dataProvider.value);
                                }
                                if (oldSeriesItem?.previousPeriodDataProvider?.value !== null) {
                                    newSeriesItem.previousPeriodDataProvider?.next(oldSeriesItem.previousPeriodDataProvider.value);
                                }
                            });
                            requestAnimationFrame(() => {
                                this.blockDisplayElementChanged$.next({block: block});
                            });
                        }

                        this.dirtyCheckSubject.next(true);
                    }
                }
            }
        };

        this.coreDialogService.open(ChartSetupDialogComponent, ioModel, this.briefingComponentInjector);
    }

    private needsDataRefresh(newChartDisplayElement: ChartDisplayElementModel, oldChartDisplayElement: ChartDisplayElementModel): boolean {
        if (!oldChartDisplayElement) {
            return true;
        }
        // TODO: add GA ones
        const needsRefresh = newChartDisplayElement.dateRangeLabel !== oldChartDisplayElement.dateRangeLabel ||
            newChartDisplayElement.metric !== oldChartDisplayElement.metric ||
            !isEqual(newChartDisplayElement.metrics, oldChartDisplayElement.metrics) ||
            newChartDisplayElement.chartHeaderSettings.previousPeriod !== oldChartDisplayElement.chartHeaderSettings.previousPeriod ||
            newChartDisplayElement.termType !== oldChartDisplayElement.termType ||
            !isEqual(newChartDisplayElement.sentiments, oldChartDisplayElement.sentiments) ||
            newChartDisplayElement.numOfItems !== oldChartDisplayElement.numOfItems;

        return needsRefresh;
    }

    updateChildBlockWidths(block: LayoutBlock): void {
        if (!block) {
            return;
        }
        block.blocks.forEach((column, columnIndex) => {
            column.forEach(x => {
                if (x.type.key === BriefingBlockTypeEnum.CHART) {
                    (x.displayElement as BriefingChartDisplayElementModel).chart.widthPx = this.getColumnWidth(null, block, columnIndex);
                }
            });
        });
    }

    getColumnWidth(blockPosition?: string, block?: LayoutBlock, blockColumnIndex?: number, fullWidth: number = 640): number {
        if (!this.currentBriefing || (blockPosition && !blockPosition.includes('.'))) {
            return fullWidth;
        }
        let layoutBlockStyles, columnIndex;
        if (blockPosition?.includes('.')) {
            const blockPositions = blockPosition.split('.');
            const layoutBlockIndex = blockPositions[0];
            columnIndex = blockPositions[1];
            layoutBlockStyles = this.currentBriefing.blocks[layoutBlockIndex]?.displayElement?.styles;
        }
        if (block) {
            layoutBlockStyles = block.displayElement.styles;
            columnIndex = blockColumnIndex;
        }

        if (layoutBlockStyles) {
            const numColumns = layoutBlockStyles.width?.length ?? 1;
            let paddingWidth = (layoutBlockStyles.columnSpacing || 0) * Math.max(0, numColumns - 1);
            if (layoutBlockStyles.padding?.paddingOption) {
                paddingWidth = paddingWidth + (layoutBlockStyles.padding.leftPadding || 0) + (layoutBlockStyles.padding.rightPadding || 0);
            } else if (layoutBlockStyles.padding?.paddingOption === false) {
                paddingWidth += (layoutBlockStyles.padding.paddingAll * 2 || 0);
            }
            if (layoutBlockStyles.width?.[columnIndex]) {
                return (fullWidth - paddingWidth) * parseFloat(layoutBlockStyles.width[columnIndex]) / 100 || 640;
            } else {
                return (fullWidth - paddingWidth);
            }
        } else {
            return fullWidth;
        }
    }

    refreshBlockData(block: BlockModel): void {
        const b = BriefingsHelper.getChartBlocks(this.currentBriefing).find(x => x.equals(block));
        this.refreshChartBlockData$.next([b]);
    }

    refreshMultipleChartBlockData(blocks: BlockModel[]): void {
        this.refreshChartBlockData$.next(blocks);
    }

    refreshChartBlockData(): Observable<BlockModel[]> {
        return this.refreshChartBlockData$;
    }

    setBlockNoData(series: ReportSeriesModel[]): void {
        this.chartBlockNoData$.next(series);
    }

    getBlockNoData(): Observable<ReportSeriesModel[]> {
        return this.chartBlockNoData$;
    }

    /**
     * Gets gets the flattened index that would match the render service orderId (which increments with layouts)
     */
    getFlattenedIndex(block: BlockModel): number {
        let index = -1;
        if (!this.currentBriefing?.blocks) {
            return;
        }
        // cannot use forEach because the the "return" gets ignored
        for (const b of this.currentBriefing?.blocks) {
            index++;
            // cannot use isEqual because TOC blocks can be "equal" if the settings & styles are the same
            if (block?.displayElement === b?.displayElement) {
                return index;
            }
            if (b.isLayoutBlockType()) {
                for (const c of (b as LayoutBlock).blocks) {
                    for (const x of c) {
                        // cannot use isEqual because TOC blocks can be "equal" if the settings & styles are the same
                        if (block?.displayElement === x?.displayElement) {
                            return index;
                        }
                        index++;
                    };
                };
                index++;
            }
        };
        return index;
    }

    /**
     * Sets the default template on the briefing block types
     */
    private setDefaultTemplatesOnBlockTypes(blockTypes: BlockTypeModel[]): BlockTypeModel[] {
        return blockTypes.map(blockType => {
            blockType.twig = this.getDefaultTemplateByBlockType(
                blockType.key,
                BriefingStyleStateEnum.BRIEFING_1
            );
            return blockType;
        });
    }

    createCuratorBlock(
        type: CuratorBlockType,
        options: BriefingCuratorBlockGenerationOptionInterface,
        briefingStyleState: BriefingStyleStateEnum = BriefingStyleStateEnum.BRIEFING_1
    ): CuratorBlock {
        const block = new CuratorBlock(null, type as CuratorBlockType, options.displayElement || new type.displayElementType() as CuratorDisplayElement);
        const curatorDisplayElement = block.displayElement as CuratorDisplayElement;

        let preferenceLanguageTag = this.languageTag.toString();
        if (preferenceLanguageTag === 'fr-FR') {
            preferenceLanguageTag = 'fr';
        }
        curatorDisplayElement.bulkAnalysisMessage.selectedLanguageOption = promptLanguageOptions.find(option => preferenceLanguageTag === option.value);
        curatorDisplayElement.bulkAnalysisMessage.selectedLanguageTag = preferenceLanguageTag;

        if (this.cachedSelectedDate) {
            if (curatorDisplayElement?.backendDataSource?.combinedSearchCriteria?.query) {
                curatorDisplayElement.backendDataSource.combinedSearchCriteria.query.dateQuery = this.cachedSelectedDate;
            }
            if (curatorDisplayElement?.backendDataSource?.combinedSearchCriteria?.filterQuery) {
                curatorDisplayElement.backendDataSource.combinedSearchCriteria.filterQuery.dateQuery = this.cachedSelectedDate;
            }
            if (curatorDisplayElement?.backendDataSource?.filterSearchCriteria?.query) {
                curatorDisplayElement.backendDataSource.filterSearchCriteria.query.dateQuery = this.cachedSelectedDate;
            }
        }

        let subBlock;
        let subBlockDisplayElement;
        const displayType =
            (options.deliveryMethod || this.currentBriefing.deliveryMethod) === BriefingDeliveryMethodEnum.ONLINE ?
                BlockDisplayTypeEnum.ONLINE : BlockDisplayTypeEnum.TWIG;

        // title block
        subBlockDisplayElement = new BriefingTitleDisplayElementModel(
            null,
            this.getDefaultStyle(BriefingBlockTypeEnum.TITLE, briefingStyleState)
        );

        subBlockDisplayElement.displayType = displayType;
        (subBlockDisplayElement as BriefingTitleDisplayElementModel).headingLevel = BriefingHeadingLevelEnum.HEADING_3;
        subBlockDisplayElement.styles.padding = new PaddingModel(0, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.headingTwo.padding = new PaddingModel(null, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.headingThree.padding = new PaddingModel(null, true, 20, 0, 10, 0);
        subBlockDisplayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        subBlockDisplayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        subBlockDisplayElement.canAutomaticallyGenerated = true;
        subBlockDisplayElement.automaticallyGenerated = true;

        subBlock = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.TITLE),
            subBlockDisplayElement
        );
        block.blocks.push(subBlock);

        // analysis block
        subBlockDisplayElement = new AnalysisDisplayElementModel();
        subBlockDisplayElement.generateOnSend = true;
        subBlockDisplayElement.analysisField = new CoverageInsights();
        subBlockDisplayElement.analysisField.aiAnalysisType = AiAnalysisTypes.EXECUTIVE_SUMMARY;
        subBlockDisplayElement.analysisField.keyTakeawayHeading = 'Key Takeaways';
        subBlockDisplayElement.analysisField.selectedLengthOption = promptLengthOptions.find(
            option => option.value === options.analysisLength || CompletionLengthEnum.SHORT
        );
        subBlockDisplayElement.analysisField.selectedLanguageOption = promptLanguageOptions.find(option => preferenceLanguageTag === option.value);

        subBlockDisplayElement.backendDataSource = curatorDisplayElement.backendDataSource;
        subBlockDisplayElement.searchCriteriaId = curatorDisplayElement.searchCriteriaId;
        subBlockDisplayElement.sourceType = curatorDisplayElement.sourceType;
        subBlockDisplayElement.displayType = displayType;
        subBlockDisplayElement.canAutomaticallyGenerated = true;
        subBlockDisplayElement.automaticallyGenerated = true;
        subBlockDisplayElement.filterMentionSearchModel = curatorDisplayElement.filterMentionSearchModel;

        subBlock = new BlockModel(
            null,
            briefingBlockTypeEnumToBlockTypeMap.get(BriefingBlockTypeEnum.ANALYSIS),
            subBlockDisplayElement
        );

        block.blocks.push(subBlock);

        // Coverage Block
        subBlock = this.createDynamicBlock(curatorDisplayElement.backendDataSource, this.CURATOR_BLOCK_MENTIONS, '');
        subBlock.displayElement.displayType = displayType;
        subBlock.displayElement.styles.accentColor = options.accentColor;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).title = 'Curator Mentions';
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.showSectionHeading = false;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.image = true;
        (subBlock.displayElement as DynamicMentionListDisplayElementModel).styles.imageSize = ImageSizeEnum.fullWidth;
        subBlock.displayElement.canAutomaticallyGenerated = true;
        subBlock.displayElement.automaticallyGenerated = true;
        subBlock.displayElement.styles.styleState = BriefingStyleStateEnum.CUSTOM;
        subBlock.displayElement.styles.displayStyleState = BriefingStyleStateEnum.CUSTOM;
        subBlock.displayElement.filterMentionSearchModel = curatorDisplayElement.filterMentionSearchModel;

        block.blocks.push(subBlock);

        return block;
    }

    private moveInCuratorBlock(block: BlockModel, curatorBlock: CuratorBlock): void {
        block.displayElement.canAutomaticallyGenerated = true;
        block.displayElement.automaticallyGenerated = true;
        if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            const displayElement = block.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
            displayElement.backendDataSource = curatorBlock.displayElement.backendDataSource;
            displayElement.searchCriteriaId = curatorBlock.displayElement.searchCriteriaId;
            displayElement.sourceType = curatorBlock.displayElement.sourceType;
            displayElement.filterMentionSearchModel = curatorBlock.displayElement.filterMentionSearchModel;

            if (block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
                (displayElement as AnalysisDisplayElementModel).generateOnSend = true;
            }

            // to refresh the RHP change
            this.selectBlock(block);
        }
    }

    moveOutCuratorBlock(block: BlockModel): void {
        block.displayElement.canAutomaticallyGenerated = false;
        block.displayElement.automaticallyGenerated = false;
        if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            const displayElement = block.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
            displayElement.backendDataSource = displayElement.backendDataSource.clone();
            displayElement.searchCriteriaId = null;

            if (displayElement.filterMentionSearchModel) {
                displayElement.filterMentionSearchModel = displayElement.filterMentionSearchModel.clone();
                displayElement.filterMentionSearchModel.id = null;
            }

            if (block.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST && !(displayElement as DynamicMentionListDisplayElementModel).title) {
                (displayElement as DynamicMentionListDisplayElementModel).title = 'Mentions';
            }

            // to refresh the RHP change
            this.selectBlock(block);
        }
    }

    lockCuratorChildSource(): void {
        const lock = (briefing: BriefingModel) => {
            if (!briefing) {
                return;
            }
            briefing.blocks.forEach(block => {
                if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                    const curatorBlock = block as CuratorBlock;
                    curatorBlock.blocks.forEach(child => {
                        if ((child.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST || child.type.key === BriefingBlockTypeEnum.ANALYSIS)
                            && child.displayElement.automaticallyGenerated
                        ) {
                            const sub = child.displayElement as DynamicMentionListDisplayElementModel | AnalysisDisplayElementModel;
                            sub.backendDataSource = curatorBlock.displayElement.backendDataSource;
                            sub.searchCriteriaId = curatorBlock.displayElement.searchCriteriaId;
                            sub.sourceType = curatorBlock.displayElement.sourceType;
                            sub.filterMentionSearchModel = curatorBlock.displayElement.filterMentionSearchModel;
                        }
                    });
                }
            });
        };

        lock(this.currentBriefing);
        lock(this.cachedCurrentBriefing);
    }

    triggerDirtyCheck(): void {
        this.dirtyCheckSubject.next(true);
    }

    createBriefingFromNaturalLanguagePrompt(prompt: string): Observable<CanvasAgentResponseApiModel> {
        return this.aIApi.canvasAgent(prompt);
    }

    canvasAgentV2(prompt: string): Observable<CanvasV2AcceptedModel> {
        return this.aIApi.canvasAgentV2(prompt);
    }

    getCanvasV2Events(runId: string): Observable<CanvasV2ProgressEvent> {
        return this.websocketEvents.getWebsocketMessages().pipe(
            filter((msg): msg is CanvasV2ProgressEvent => !!msg && msg.runId === runId)
        );
    }

    setCoverageImage(): void {
        const imageUrls = [];
        this.currentBriefingMentions.forEach(mention => {
            mention.images.forEach(image =>{
                const normalized = ImageHelper.normalizeImageUrl(image.source);
                if (!imageUrls.includes(normalized)) {
                    imageUrls.push(normalized);
                }
            });
        });

        if (this.currentBriefing.sendAt.recurring) {
            return;
        }

        let imageBlock;
        this.currentBriefing.blocks.some(block => {
            if (block.type.key === BriefingBlockTypeEnum.IMAGE) {
                imageBlock = block;
                return true;
            }

            if (
                block.type.key === LayoutBlockTypesEnum.briefingOne ||
                block.type.key === LayoutBlockTypesEnum.briefingTwo ||
                block.type.key === LayoutBlockTypesEnum.briefingThree
            ) {
                return (block as LayoutBlock).blocks.some(column =>
                    column.some(subBlock => {
                        if (subBlock.type.key === BriefingBlockTypeEnum.IMAGE) {
                            imageBlock = subBlock;
                            return true;
                        }
                        return false;
                    })
                );
            }

            return false;
        });

        if (imageBlock) {
            const imageDisplayElement = imageBlock.displayElement as BriefingImageDisplayElementModel;
            const numOfImage = imageDisplayElement.imageEffects.imageFrame === BriefingImageFrameEnum.COLLAGE ? 4 : 1;

            (async () => {
                let slot = 0;
                const batchSize = 10;

                for (let start = 0; start < imageUrls.length && slot < numOfImage; start += batchSize) {
                    const batch = imageUrls.slice(start, start + batchSize);

                    const results = await Promise.allSettled(
                        batch.map(url => ImageHelper.validateAutomateImage(url))
                    );

                    for (let i = 0; i < results.length && slot < numOfImage; i++) {
                        const r = results[i];

                        // only accept fulfilled + true
                        if (r.status === 'fulfilled' && r.value === true) {
                            const imgProperty = new BriefingImagePropertiesModel();
                            imgProperty.location = slot;
                            imgProperty.imageSource = ImageSourceEnum.NORMAL;
                            imgProperty.url = batch[i];
                            imageDisplayElement.images.push(imgProperty);
                            // force re-render
                            imageDisplayElement.images = [...imageDisplayElement.images];
                            slot++;
                        }
                    }
                }
                this.blockDisplayElementChanged$.next({
                    block: imageBlock,
                });
            })().catch(console.error);
        }
    }

    cacheMentionWithAutoGenInsight(
        mentionWithAutoGenInsight: BriefingCuratedMentionDetailsApi,
        briefingId: number
    ): void {
        if (!this.currentBriefing?.id || this.currentBriefing.id !== briefingId || !this.MERCURY_ID_PATTERN.test(mentionWithAutoGenInsight.mentionId)) {
            // we only cache unsaved mentions
            return;
        };

        this.currentBriefing.cacheMentionWithAutoGenInsight(mentionWithAutoGenInsight);
    }

    getAllCuratedMentions(): MentionModel[] {
        const mentions = [];
        this.getAllMentionBlocks().forEach(block => {
            if (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                const mentionList = block.displayElement as CuratedMentionListDisplayElementModel;
                mentions.push(...mentionList.mentions);
            }
        });
        return mentions;
    }

    fetchDigestsForBriefing(briefingsApi: BriefingApiModel): Observable<BriefingApiModel> {
        if (briefingsApi.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            return this.digestService.getDigestsByBriefingId(briefingsApi.briefingId).pipe(
                catchError(err => {
                    // If we fail to get digests, we don't want it to block the briefing from loading, just set it to an empty array
                    return of([]);
                }),
                map(digests => {
                    briefingsApi.digests = digests.map(x => x.id);
                    return briefingsApi;
                })
            );
        }

        return of(briefingsApi);
    }

    createMonthlyReportBriefing(briefing: BriefingModel, options: OnlineBriefingTemplateConfigModel) {
        this.createMediaCoverageBriefing(briefing, options);
    }

    // TODO: implement quarterly report briefing creation
    createQuarterlyReportBriefing(briefing: BriefingModel, options: OnlineBriefingTemplateConfigModel) {
        this.createMediaCoverageBriefing(briefing, options);
    }

    private setHasDynamicElements(hasDynamicElements: boolean): void {
        this.hasDynamicElements$.next(hasDynamicElements);
    }

    private checkIfBriefingHasDynamicElements(): void {
        let hasDynamicElements = false;
        for (const block of this.currentBriefing?.blocks) {
            if (block.isLayoutBlockType()) {
                for (const column of (block as LayoutBlock).blocks) {
                    for (const row of column) {
                        if (this.isDynamicElementValid(row)) {
                            this.setHasDynamicElements(true);
                            hasDynamicElements = true;
                            return;
                        }
                    };
                };
            } else {
            // we want curator blocks to be handled as normal block here
                if (this.isDynamicElementValid(block)) {
                    this.setHasDynamicElements(true);
                    hasDynamicElements = true;
                    return;
                }
            }
        };
        if (!hasDynamicElements) {
            this.setHasDynamicElements(false);
        }
    }

    isDynamicElementValid(block: BlockModel): boolean {
        if (block.type.key === BriefingBlockTypeEnum.ANALYSIS) {
            return this.isAnalysisBlockValid(block);
        }
        if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {

            // check if source attached it valid
            let validForSoureType: boolean = false;
            if ((block.displayElement as CuratorDisplayElement)?.backendDataSource?.type === MonitoringSeriesTypeEnum.allSavedCoverage) {
                validForSoureType = true;
            } else {
                validForSoureType = !!(block.displayElement as CuratorDisplayElement)?.backendDataSource?.id;
            }

            // eslint-disable-next-line max-len
            const hasNoCustomInstructions: boolean = ((block.displayElement as CuratorDisplayElement)?.bulkAnalysisMessage?.aiAnalysisType === AiAnalysisTypes.COLLECTION_CUSTOM &&
                isEmpty((block.displayElement as CuratorDisplayElement)?.bulkAnalysisMessage?.adHocCustomPrompt));

            // generate now && has valid source && has No Custom Instructions
            return !(block.displayElement as CuratorDisplayElement)?.generateOnSend &&
                (
                    validForSoureType &&
                    !hasNoCustomInstructions
                );
        }

        if (block.type.key === BriefingBlockTypeEnum.CHART){
            // chart has a source & generate now
            return block.canEditChart && !(block.displayElement as BriefingChartDisplayElementModel)?.chart?.generateOnSend;
        }
        return false;
    }

    private isAnalysisBlockValid(block: BlockModel): boolean {
        const type: MonitoringSeriesTypeEnum = (block.displayElement as AnalysisDisplayElementModel)?.backendDataSource?.type;

        // check if source attached it valid
        let validForSoureType: boolean = false;
        if (type === MonitoringSeriesTypeEnum.coverageInSection) {
            validForSoureType = !!(block.displayElement as AnalysisDisplayElementModel).backendDataSource.referenceId ||
                !!(block.displayElement as AnalysisDisplayElementModel).sourceBlockReferenceId;
        } else if ((type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) || (type === MonitoringSeriesTypeEnum.allSavedCoverage)) {
            validForSoureType = true;
        } else {
            validForSoureType = !!(block.displayElement as CuratorDisplayElement)?.backendDataSource?.id;
        }

        // has valid source type, generate on send & Insights Coverage is Valid
        return validForSoureType &&
            !(block.displayElement as AnalysisDisplayElementModel)?.generateOnSend &&
            this.isInsightsCoverageValid((block.displayElement as AnalysisDisplayElementModel).analysisField);
    }

    isInsightsCoverageValid(coverageInsights: CoverageInsights): boolean {
        let promptIsValid = false;
        if (!coverageInsights.aiAnalysisType) {
            promptIsValid = false;
            return promptIsValid;
        }

        switch (coverageInsights.aiAnalysisType) {
            case AiAnalysisTypes.EXECUTIVE_SUMMARY:
                if (coverageInsights) {
                    if (!coverageInsights.selectedLanguageOption) {
                        promptIsValid = false;
                        return promptIsValid;
                    }
                    if (!coverageInsights.selectedLengthOption) {
                        promptIsValid = false;
                        return promptIsValid;
                    }
                }

                promptIsValid = true;
                break;
            case AiAnalysisTypes.SENTIMENT_OPINION:
            case AiAnalysisTypes.KEY_TAKEAWAYS:
                if (coverageInsights) {
                    if (!coverageInsights.selectedLanguageOption) {
                        promptIsValid = false;
                        return promptIsValid;
                    }
                }

                promptIsValid = true;
                break;
            case AiAnalysisTypes.BRAND_IMPACT:
                if (coverageInsights) {
                    if (!coverageInsights.selectedLanguageOption) {
                        promptIsValid = false;
                        return promptIsValid;
                    }
                    if (!coverageInsights.brand) {
                        promptIsValid = false;
                        return promptIsValid;
                    }
                }

                promptIsValid = true;
                break;
            case AiAnalysisTypes.CUSTOM:
                if (!coverageInsights.customPromptId) {
                    promptIsValid = false;
                    return promptIsValid;
                }
                promptIsValid = true;
                break;
            default:
                promptIsValid = true;
                break;
        }
        return promptIsValid;
    }

    setDefaultGeneralStylesWithUserPreferences(generalStyles: any) {
        if (this.preferences && generalStyles.date) {
            const dateFormatModel = dateFormats.find(f => f.id === this.preferences.dateFormatId);
            if (dateFormatModel) {
                generalStyles.date.dateFormat = dateFormatModel.twigFormat;
            }
            const timeFormatModel = timeFormats.find(f => f.id === this.preferences.timeFormat);
            if (timeFormatModel) {
                generalStyles.date.timeFormat = timeFormatModel.momentFormat;
            }

            generalStyles.date.timezone = this.userTimezonePreference;
            generalStyles.date.showTimeZone = true;
            generalStyles.date.showTime = true;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
